import {
  Component,
  DoCheck,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ChangeDetectionStrategy
} from '@angular/core';
import {ChatPageContextService} from '../../services/chat/chat-page-context.service';
import {
  faArrowDownToBracket,
  faArrowsRotate,
  faArrowsUpDownLeftRight,
  faArrowUpFromBracket,
  faArrowUpRightFromSquare,
  faBars,
  faChevronDown,
  faChevronLeft,
  faChevronRight,
  faCircleCheck,
  faCircleExclamation,
  faCircleInfo,
  faCircleQuestion,
  faCircleXmark,
  faCopy,
  faEllipsisVertical,
  faFile,
  faFileDoc,
  faFilePdf,
  faFilePpt,
  faFileXls,
  faFolderOpen,
  faFolderPlus,
  faGear,
  faHouse,
  faInfoCircle,
  faLayerGroup,
  faLayerGroupPlus,
  faMagnifyingGlass,
  faPenToSquare,
  faSort,
  faSortDown,
  faSortUp,
  faSquareDashedCirclePlus,
  faTags,
  faTrash,
  faTriangleExclamation,
  faXmark,
  faLinkSlash,
  faBarsProgress,
} from "@fortawesome/pro-solid-svg-icons";
import {ProjectFile, ProjectFileControllerService, SourceLinkControllerService, SourceLinkView} from "../../api";
import {LinkSourceFolderDialogComponent} from "./components/source-link/link-source-folder-dialog.component";
import {UnlinkSourceFolderDialogComponent} from "./components/source-link/unlink-source-folder-dialog.component";
import {SourceLinkSyncProgressDialogComponent} from "./components/source-link/source-link-sync-progress-dialog.component";
import {SourceLinkSyncService} from "../../services/source-link/source-link-sync.service";
import {FolderSyncIconComponent} from "./components/source-link/folder-sync-icon.component";

import {ProjectFileTableService} from "../../services/files/project-file-table.service";
import {FilePrefetchService} from "../../services/prefetch/file-prefetch.service";
import {ClassificationService} from '../../services/classification/classification.service';
import {DeleteService} from "../../services/delete/delete.service";
import {StateConfigService} from "../../services/state/state-config.service";
import {MatDialog, MatDialogRef} from "@angular/material/dialog";
import {NavigationEnd, Router} from "@angular/router";
import {
  CopyMoveComponent,
  DeleteFileComponent,
  DeleteProgressComponent,
  EditCoordinationModelComponent,
  FilesRejectedComponent,
  FilesRejectedData,
  NewCoordinationModelComponent,
  NewFolderComponent,
  RenameComponent,
  StorageLimitExceededComponent,
  StorageLimitExceededData,
  topLevelSelection,
  TrashComponent,
  UploadProgressComponent,
  UploadProgressData,
  zipFolderHint,
} from "./components/pop-ups/pop-ups";
import {isAllowedUploadExtension} from "./files.constants";
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  EMPTY,
  filter,
  firstValueFrom,
  from,
  mergeMap,
  range,
  Subject,
  Subscription,
  tap
} from "rxjs";
import {HttpErrorResponse} from "@angular/common/http";
import {PopoutContentService} from "../../services/popout/popout.service";
import {coordinationModelErrorMessage, coordinationModelPath} from "../../services/bim-viewer/coordination-model-error";
import {faIfc} from "../util/icons/custom-icons";
import {
  FileCircleState,
  FilesStatusCircleComponent
} from "./components/files-status-circle/files-status-circle.component";

import {FaIconComponent} from "@fortawesome/angular-fontawesome";
import {faSpinnerThird} from "@fortawesome/pro-duotone-svg-icons";
import { DatePipe, DecimalPipe, NgClass } from "@angular/common";
import {FILE_STATUS_LABELS, FileStatusPipe, FileTypePipe} from "../../pipes/file-type.pipe";
import {countRunningClassifications} from "../../services/files/classification-count.util";
import {
  CdkCell,
  CdkCellDef,
  CdkColumnDef,
  CdkHeaderCell,
  CdkHeaderCellDef,
  CdkHeaderRow,
  CdkHeaderRowDef,
  CdkRow,
  CdkRowDef,
  CdkTable
} from "@angular/cdk/table";
import {
  BicButtonComponent,
  BicDropdownComponent,
  BicDropdownElementComponent,
  BicDropdownTriggerDirective,
  BicInputComponent,
  BicToastService,
  TooltipDirective
} from "@buildinginformationcloud/bic-ui-components";
import {UploadJob, UploadService} from "../../services/upload/upload.service";
import {BicFilterComponent, BicFilterConfig} from "../util/design/bic-filter/bic-filter.component";
import {PageHeaderComponent} from "../util/design/page-header/page-header.component";
import {EmptyStateComponent} from "../util/design/empty-state/empty-state.component";
import {PermissionService} from "../../services/permission/permission.service";
import {NotificationsService} from "../../services/notifications/notifications.service";
import {DocumentClassService} from "./components/document-classes/document-class.service";
import {
  ClassificationProgressBannerComponent
} from "../util/classification-progress-banner/classification-progress-banner.component";

export enum classifyStateEnum {
  BEFORE = 'beforer',
  IN = 'in',
  AFTER = 'after'
}

/**
 * Welcher Status-Kreis in einer Zeile gerendert wird - genau einer.
 *
 * Vorher entschieden das fuenf unabhaengige, jeweils mehrzeilige *ngIf-Ausdruecke im Template, die
 * sich gegenseitig nicht ausschlossen: eine IFC-Datei in PROCESSING zeigte gleichzeitig den
 * "Analyse laeuft"- und den "Bereit"-Kreis.
 */
export type FileStatusCircleKind =
  | 'uploading'
  | 'stalled'
  | 'ifcUnclassified'
  | 'analysing'
  | 'error'
  | 'ready';

/**
 * Einzige Definition von "Suche oder Filter aktiv" (#2770): Tabelle (`applyColumnFilters`), Zaehler
 * (`selectableFileCount`), Auswahl (`addElementToSelectedFiles`, `visibleSelectedFiles`) und der Getter
 * `hasActiveSearchOrFilter` (delegiert hierher) muessen dieselbe Sicht haben. Bewusst null-sicher und
 * keine Methode: die Prototyp-Tests rufen die Komponenten-Methoden mit handgebauten Kontexten auf,
 * in denen `columnFilters` fehlen kann.
 */
function isSearchOrFilterActive(searchTerm: string | undefined, columnFilters: { [key: string]: string[] } | undefined): boolean {
  return (searchTerm || '').trim().length > 0
    || Object.values(columnFilters ?? {}).some(v => !!v && v.length > 0);
}

interface FolderStatusCounts {
  total: number;
  checked: number;
  ready: number;
  processing: number;
  uploading: number;
  error: number;
  /**
   * Gescheiterte Dateien, fuer die noch ein Versuch vorgesehen ist.
   *
   * Nicht dasselbe wie `error`: eine Datei mit erschoepften Versuchen bleibt im Fehlerzustand, ein
   * weiterer Lauf bringt sie aber nicht weiter. Der Klick auf den Ordner-Kreis haengt an dieser
   * Zahl - und weil sie im selben (zwischengespeicherten) Durchlauf entsteht, kostet sie nichts.
   */
  retryable: number;
}

@Component({
  selector: 'app-files',
  templateUrl: './files.component.html',
  styleUrls: ['./files.component.scss'],

  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    FaIconComponent,
    NgClass,
    FileTypePipe,
    FileStatusPipe,
    CdkTable,
    CdkHeaderCell,
    CdkCell,
    CdkColumnDef,
    CdkCellDef,
    FilesStatusCircleComponent,
    DatePipe,
    DecimalPipe,
    CdkHeaderCellDef,
    CdkRow,
    CdkHeaderRow,
    CdkHeaderRowDef,
    CdkRowDef,
    BicButtonComponent,
    BicDropdownComponent,
    BicDropdownTriggerDirective,
    TooltipDirective,
    BicDropdownElementComponent,
    BicInputComponent,
    BicFilterComponent,
    PageHeaderComponent,
    EmptyStateComponent,
    ClassificationProgressBannerComponent,
    FolderSyncIconComponent
]
})
export class FilesComponent implements OnInit, OnDestroy, OnChanges, DoCheck {
  stateConfigService = inject(StateConfigService);
  dialog = inject(MatDialog);
  private fileController = inject(ProjectFileControllerService);
  private fileTableService = inject(ProjectFileTableService);
  private filePrefetchService = inject(FilePrefetchService);
  private deleteService = inject(DeleteService);
  private classificationService = inject(ClassificationService);
  private popoutContentService = inject(PopoutContentService);
  private uploadService = inject(UploadService);
  private notificationsService = inject(NotificationsService);
  private sourceLinkService = inject(SourceLinkControllerService);
  private sourceLinkSync = inject(SourceLinkSyncService);

  // --- Verknuepfte Ordner externer Datenquellen (BIC-Frontend#2720) ---
  /** Verknuepfte Ordner des Projekts, je Zielordner (planBIC-Ordner-ID). */
  linkByTargetFolder = new Map<string, SourceLinkView>();
  /** Verknuepfung, in deren Zielordner (oder darunter) man gerade steht. */
  currentLink: SourceLinkView | undefined;
  readonly mirroredHint = 'Wird aus SharePoint synchronisiert und ist in planBIC schreibgeschützt. Bitte in SharePoint ändern.';
  private sourceLinkSseSub?: Subscription;
  protected readonly faLinkSlash = faLinkSlash;
  protected readonly faBarsProgress = faBarsProgress;
  public permissionService = inject(PermissionService);
  protected readonly faGear = faGear;
  protected readonly faTriangleExclamation = faTriangleExclamation;
  private router = inject(Router);
  private documentClassService = inject(DocumentClassService);
  private bicToastService: BicToastService = inject(BicToastService);
  private chatPageContext = inject(ChatPageContextService);

  /** Navigiert zur Dokumentenklassenverwaltung. */
  navigateToDocumentClasses(): void {
    this.router.navigate(['projects', this.stateConfigService.getProjectId(), 'files', 'document-classes']);
  }


  filteredData: ProjectFile[] = [];
  originalData: ProjectFile[] = [];
  loading = true;

  @Input()
  selectedFiles: ProjectFile[] = [];

  @Input()
  customFiles: ProjectFile[] = [];
  @Input() customFilesActivated = false;

  @Input()
  headline = "Dateien";

  @Input() filterAndContextButtonActivated = true;

  @Output()
  selectedFilesChange: EventEmitter<ProjectFile[]> = new EventEmitter<ProjectFile[]>();

  columns: any[] = ["selectionFields", "icon", "name", "fileType", "size", "fileStatus", "lastModified", "createdBy", "contextMenu"];
  columnNames: any[] = ["selectionFields", "icon", "Dateiname", "Typ", "Grösse", "Status", "Zuletzt geändert", "Ersteller*in", "contextMenu"];
  columnTypes: any[] = ["selectionFields", "icon", "text", "text", "filesize", "status", "date", "text", "contextMenu"];
  columnWidths: any[] = ["48px", "48px", "auto", "auto", "auto", "110px", "150px", "0px", "48px"]; // 0px width for hidden column
  // Untere Grenze, auf die die Spalten bei wenig Platz schrumpfen dürfen, statt sofort horizontal zu scrollen.
  columnMinWidths: any[] = ["48px", "48px", "60px", "120px", "120px", "80px", "110px", "0px", "48px"];

  /** Typ/Größe: unterhalb xl ausgeblendet, Breite wächst per Breakpoint-Klasse (siehe Template) bis 200px auf 2xl */
  isBreakpointHiddenColumn(col: string): boolean {
    return col === 'fileType' || col === 'size';
  }

  /** Praefix der temporaeren IDs, die Ordner-Dummies bis zur Antwort von createFolderStructure tragen. */
  private static readonly FOLDER_DUMMY_ID_PREFIX = '__folder_dummy_';
  protected readonly statusLabelUploading = FILE_STATUS_LABELS[ProjectFile.FileStatusEnum.UPLOADING];

  // columnWidths: any[] = ["50", "50", "415", "170", "170", "110", "170", "50"];
  columnAlignments: any[] = ["center", "center", "start", "start", "start", "center", "start", "end"];
  displayedColumns = ["selectionFields", "icon", "name", "type", "size", "fileStatus", "lastModified", "createdBy", "contextMenu"];
  dark: boolean = false;
  contextMenu: boolean = true;
  selectionFields: boolean = true;
  icon: boolean = true;
  /** Laufender ZIP-Download (Toolbar-Button) bzw. laufender Einzeldownload (Kontextmenü). */
  protected downloadInProgress = false;
  protected downloadingFileId: string | null = null;
  sortedColumn: string = '';
  sortedDirection: string = '';
  statusOrder = [ProjectFile.FileStatusEnum.ERROR, ProjectFile.FileStatusEnum.UPLOADING, ProjectFile.FileStatusEnum.UPLOADED, ProjectFile.FileStatusEnum.PROCESSING, ProjectFile.FileStatusEnum.READY];
  hoveredRow!: any;
  searchTerm: string = '';
  /** Entprellte Sucheingaben - applyColumnFilters() geht über den gesamten Projektbaum. */
  private readonly searchInput$ = new Subject<string>();
  private searchSub?: Subscription;
  detailedInfos: boolean = false;
  selectedElement!: ProjectFile;
  currentParent: ProjectFile | undefined = undefined;
  currentDepth: number = 0;
  parentDepthMap: Map<number, ProjectFile> = new Map<number, ProjectFile>();
  @Input() calledFromCheck = false;
  @Input() backButtonActivated = false;
  @Output() backButtonClicked: EventEmitter<void> = new EventEmitter<void>();
  @Input() noOuterPadding: boolean = false;
  columnFilters: { [key: string]: string[] } = {}; // Changed from string to string[]
  uploadingDocuments: Map<string, number> = new Map<string, number>();
  private readonly uploadClientIdByDummyKey: Map<string, string> = new Map<string, string>();
  private uploadProgressDialogRef?: MatDialogRef<UploadProgressComponent>;
  /** Batches, deren Upload gar nicht erst startet \- der Fortschritts-Dialog darf dafür nicht (mehr) aufgehen. */
  private readonly abortedUploadBatchIds = new Set<string>();
  /** Aufräumfunktion je Batch, solange das Backend noch die Ordnerstruktur anlegt (siehe openUploadSelection). */
  private readonly pendingFolderStructureByBatchId = new Map<string, () => void>();
  private uploadJobsSub?: Subscription;
  private uploadRunningSub?: Subscription;
  private uploadedReportedSub?: Subscription;
  private projectSwitchSub?: Subscription;
  private chatDataChangedSub?: Subscription;
  /**
   * Projekt, dessen Dateien gerade in der Liste stehen.
   *
   * Angular verwendet diese Komponente beim Wechsel von `/projects/140/files` nach
   * `/projects/156/files` WIEDER \- gleiche Route, nur ein anderer Parameter. `ngOnInit` laeuft
   * dabei nicht noch einmal, und ohne diesen Merker faellt gar nicht auf, dass die angezeigten
   * Dateien zu einem anderen Projekt gehoeren.
   */
  private loadedProjectId = 0;

  /** Bestaetigte UPLOADED-Meldungen, deren Zeile noch nicht (mit ID) in der Liste steht. */
  private readonly pendingUploadedFileIds = new Set<string>();
  /** Läuft solange eine Löschaktion offen ist - auch wenn das Fortschritts-Popup schon zu ist. */
  private deleteJobsSub?: Subscription;
  private readonly ifcReadyPollTimeoutByFileId: Map<string, ReturnType<typeof setTimeout>> = new Map<string, ReturnType<typeof setTimeout>>();
  activeFilters: { [key: string]: any } = {};
  file_classify_state_map: Map<string, classifyStateEnum> = new Map<string, classifyStateEnum>();

  currentFolderData: ProjectFile[] = [];

  highlightedRow: ProjectFile | null = null;
  protected readonly statusLabelProcessing = FILE_STATUS_LABELS[ProjectFile.FileStatusEnum.PROCESSING];

  private clickTimer: any = null;
  private clickDelay = 200;

  private runningClassificationsSub?: any;
  private classificationCompletedSub?: any;

  /**
   * Fallback-Poll: Falls ein CLASSIFICATION_COMPLETED-SSE nicht ankommt (z.B. Multi-Replica),
   * werden die noch als PROCESSING markierten Dateien periodisch nachgeladen, bis keine mehr laufen.
   */
  private classificationPollInterval: ReturnType<typeof setInterval> | null = null;
  private readonly CLASSIFICATION_POLL_INTERVAL_MS = 15000;
  /**
   * Wie viele getFile()-Abfragen der Fallback-Poll gleichzeitig offen haelt. Muss begrenzt sein: die
   * Zahl der PROCESSING-Dateien ist nach einem Batch-Upload beliebig gross, der Poll wiederholt sich
   * alle CLASSIFICATION_POLL_INTERVAL_MS, und jede Antwort rechnet Ordnerstatus und Filter neu.
   */
  private readonly CLASSIFICATION_POLL_CONCURRENCY = 4;
  protected readonly statusLabelReady = FILE_STATUS_LABELS[ProjectFile.FileStatusEnum.READY];
  protected readonly statusLabelError = FILE_STATUS_LABELS[ProjectFile.FileStatusEnum.ERROR];
  /** Koordinationsmodell ERSTELLEN. "Oeffnen" behaelt faLayerGroup - vorher trugen beide
   *  Toolbar-Buttons dasselbe Icon und standen bei >=2 ausgewaehlten IFC gleichzeitig da. */
  protected readonly faLayerGroupPlus = faLayerGroupPlus;
  /** Dokumentenklasse zuweisen - vorher dasselbe faArrowsRotate wie "Klassifizierung erneut versuchen". */
  protected readonly faTags = faTags;
  private readonly columnLayoutCache = new Map<string, {
    width: string | null;
    minWidth: string | null;
    flex: string
  }>();
  /**
   * Klassifizierungen, die diese Ansicht selbst angestossen hat und die das Backend noch nicht in
   * `runningClassifications` fuehrt. Ohne sie waere der Banner-Zaehler zwischen Klick und naechstem
   * State-Abruf bei 0.
   */
  private readonly locallyStartedClassificationIds = new Set<string>();
  /**
   * Dateien, deren Klassifizierung nicht mehr voranschreitet: der Server fuehrt sie weiter als
   * PROCESSING, meldet sie aber ueber mehrere Poll-Runden hinweg nicht (mehr) als laufende
   * Klassifizierung. Sie bekommen statt des endlos drehenden Rings den roten Retry-Kreis.
   */
  private readonly stalledClassificationIds = new Set<string>();
  /** Poll-Runden, in denen eine PROCESSING-Datei nicht in runningClassifications auftauchte. */
  private readonly classificationMissesById = new Map<string, number>();
  /**
   * Nach so vielen aufeinanderfolgenden Poll-Runden (a 15s) ohne Eintrag in runningClassifications
   * gilt eine PROCESSING-Datei als haengengeblieben.
   */
  private readonly STALLED_CLASSIFICATION_MISSES = 2;
  /**
   * Felder, die die Freitextsuche durchsucht.
   *
   * Vorher lief die Suche ueber `columns`, also zusaetzlich ueber `fileStatus`, `size`,
   * `lastModified` und die Pseudo-Spalten selectionFields/icon/contextMenu. Begriffe wie "pdf",
   * "ready" oder "2025" trafen damit Zeilen, in denen der Nutzer den Begriff nirgends sieht -
   * die Trefferzahl wirkte willkuerlich.
   */
  private readonly searchableColumns: readonly string[] = ['name', 'fileType', 'createdBy', 'documentClassTitles'];
  /**
   * Ergebnis von hasPendingUploadInSelection(selectedFiles) fuer diesen Change-Detection-Zyklus.
   * Die Toolbar fragt es vier Mal je Zyklus ab (disabled + Tooltip an zwei Buttons) und der Weg
   * ueber collectAllFileDescendants ist bei grossen Ordnern nicht billig.
   */
  private pendingUploadInSelectionCache?: boolean;
  /** Ergebnis von hasPendingClassificationInSelection(selectedFiles) für diesen Change-Detection-Zyklus. */
  private pendingClassificationInSelectionCache?: boolean;
  /**
   * Ergebnis von {@link visibleSelectedFiles} bei aktiver Suche/Filter (#2770). Schluessel sind die
   * Referenzen von `selectedFiles`/`filteredData` und die Laenge der Auswahl (fuer `push`). In-place-
   * Aenderungen (`splice`, Ersetzen einzelner Eintraege) faengt das Leeren in `ngDoCheck()` ab.
   */
  private visibleSelectionCache?: {selected: ProjectFile[]; length: number; filtered: ProjectFile[]; result: ProjectFile[]};
  /**
   * IDs, fuer die gerade ein Nachladen laeuft. `removeRunningClassification` im StateConfigService
   * feuert `classificationCompleted$` UND aktualisiert `runningClassificationsSet$` - beide Handler
   * wollen dieselbe Datei nachladen. Ohne den Guard sind das zwei identische Requests pro Datei.
   */
  private readonly reloadingClassifiedFileIds = new Set<string>();

  /** Blockgroesse und Sammelfenster fuer das Nachladen klassifizierter Zeilen. */
  private static readonly CLASSIFIED_RELOAD_CHUNK = 100;
  private static readonly CLASSIFIED_RELOAD_DELAY_MS = 400;

  /** Vorgemerkte Dateien fuer den naechsten Sammel-Request (siehe reloadClassifiedFile). */
  private readonly classifiedReloadQueue = new Set<string>();
  private classifiedReloadTimer: ReturnType<typeof setTimeout> | null = null;

  /**
   * Untergrenze der Tabellenbreite: Summe der Mindestbreiten der tatsaechlich gerenderten Spalten.
   *
   * Vorher wurde ueber columnWidths summiert und jedes 'auto' pauschal mit 200px angesetzt (= 1004px).
   * Diese Zahl war zugleich die *feste* Zeilenbreite: unterhalb von xl sind Typ/Groesse ausgeblendet,
   * steuerten aber weiter 400px bei, und die auf 50% gekappte Namensspalte konnte den Rest nicht
   * aufnehmen - das Kontextmenue rutschte aus dem (overflow-x-hidden) Container und in der Mitte
   * blieb eine Luecke. `createdBy` ist nur fuer den Filter im DOM und zaehlt nicht mit.
   */
  get tableMinWidth(): number {
    return this.columns.reduce((sum: number, col: string, i: number) => {
      if (col === 'createdBy') return sum;
      const px = parseInt(this.columnMinWidths[i], 10);
      return sum + (isNaN(px) ? 0 : px);
    }, 0);
  }

  /**
   * Anzahl der Dateien im Projekt, die gerade klassifiziert werden.
   *
   * Quelle ist die Backend-Liste `runningClassifications`, eingeschraenkt auf die Dateien dieser
   * Ansicht, vereinigt mit den in dieser Sitzung selbst angestossenen Klassifizierungen (die das
   * Backend im Moment des Klicks noch nicht meldet). Vorher wurde stattdessen jede Zeile mit
   * DB-Status PROCESSING gezaehlt: nach einem Seitenwechsel laedt loadFiles() den Serverstand neu,
   * `file_classify_state_map` ist dabei leer, und jede in der DB haengengebliebene Altlast zaehlte
   * wieder voll mit - daraus entstand der Sprung von "13 offen" auf "370 werden klassifiziert".
   * Als haengend erkannte Dateien (siehe stalledClassificationIds) zaehlen bewusst nicht mit.
   */
  protected get classifyingCount(): number {
    return countRunningClassifications(
      {
        runningIds: this.stateConfigService.runningClassifications ?? [],
        locallyStartedIds: this.locallyStartedClassificationIds,
      },
      this.classifiableFileIds(),
      this.stalledClassificationIds,
    );
  }

  /**
   * Dateien, die diese Ansicht lokal als "laeuft noch" fuehrt (Upload oder Klassifizierung).
   *
   * Steuert den Fallback-Poll. Bewusst NICHT `classifyingCount`: der zaehlt die Backend-Liste, in
   * der eine frisch hochgeladene Datei nie steht - der Poll waere fuer genau die Faelle, fuer die
   * er gebaut wurde, nie angelaufen.
   */
  private get locallyPendingFileCount(): number {
    return (this.originalData ?? []).filter(
      pf => !pf.isFolder
        && pf.id
        && pf.fileStatus === ProjectFile.FileStatusEnum.PROCESSING
        && !this.stalledClassificationIds.has(pf.id),
    ).length;
  }

  /**
   * Geometrie einer Spalte. Header- und Datenzelle binden dieselbe Quelle, sonst laufen sie
   * auseinander: die Status-Zelle setzte früher keine Breite und war damit schmaler als ihr Header,
   * wodurch alle Spalten rechts davon (u.a. "Zuletzt geändert") verschoben waren.
   */
  columnLayout(col: string, i: number): { width: string | null; minWidth: string | null; flex: string } {
    // Gecacht, weil das Template die Methode pro Zelle dreimal je Change-Detection-Zyklus aufruft.
    // Schluessel enthaelt den Spaltennamen: der Cache war nur nach Index gekeyt und wurde nie
    // invalidiert, weshalb die Mutation von columnWidths[2] in ngOnInit stillschweigend verpuffte.
    const cacheKey = `${col}|${i}`;
    let layout = this.columnLayoutCache.get(cacheKey);
    if (!layout) {
      const hidden = this.isBreakpointHiddenColumn(col);
      layout = {
        width: hidden ? null : this.columnWidths[i],
        minWidth: hidden ? null : this.columnMinWidths[i],
        flex: col === 'name' ? '1 1 0' : '0 1 auto',
      };
      this.columnLayoutCache.set(cacheKey, layout);
    }
    return layout;
  }

  ngOnInit(): void {
    if (this.backButtonActivated)
      this.columnWidths[2] = "auto"; // adjust column width if back button is activated (used in check module)
    this.loadFiles();

    // Was chatBIC an Dateien ändert (umbenennen, verschieben, Ordner, Papierkorb …), sofort zeigen.
    this.chatDataChangedSub = this.chatPageContext.dataChanged$
      .pipe(filter(kind => kind === 'files'))
      .subscribe(() => this.loadFiles());

    // Abgleich eines verknuepften Ordners beendet: Link-Stand uebernehmen und die Dateien neu laden.
    this.sourceLinkSseSub = this.stateConfigService.sseEvents$
      .pipe(filter(event => event.type === 'SOURCE_LINK_SYNCED'))
      .subscribe(event => {
        const link = event.data as SourceLinkView;
        if (link?.projectId === this.stateConfigService.getProjectId()) {
          this.setSourceLinks([...[...this.linkByTargetFolder.values()].filter(l => l.id !== link.id), link]);
          this.loadFiles();
        }
      });

    this.searchSub = this.searchInput$
      .pipe(debounceTime(200), distinctUntilChanged())
      .subscribe(term => {
        this.searchTerm = term;
        this.applyColumnFilters();
      });

    // Keep classification state updated even if the file isn't currently rendered
    this.runningClassificationsSub = this.stateConfigService.runningClassificationsSet$.subscribe(set => {
      for (const pf of this.originalData ?? []) {
        const id = pf.id;
        if (!id) continue;

        const isRunning = set.has(id);
        if (isRunning) {
          this.file_classify_state_map.set(id, classifyStateEnum.IN);
          this.ensureClassificationFallbackPoll();
        } else if (this.file_classify_state_map.get(id) === classifyStateEnum.IN) {
          // Die Klassifizierung ist durch - aber WIE, weiss nur der Server (READY oder ERROR, bei
          // IFC zusaetzlich fragmentsCreated). Deshalb hier NICHT mehr sofort auf "fertig" setzen:
          // das zeigte erst den gruenen Kreis und kippte ihn, sobald der Serverstand nachkam, auf
          // rot. Der Ring dreht weiter, bis die Datei geladen ist; reloadClassifiedFile() setzt
          // AFTER erst mit den echten Daten.
          const projectId = this.stateConfigService.getProjectId();
          if (projectId) {
            this.reloadClassifiedFile(id, projectId);
          } else {
            // Ohne Projektkontext bleibt nur der lokale Abschluss.
            this.file_classify_state_map.set(id, classifyStateEnum.AFTER);
            this.markClassificationSettled(id);
          }
        }
      }
    });

    // Auto-Update: Wenn das Backend eine einzelne Datei als fertig gemeldet hat (SSE
    // CLASSIFICATION_COMPLETED), diese gezielt neu laden - unabhaengig vom
    // runningClassifications-Set (neue Uploads waren dort nie enthalten) und fuer
    // IFC ebenso wie Nicht-IFC. Aktualisiert fileStatus (READY/ERROR) + fragmentsCreated.
    this.classificationCompletedSub = this.stateConfigService.classificationCompleted$.subscribe(fileId => {
      if (!fileId) return;
      const projectId = this.stateConfigService.getProjectId();
      if (!projectId) return;
      const inView = (this.originalData ?? []).some(pf => pf.id === fileId);
      if (!inView) {
        // Race Condition: SSE kam bevor applyUploadJobUpdate die Datei in originalData
        // eingetragen hat. Datei merken und nach dem nächsten Upload-Job-Update nachladen.
        this.pendingClassificationCompletedIds.add(fileId);
        return;
      }
      this.reloadClassifiedFile(fileId, projectId);
    });

    // Ob ueberhaupt ein Upload laeuft - siehe effectiveStatus().
    this.uploadRunningSub = this.uploadService.hasActiveUploads$.subscribe(active => {
      this.uploadRunning = active;
    });

    // Der Statuswechsel UPLOADING -> UPLOADED hat keinen Push-Kanal (SSE meldet nur fertige
    // Klassifizierungen). Der UploadService hat die Meldung aber selbst geschickt und bestaetigt
    // bekommen - hier wird sie in die Liste gespiegelt. Ohne das zaehlte der Ordner als
    // "hochgeladen" nur die bereits fertig klassifizierten Dateien.
    this.uploadedReportedSub = this.uploadService.uploadedReported$.subscribe(fileIds => {
      this.applyUploadedStatus(fileIds);
    });

    // Upload-Job Updates (läuft auch wenn du navigierst; component reagiert nur solange sie lebt)
    this.uploadJobsSub = this.uploadService.jobUpdates$.subscribe(job => {
      if (job.projectId !== this.stateConfigService.getProjectId()) return;
      this.applyUploadJobUpdate(job);
    });

    // Projektwechsel bei stehender Komponente - siehe reloadOnProjectSwitch().
    this.projectSwitchSub = this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe(() => this.reloadOnProjectSwitch());
  }

  /**
   * Laedt die Liste neu, wenn wir inzwischen in einem ANDEREN Projekt stehen.
   *
   * Der Router baut diese Komponente beim Wechsel von `/projects/140/files` nach
   * `/projects/156/files` nicht neu auf: es ist dieselbe Routen-Konfiguration, nur mit einem
   * anderen Parameter, also wird die Instanz wiederverwendet. `ngOnInit` - und damit das einzige
   * `loadFiles()` - laeuft kein zweites Mal. Man sah deshalb die Dateien des alten Projekts,
   * waehrend Kopfzeile und `getProjectId()` schon das neue meinten.
   *
   * `projectChange` des StateConfigService taugt dafuer nicht: beim Routen setzt der
   * `ProjectResolver` `selectedProject` direkt, ohne `selectProject()` und ohne das Event (die
   * chatBIC-Komponenten umgehen das seit jeher genauso ueber NavigationEnd). NavigationEnd feuert
   * erst nach den Resolvern, `getProjectId()` ist hier also bereits das neue Projekt.
   *
   * Nicht nur neu laden, sondern auch die projektbezogene Ansicht zuruecksetzen: Ordnerpfad,
   * Auswahl und die Zwischenspeicher zeigen sonst auf Dateien, die es in diesem Projekt nicht gibt.
   */
  private reloadOnProjectSwitch(): void {
    // Fremdbefuellte Ansicht (Check-Module): die Dateien kommen per @Input, nicht vom Projekt.
    if (this.customFilesActivated) return;

    const projectId = this.stateConfigService.getProjectId();
    if (!projectId || projectId === this.loadedProjectId) return;

    this.navigateToSpecificFolder(undefined, 0);
    this.parentDepthMap.clear();
    // navigateToSpecificFolder laesst die Auswahl im Check-Fall stehen - hier waere sie aber aus
    // dem alten Projekt und damit in jedem Fall falsch.
    this.selectedFiles = [];
    this.folderStatusCache.clear();
    this.file_classify_state_map.clear();
    this.pendingClassificationCompletedIds.clear();
    this.pendingUploadedFileIds.clear();
    this.deletedFileIds.clear();
    this.loading = true;
    this.loadFiles();
  }

  applyColumnFilters(): void {
    // Suche oder Filter aktiv: nur tatsächliche Treffer anzeigen, projektweit bzw. im ganzen aktuellen Ordner-Teilbaum (computeScopeDataset)
    // Laufende Uploads bleiben trotzdem sichtbar - siehe isRunningUpload().
    if (isSearchOrFilterActive(this.searchTerm, this.columnFilters)) {
      const matchingIds = this.computeMatchingIdsByScope();
      this.filteredData = this.originalData.filter(row => {
        if (this.isRunningUpload(row)) return true;
        if (!row.id) return false;
        return matchingIds.has(row.id);
      });
      return;
    }

    // Weder Suche noch Filter aktiv: normale Ordnernavigation, aktuelle Ebene unverändert anzeigen.
    this.filteredData = this.currentFolderData;
  }

  /**
   * Ausnahme von Suche/Filter: Eine Zeile, deren Upload gerade LAEUFT, soll nicht verschwinden, nur
   * weil Suche/Filter aktiv ist - der Nutzer hat sie in dieser Ansicht selbst gerade angelegt und
   * will ihren Fortschritt sehen. Gilt fuer die Tabelle ({@link applyColumnFilters}) und fuer das
   * Anhaken ({@link addElementToSelectedFiles}), damit Auswahl und Tabelle dieselbe Sicht haben.
   *
   * <p>Bewusst NUR laufende Uploads. Vorher galt die Ausnahme fuer jede Zeile mit fileStatus
   * PROCESSING, also fuer jede noch nicht fertig klassifizierte Datei im ganzen Projekt: in einem
   * Projekt mit hunderten offenen Klassifizierungen lieferte jede Suche zusaetzlich all diese
   * Dateien, obwohl ihr Name den Suchbegriff nicht enthaelt. Eine laufende Klassifizierung ist
   * kein Grund, die Suche zu ignorieren - der Status ist dafuer in der Statusspalte sichtbar.</p>
   */
  private isRunningUpload(row: ProjectFile): boolean {
    if (this.effectiveStatus(row) === ProjectFile.FileStatusEnum.UPLOADING) return true;
    // Dummy/Edge: Status schon gepatcht, aber der Upload-Job laeuft noch.
    return this.uploadingDocuments.has(this.uploadProgressKey(row));
  }

  /** Steuert den Leerzustand: ohne Treffer bei aktiver Suche/Filter "Keine Treffer" statt Upload-Hinweis. */
  get hasActiveSearchOrFilter(): boolean {
    return isSearchOrFilterActive(this.searchTerm, this.columnFilters);
  }

  resetSearch(): void {
    this.clearSearchTerm();
    this.applyColumnFilters();
  }

  sortData(column: string, columnName: string, columnType: string): void {
    if (this.sortedColumn === columnName) {
      if (this.sortedDirection === 'asc') {
        this.sortedDirection = 'desc';
      } else if (this.sortedDirection === 'desc') {
        this.sortedColumn = '';
        this.sortedDirection = '';
      } else {
        this.sortedDirection = 'asc';
      }
    } else {
      this.sortedColumn = columnName;
      this.sortedDirection = 'asc';
    }

    if (this.sortedDirection !== '') {
      const factor = this.sortedDirection === 'asc' ? 1 : -1;
      // Sort the currently filteredData
      this.filteredData = [...this.filteredData].sort((a, b) => {
        const aValue: any = (a as any)[column] ?? '';
        const bValue: any = (b as any)[column] ?? '';

        if (columnType === 'text') {
          return factor * String(aValue).localeCompare(String(bValue));
        } else if (columnType === 'status') {
          return factor * (this.statusOrder.indexOf(aValue) - this.statusOrder.indexOf(bValue));
        } else if (columnType === 'date') {
          // lastModified ist im Modell als Date typisiert, kommt über HttpClient aber als ISO-String
          // an - ohne die Konvertierung ergibt die Subtraktion NaN und es wird nie umsortiert.
          return factor * (this.toTimestamp(aValue) - this.toTimestamp(bValue));
        } else {
          return factor * (Number(aValue) - Number(bValue));
        }
      });
    } else {
      // Sortierung aufgehoben: applyColumnFilters() stellt Suche und Filter bereits her.
      // Die frueher hier stehende zweite Suchimplementierung lief ueber alle `columns` und
      // widersprach damit applySearchToDataset().
      this.applyColumnFilters();
    }
  }
  /**
   * Per-Tick-Memo fuer getFolderUploadProgress(). Gleiche Lebensdauer wie folderStatusCache (beide
   * werden in ngDoCheck() geleert) und damit dieselbe Sicherheit: innerhalb eines
   * Change-Detection-Durchlaufs kann sich nichts aendern.
   *
   * Ohne das Memo laeuft die Berechnung pro Ordnerzeile PRO TICK \- und zwar zweimal, weil
   * statusCircleTooltip() sie ueber uploadProgressOf() noch einmal aufruft. Jeder Lauf geht ueber den
   * ganzen Teilbaum (collectAllFileDescendants) plus mehrere some/filter-Durchlaeufe.
   */
  private readonly folderUploadProgressCache = new Map<string, number>();
  private readonly folderUploadCountsCache = new Map<string, {done: number, total: number}>();


  /**
   * Ob dieser Tab gerade Dateien hochlaedt.
   *
   * Rueckfall-Kriterium in {@link effectiveStatus} fuer Zeilen ohne UPLOADED-Meldung: laeuft hier
   * kein Upload, wird auch nichts uebertragen - und das gilt auch nach einem Neuladen.
   */
  private uploadRunning = false;

  /**
   * Anzahl der Dateien, die die Tabelle gerade zeigt.
   *
   * Bewusst aus `filteredData` - also aus genau der Menge, die `applyColumnFilters()`
   * durchgelassen hat. Vorher wurde unabhaengig davon ueber das gesamte `originalData` gerechnet:
   * in einem Unterordner zaehlte die Anzeige projektweit, und die `isTransientUploadState`-Zeilen
   * (laufende Uploads, die trotz Filter sichtbar bleiben) fehlten in der Zahl.
   */
  lengthOfFilteredFilesWithoutFolder() {
    return this.filteredData.filter(pf => !pf.isFolder).length;
  }

  /**
   * Anzahl der Dateien, die in der aktuellen Ansicht ueberhaupt ausgewaehlt werden koennen — der
   * Nenner der "X von Y ausgewaehlt"-Anzeige.
   *
   * <p>Muss denselben Bezugsrahmen haben wie {@link numberOfSelectedFilesWithoutFolder}: das Anhaken
   * eines Ordners selektiert dessen ganzen Unterbaum, der Zaehler zaehlt also baumweit. Ohne aktive
   * Suche/Filter ist {@link filteredData} aber nur die AKTUELLE Ebene — im Wurzelverzeichnis alle
   * Ordner auszuwaehlen ergab deshalb "389 von 8", weil 8 die losen Dateien der Wurzel waren.</p>
   *
   * <p>Bei aktiver Suche/Filter ist {@link filteredData} bereits die flache Treffermenge des ganzen
   * Scopes (siehe {@link applyColumnFilters}) und damit der richtige Nenner. Sonst zaehlt der Scope:
   * das ganze Projekt in der Wurzel, sonst der Teilbaum des aktuellen Ordners.</p>
   *
   * <p>Bewusst NICHT ueber {@code computeScopeDataset()}: das baut eine projektweite children-Map,
   * und diese Methode laeuft aus dem Template in jedem Change-Detection-Zyklus. Der Teilbaum wird
   * daher direkt ueber {@code children} gelaufen (von {@link rebuildChildrenRelations} nach jedem
   * Laden aufgebaut).</p>
   *
   * <p>Zaehler und Nenner haben bei aktiver Suche/Filter denselben Bezugsrahmen (`filteredData`), #2770.</p>
   */
  selectableFileCount(): number {
    if (isSearchOrFilterActive(this.searchTerm, this.columnFilters)) {
      return this.lengthOfFilteredFilesWithoutFolder();
    }
    if ((this.currentDepth || 0) === 0 || !this.currentParent?.id) {
      // Wurzel: originalData ist der vollstaendige, flache Projektbestand.
      return this.originalData.filter(pf => !pf.isFolder).length;
    }
    return this.countFilesInSubtree(this.currentParent);
  }

  /** Zaehlt die Dateien (ohne Ordner) im gesamten Unterbaum eines Ordners. */
  private countFilesInSubtree(folder: ProjectFile): number {
    let count = 0;
    const stack: ProjectFile[] = [...(folder.children ?? [])];

    while (stack.length > 0) {
      const node = stack.pop() as ProjectFile;
      if (node.isFolder) {
        if (node.children?.length) {
          stack.push(...node.children);
        }
      } else {
        count++;
      }
    }
    return count;
  }

  /**
   * IDs, für die CLASSIFICATION_COMPLETED SSE gefeuert wurde, bevor die Datei in
   * originalData eingetragen war (Race Condition bei Neu-Uploads). Werden beim
   * nächsten applyUploadJobUpdate nachgeladen.
   */
  private readonly pendingClassificationCompletedIds = new Set<string>();

  /**
   * IDs, die in dieser Ansicht gelöscht wurden und deren Server-Delete noch nicht bestätigt ist.
   * Notwendig, weil `applyUploadJobUpdate` jedes ProjectFile, das nicht (mehr) in `originalData`
   * steht, wieder einfügt - ein spät eintreffendes Job-Update würde die gerade gelöschte Zeile
   * sonst zurückholen. Wird bei jedem erfolgreichen `loadFiles()` geleert, weil der Server dann
   * die Wahrheit geliefert hat.
   */
  private readonly deletedFileIds = new Set<string>();

  filterConfig: BicFilterConfig[] = [
    {
      key: 'fileStatus',
      displayName: 'Status',
      type: 'status',
      getOptions: () => Array.from(new Set(this.originalData.map(item => item.fileStatus))).filter(v => v != null),
      pipe: new FileStatusPipe()
    },
    {
      key: 'fileType',
      displayName: 'Typ',
      type: 'text',
      getOptions: () => Array.from(new Set(this.originalData.map(item => item.fileType))).filter(v => v != null),
      pipe: new FileTypePipe()
    },
    {
      key: 'lastModified',
      displayName: 'Zuletzt geändert',
      type: 'date',
      options: ['Heute', 'Letzte 7 Tage', 'Letzte 30 Tage', 'Dieses Jahr', 'Vor diesem Jahr']
    },
    {
      key: 'createdBy',
      displayName: 'Ersteller*in',
      type: 'text',
      getOptions: () => Array.from(new Set(this.originalData.map(item => item.createdBy))).filter(v => v != null)
    },
    {
      key: 'documentClassTitles',
      displayName: 'Dokumentenklasse',
      type: 'text',
      getOptions: () => Array.from(new Set(this.originalData.flatMap(item => (item as any).documentClassTitles ?? []))).filter((v): v is string => !!v)
    }
  ];


  protected readonly faArrowsRotate = faArrowsRotate;
  protected readonly faSpinnerThird = faSpinnerThird;

  navigateIntoFolder(row: any): void {
    // Clear the single click timer to prevent it from executing
    if (this.clickTimer) {
      clearTimeout(this.clickTimer);
      this.clickTimer = null;
    }

    if (row.isFolder) {

      // Clear selected rows and details when navigating into folder
      if (!this.calledFromCheck) {
        this.selectedFiles = [];
      }
      this.detailedInfos = false;
      this.selectedElement = {} as ProjectFile;

      this.currentParent = row;
      this.currentDepth++;
      this.parentDepthMap.set(this.currentDepth, row);
      this.clearSearchTerm();
      this.updateCurrentFolderData();
    }
  }

  navigateToSpecificFolder(folder: ProjectFile | undefined, depth: number): void {
    // Clear selected rows and details when navigating
    if (!this.calledFromCheck) {
      this.selectedFiles = [];
    }
    this.detailedInfos = false;
    this.selectedElement = {} as ProjectFile;

    this.currentParent = folder;
    this.currentDepth = depth;

    for (let i = depth + 1; i <= this.parentDepthMap.size; i++) {
      this.parentDepthMap.delete(i);
    }

    this.clearSearchTerm();
    this.updateCurrentFolderData();
  }

  ngOnChanges(changes: SimpleChanges): void {

    if (changes.customFiles || changes.customFilesActivated) {
      this.loadFiles();
    }
  }
  private readonly folderStatusCache = new Map<string, FolderStatusCounts>();

  openUploadSelection(event: Event, isFolder: boolean = false): void {
    if (!this.permissionService.hasProjectPrivilege('PROJECT_FILE_EDIT')) {
      this.notificationsService.showErrorMessage('Sie haben nicht die Berechtigung Dateien oder Ordner hochzuladen.');
      return;
    }
    const input = event.target as HTMLInputElement | null;
    const fileList: FileList | null = input?.files ?? null;
    if (!fileList || fileList.length === 0) {
      if (input) input.value = '';
      if (isFolder) {
        this.bicToastService.error('Leere Ordner können nicht hochgeladen werden.');
      }
      return;
    }

    const files: File[] = Array.from(fileList);
    if (input) input.value = '';

    // Format- und Größenprüfung: beide Ablehnungsgründe werden in EINEM Popup zusammengefasst, auch bei
    // gemischter Teil-Ablehnung (manche Dateien ok, andere nicht) - vorher gab es dafür gar kein Popup.
    let rejectedDialogRef: MatDialogRef<FilesRejectedComponent> | undefined;

    const unsupportedTypeFiles = files.filter((f) => !isAllowedUploadExtension(f.name));
    const sizeCheckedFiles = files.filter((f) => isAllowedUploadExtension(f.name));
    const tooLargeFiles = sizeCheckedFiles.filter((f) => this.calculateSizeInMB(f.size) > 20000);
    const validFiles = sizeCheckedFiles.filter((f) => this.calculateSizeInMB(f.size) <= 20000);

    if (unsupportedTypeFiles.length > 0 || tooLargeFiles.length > 0) {
      const rejectedData: FilesRejectedData = {
        unsupportedType: unsupportedTypeFiles.map((f) => f.name),
        tooLarge: tooLargeFiles.map((f) => f.name),
        validCount: validFiles.length,
      };
      rejectedDialogRef = this.dialog.open(FilesRejectedComponent, {hasBackdrop: true, data: rejectedData});
    }

    if (validFiles.length === 0) {
      return;
    }

    const totalNewSize = validFiles.reduce((sum, f) => sum + f.size, 0);
    if (!this.stateConfigService.canUploadFiles(totalNewSize)) {
      const usedBytes = this.stateConfigService.getStorageUsageBytes();
      // canUploadFiles sperrt nur, wenn die Grenze bekannt ist - hier ist sie also gesetzt. Das
      // `?? 0` ist der Typ-Riegel, kein Ersatzwert: eine unbekannte Grenze kommt nie hierher.
      const limitBytes = this.stateConfigService.getStorageLimitBytes() ?? 0;
      const gb = 1024 * 1024 * 1024;
      const data: StorageLimitExceededData = {
        usedGb: usedBytes / gb,
        limitGb: limitBytes / gb,
        requiredGb: totalNewSize / gb,
        freeGb: Math.max(0, (limitBytes - usedBytes) / gb),
      };
      this.dialog.open(StorageLimitExceededComponent, {hasBackdrop: true, data});
      return;
    }

    const parentId = this.currentParent ? this.currentParent.id ?? '' : undefined;

    // Alle Dateien dieser Auswahl gehören zu einem Batch \- auch beim Ordner-Upload, der pro
    // Zielordner ein eigenes enqueueFiles() macht. Der Fortschritts-Dialog hängt an dieser Id.
    const uploadBatchId = this.uploadService.createBatchId();
    const folderCount = isFolder ? this.countFolderPathsInSelection(validFiles) : 0;
    if (rejectedDialogRef) {
      // Nicht stapeln: erst den Ablehnungs-Hinweis wegklicken lassen, dann den Fortschritt zeigen.
      // Die Uploads laufen aber schon los. Sind sie fertig, bevor der Hinweis zu ist, räumt der
      // UploadService die Jobs per clearFinished() weg und der Dialog fände nie welche (hing bei 0/n).
      // Deshalb die Jobs ab jetzt mitschreiben und dem Dialog als Startbestand mitgeben.
      const seenJobs = new Map<string, UploadJob>();
      const collectSub = this.uploadService.jobsForBatch$(uploadBatchId)
        .subscribe(jobs => jobs.forEach(job => seenJobs.set(job.clientId, job)));
      rejectedDialogRef.afterClosed().subscribe(() => {
        collectSub.unsubscribe();
        this.openUploadProgressDialog(uploadBatchId, validFiles.length, folderCount, Array.from(seenJobs.values()));
      });
    } else {
      this.openUploadProgressDialog(uploadBatchId, validFiles.length, folderCount);
    }

    // ── Ordner-Upload: Sofort Dummies anzeigen, im Hintergrund Ordner anlegen + hochladen ──
    if (isFolder) {
      const projectId = this.stateConfigService.getProjectId();

      // Windows-Style: Top-Level-Ordnernamen im Zielordner eindeutig machen. Ohne das würde derselbe
      // Ordner bei jedem erneuten Upload denselben Namen bekommen - das Backend liefert für einen bereits
      // existierenden Pfad dieselbe Ordner-ID zurück, wodurch zwei Zeilen in der UI dieselbe id teilen und
      // sich beim Auswählen gegenseitig mit auswählen. Verschachtelte Unterordner erben den neuen Namen automatisch.
      const existingFolderNamesLower = this.buildExistingNameSet(this.getEntriesInTargetFolder(parentId), true);
      const topLevelNameMap = new Map<string, string>();
      const relativePathByFile = new Map<File, string>();

      for (const file of validFiles) {
        const originalRelativePath = ((file as any).webkitRelativePath as string).replace(/\\/g, '/');
        const parts = originalRelativePath.split('/');
        const topLevelName = parts[0];

        if (!topLevelNameMap.has(topLevelName)) {
          const uniqueName = this.makeNameUniqueWindowsStyle(topLevelName, existingFolderNamesLower, true);
          existingFolderNamesLower.add(uniqueName.toLocaleLowerCase());
          topLevelNameMap.set(topLevelName, uniqueName);
        }

        parts[0] = topLevelNameMap.get(topLevelName)!;
        relativePathByFile.set(file, parts.join('/'));
      }

      const relativePaths: string[] = validFiles.map((file) => relativePathByFile.get(file)!);

      // ─── 1) Ordnerstruktur aus den Pfaden ableiten ───
      // Sammle alle einzigartigen Ordnerpfade + Top-Level-Ordnernamen
      const folderPathSet = new Set<string>();
      for (const rp of relativePaths) {
        const parts = rp.replace(/\\/g, '/').split('/');
        let pathBuilder = '';
        for (let i = 0; i < parts.length - 1; i++) {
          pathBuilder = pathBuilder ? pathBuilder + '/' + parts[i] : parts[i];
          folderPathSet.add(pathBuilder);
        }
      }
      const sortedFolderPaths = Array.from(folderPathSet).sort((a, b) => a.split('/').length - b.split('/').length);

      // ─── 2) Dummy-Ordner sofort in die UI einfügen ───
      const dummyFolderByPath = new Map<string, ProjectFile>();

      for (const folderPath of sortedFolderPaths) {
        const parts = folderPath.split('/');
        const folderName = parts[parts.length - 1];
        const parentPath = parts.length > 1 ? parts.slice(0, -1).join('/') : null;

        // ParentId bestimmen: entweder ein Dummy-Ordner oder der aktuelle Ordner
        let dummyParentId: string | undefined;
        if (parentPath) {
          dummyParentId = dummyFolderByPath.get(parentPath)?.id;
        } else {
          dummyParentId = parentId;
        }

        // Dummy-Ordner mit temporärer ID
        const tempId = `${FilesComponent.FOLDER_DUMMY_ID_PREFIX}${folderPath}_${Date.now()}`;
        const dummyFolder: ProjectFile = {
          id: tempId,
          name: folderName,
          isFolder: true,
          fileType: ProjectFile.FileTypeEnum.FOLDER,
          size: 0,
          lastModified: new Date(),
          children: [],
          parentId: dummyParentId,
        };

        dummyFolderByPath.set(folderPath, dummyFolder);

        // In originalData einfügen + Parent verlinken
        this.originalData.push(dummyFolder);
        if (dummyParentId) {
          this.setOrRemoveParentInOriginalData(dummyFolder, dummyParentId);
        }
      }

      // ─── 3) Dummy-Dateien sofort in die UI einfügen (UPLOADING) ───
      const dummyFilesByPath = new Map<string, { dummy: ProjectFile; file: File }[]>();

      for (const file of validFiles) {
        const relativePath = relativePathByFile.get(file)!;
        const parts = relativePath.split('/');
        const folderPath = parts.length > 1 ? parts.slice(0, -1).join('/') : null;

        const fileParentId = folderPath
          ? (dummyFolderByPath.get(folderPath)?.id ?? parentId)
          : parentId;

        const dummy: ProjectFile = {
          name: file.name,
          size: file.size,
          lastModified: new Date(),
          isFolder: false,
          fileStatus: ProjectFile.FileStatusEnum.UPLOADING,
          fileType:
            ProjectFile.FileTypeEnum[
              (file.name.split('.').pop() ?? '').toUpperCase() as keyof typeof ProjectFile.FileTypeEnum
              ] || ProjectFile.FileTypeEnum.UNKNOWN,
          parentId: fileParentId,
        };

        // Kein Vorbelegen des Fortschritts: die parentId dieser Zeile wechselt noch von der
        // Dummy- auf die echte Ordner-ID, der Schluessel waere also sofort verwaist. Bis zum
        // ersten Job-Update rendert die Zeile ueber `?? 0` ohnehin 0 %.
        // deferRefresh: der Refresh laeuft einmal nach der Schleife (siehe unten).
        this.addOrRemoveFileToOrFromOriginalData(dummy, false, true);
        if (fileParentId) {
          this.setOrRemoveParentInOriginalData(dummy, fileParentId);
        }

        const key = folderPath ?? '__root__';
        const arr = dummyFilesByPath.get(key) || [];
        arr.push({ dummy, file });
        dummyFilesByPath.set(key, arr);
      }

      // UI sofort aktualisieren
      this.updateCurrentFolderData();

      // Dummies wieder entfernen, wenn kein Upload mehr folgt (Fehler oder Abbruch durch den Nutzer).
      const removeFolderDummies = () => {
        for (const [, dummyFolder] of dummyFolderByPath) {
          this.originalData = this.originalData.filter(pf => pf.id !== dummyFolder.id);
        }
        for (const [, entries] of dummyFilesByPath) {
          for (const {dummy, file} of entries) {
            this.originalData = this.originalData.filter(pf => pf !== dummy);
            this.filteredData = this.filteredData.filter(pf => pf !== dummy);
            this.uploadingDocuments.delete(this.uploadProgressKey(dummy));
          }
        }
        this.updateCurrentFolderData();
      };

      // ─── 4) Backend: Ordnerstruktur anlegen (kein Spinner) ───
      const folderStructureSub = this.fileController.createFolderStructure(relativePaths, projectId, parentId ?? '').subscribe({
        next: (folderMap: { [key: string]: string }) => {

          // ─── 5) Dummy-Ordner durch echte ersetzen ───
          for (const [folderPath, dummyFolder] of dummyFolderByPath) {
            const realId = folderMap[folderPath];
            if (!realId) continue;

            const oldTempId = dummyFolder.id;

            // Bewusst in place: `dummyFolderByPath`, `parent.children` und der Aufraeumpfad
            // removeFolderDummies() zeigen alle auf DIESES Objekt. Wurde es durch eine Kopie
            // ersetzt, lief dummyFolder.id danach auf die echte ID - ein Abbruch haette dann die
            // real angelegte Zeile geloescht statt den Dummy.
            const dummyInOriginal = this.originalData.find(pf => pf.id === oldTempId);
            if (dummyInOriginal) {
              dummyInOriginal.id = realId;
            }

            // Alle Kinder (Ordner + Dateien) auf die echte parentId umhaengen - ebenfalls in place,
            // sonst verlieren die Fortschritts- und Ordnerstatus-Referenzen ihre Zeile.
            for (const pf of this.originalData) {
              if (pf.parentId === oldTempId) {
                // Cast, weil der generierte Client parentId als readonly deklariert. Eine Kopie ist
                // hier keine Option: removeFolderDummies() und die Fortschrittszeilen finden ihre
                // Datei-Dummies ueber die Objektidentitaet (pf !== dummy).
                (pf as { parentId?: string }).parentId = realId;
              }
            }

            // In der dummyFolderByPath die ID aktualisieren
            dummyFolder.id = realId;
          }

          // ─── 6) Datei-Dummies parentIds aktualisieren + Uploads starten ───
          // Dateien nach realem parentId gruppieren
          const filesByRealParentId = new Map<string, File[]>();

          for (const file of validFiles) {
            const relativePath = relativePathByFile.get(file)!;
            const parts = relativePath.split('/');
            const folderPath = parts.length > 1 ? parts.slice(0, -1).join('/') : null;

            const realParentId = folderPath
              ? (folderMap[folderPath] ?? parentId ?? '')
              : (parentId ?? '');

            const existing = filesByRealParentId.get(realParentId) || [];
            existing.push(file);
            filesByRealParentId.set(realParentId, existing);
          }

          // Pro Ordner die Uploads starten
          let enqueuedCount = 0;
          for (const [folderId, folderFiles] of filesByRealParentId) {
            const jobs = this.uploadService.enqueueFiles(projectId, folderFiles, folderId || undefined, uploadBatchId);
            enqueuedCount += jobs.length;
            for (const job of jobs) {
              this.uploadClientIdByDummyKey.set(this.getDummyKey(job.file, job.parentId), job.clientId);
            }
          }

          // Dem Fortschritts-Dialog sagen, wieviele Jobs wirklich entstanden sind. Ohne das wartete
          // er auf `expectedFileCount` und wurde nie fertig, wenn createFolderStructure fuer einen
          // Pfad keine ID lieferte.
          this.reportEnqueuedUploadCount(validFiles.length, enqueuedCount);

          // UI aktualisieren (Ordner haben jetzt echte IDs)
          this.pendingFolderStructureByBatchId.delete(uploadBatchId);
          this.updateCurrentFolderData();
        },
        error: (err: any) => {
          console.error('Fehler beim Anlegen der Ordnerstruktur:', err);
          this.pendingFolderStructureByBatchId.delete(uploadBatchId);
          // Dummies raus \- es wird kein Upload mehr starten, sonst blieben sie ewig in UPLOADING.
          removeFolderDummies();
          // Der Fortschritts-Dialog würde sonst bei 0 % hängen, weil nie Jobs entstehen.
          this.closeUploadProgressDialog(uploadBatchId);
        }
      });

      // Bricht der Nutzer ab, während das Backend noch die Ordner anlegt, gibt es noch keine Jobs:
      // dann muss der laufende Request weg und die Dummies müssen raus, sonst bleiben 120 Zeilen in UPLOADING.
      this.pendingFolderStructureByBatchId.set(uploadBatchId, () => {
        folderStructureSub.unsubscribe();
        removeFolderDummies();
      });

      return;
    }

    // ── Einzelne Dateien hochladen ──

    // Windows-Style: Namen im Zielordner eindeutig machen (inkl. Kollisionen innerhalb des Upload-Batches)
    const existingFileNamesLower = this.buildExistingNameSet(this.getEntriesInTargetFolder(parentId), false);

    // Dateien ggf. umbenennen (so dass Dummy + Upload wirklich denselben finalen Namen haben)
    const renamedValidFiles: File[] = validFiles.map((file) => {
      const uniqueName = this.makeNameUniqueWindowsStyle(file.name, existingFileNamesLower, false);
      existingFileNamesLower.add(uniqueName.toLocaleLowerCase());
      return this.renameFileKeepingContent(file, uniqueName);
    });

    const newProjectFiles: ProjectFile[] = renamedValidFiles.map((file) => ({
      name: file.name,
      size: file.size,
      lastModified: new Date(),
      isFolder: false,
      fileStatus: ProjectFile.FileStatusEnum.UPLOADING,
      fileType:
        ProjectFile.FileTypeEnum[
          (file.name.split('.').pop() ?? '').toUpperCase() as keyof typeof ProjectFile.FileTypeEnum
          ] || ProjectFile.FileTypeEnum.UNKNOWN,
      parentId,
    }));

    for (const npf of newProjectFiles) {
      this.uploadingDocuments.set(this.uploadProgressKey(npf), 0);
    }

    // Dummy sofort in UI + originalData. deferRefresh: der Refresh laeuft einmal nach der Schleife.
    for (const npf of newProjectFiles) {
      if (npf.parentId) {
        this.setOrRemoveParentInOriginalData(npf, npf.parentId);
      }
      this.addOrRemoveFileToOrFromOriginalData(npf, false, true);
    }

    // Wichtig: Dummy sofort sichtbar machen (auch wenn Filter/Suche aktiv sind)
    this.filteredData = [...newProjectFiles, ...this.filteredData];
    this.updateCurrentFolderData();


    // Uploads zentral starten (laufen weiter über Navigation hinweg)
    const jobs = this.uploadService.enqueueFiles(this.stateConfigService.getProjectId(), renamedValidFiles, parentId, uploadBatchId);
    // Job clientIds merken (DummyKey -> clientId)
    for (const job of jobs) {
      this.uploadClientIdByDummyKey.set(this.getDummyKey(job.file, job.parentId), job.clientId);
    }
    this.reportEnqueuedUploadCount(renamedValidFiles.length, jobs.length);
  }

  private getDummyKey(file: File, parentId?: string): string {
    return `${file.name}__${file.size}__${parentId ?? ''}`;
  }

  /**
   * Oeffnet ein Koordinationsmodell im Viewer.
   *
   * `linkedFiles` ist in der Tabellen-Projektion NICHT enthalten (siehe ProjectFileTableService) -
   * im Speicher hat es nur ein Modell, das in dieser Sitzung erstellt oder bearbeitet wurde. Fuer
   * jedes andere (eine Kopie, oder einfach nach einem Reload) war die Liste leer und die Methode
   * kehrte still zurueck: der Klick auf "Oeffnen" tat schlicht nichts. Deshalb wird der volle Stand
   * hier bei Bedarf nachgeladen - und wenn wirklich keine IFC verknuepft ist, gibt es eine Meldung
   * statt Stille.
   */
  openCoordinationModelFile(file: ProjectFile): void {
    const linked = file.linkedFiles ?? [];
    if (linked.length > 0) {
      this.openLinkedCoordinationFiles(file);
      return;
    }

    const projectId = this.stateConfigService.getProjectId();
    if (!file.id || !projectId) {
      this.notificationsService.showErrorMessage('Koordinationsmodell konnte nicht geöffnet werden.');
      return;
    }

    this.fileController.getFile(projectId, file.id).subscribe({
      next: (full: ProjectFile) => {
        // Merkt die verknuepften Dateien an der Zeile, damit der naechste Klick sie nicht erneut holt.
        this.patchFileInDataSets(full);

        const fullLinked = full.linkedFiles ?? [];
        if (fullLinked.length === 0) {
          this.notificationsService.showErrorMessage(
            'Dieses Koordinationsmodell hat keine verknüpften IFC-Dateien. '
            + 'Bitte über "Verknüpfte Dateien bearbeiten" zuordnen.',
          );
          return;
        }
        this.openLinkedCoordinationFiles(full);
      },
      error: () => {
        this.notificationsService.showErrorMessage('Koordinationsmodell konnte nicht geladen werden.');
      },
    });
  }

  ngDoCheck(): void {
    this.folderStatusCache.clear();
    this.folderUploadProgressCache.clear();
    this.folderUploadCountsCache.clear();
    this.pendingUploadInSelectionCache = undefined;
    this.pendingClassificationInSelectionCache = undefined;
    this.visibleSelectionCache = undefined;
    // Nach dem Leeren der Caches: liest visibleSelectedFiles() (#2770).
    this.reportSelectionToChat();
  }

  /** Zuletzt an chatBIC gemeldete Auswahl – damit nur Änderungen gemeldet werden. */
  private reportedSelectionKey = '';

  /**
   * Meldet die markierten Dateien an chatBIC (`ChatPageContextService`), damit „Fasse die markierten
   * Dateien zusammen" ohne Umweg über die Kontext-Auswahl funktioniert. Über ngDoCheck, weil die
   * Auswahl an vielen Stellen per `push` verändert wird – ein Setter sähe das nicht. Nicht im
   * Auswahlmodus anderer Seiten (`calledFromCheck`): dort ist die Markierung keine Nutzeraussage
   * über diese Seite. Gemeldet werden nur die sichtbaren markierten Dateien (#2770), wie im Zähler.
   */
  private reportSelectionToChat(): void {
    if (this.calledFromCheck) return;
    const ids = this.visibleSelectedFiles().map(file => file.id).filter((id): id is string => !!id);
    const key = ids.join(',');
    if (key === this.reportedSelectionKey) return;
    this.reportedSelectionKey = key;
    this.chatPageContext.set('files', `/projects/${this.stateConfigService.getProjectId()}/files`,
      {kind: 'files', selectedFileIds: ids});
  }

  ngOnDestroy(): void {
    this.chatPageContext.clear('files');
    this.searchSub?.unsubscribe();
    this.runningClassificationsSub?.unsubscribe?.();
    this.classificationCompletedSub?.unsubscribe?.();
    this.uploadJobsSub?.unsubscribe();
    this.uploadRunningSub?.unsubscribe();
    this.uploadedReportedSub?.unsubscribe();
    this.deleteJobsSub?.unsubscribe();
    this.projectSwitchSub?.unsubscribe();
    this.chatDataChangedSub?.unsubscribe();
    this.sourceLinkSseSub?.unsubscribe();
    this.stopClassificationFallbackPoll();
    for (const timeoutId of this.ifcReadyPollTimeoutByFileId.values()) {
      clearTimeout(timeoutId);
    }
    this.ifcReadyPollTimeoutByFileId.clear();
  }

  private isDateInRange(value: any, range: string): boolean {
    if (!value) return false;

    const date = new Date(value);
    if (isNaN(date.getTime())) return false;

    // Normalize to date-only (midnight) for consistent comparisons
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const sevenDaysAgo = new Date(today);
    sevenDaysAgo.setDate(today.getDate() - 7);

    const thirtyDaysAgo = new Date(today);
    thirtyDaysAgo.setDate(today.getDate() - 30);

    const threeSixtyFiveDaysAgo = new Date(today);
    threeSixtyFiveDaysAgo.setDate(today.getDate() - 365);

    switch (range) {
      case 'Heute':
        return d.getTime() === today.getTime();

      case 'Letzte 7 Tage':
        return d >= sevenDaysAgo || d.getTime() === today.getTime();

      case 'Letzte 30 Tage':
        return d >= thirtyDaysAgo || d.getTime() === today.getTime();

      case 'Dieses Jahr':
        return d >= threeSixtyFiveDaysAgo || d.getTime() === today.getTime();

      case 'Vor diesem Jahr':
        return d < threeSixtyFiveDaysAgo;

      default:
        return false;
    }
  }

// Hilfsfunktion: wendet die aktuellen columnFilters auf ein beliebiges Dataset an
  private applyFiltersToDataset(dataset: ProjectFile[]): ProjectFile[] {
    return dataset.filter(item =>
      Object.entries(this.columnFilters).every(([col, values]) => {
        if (!values || values.length === 0) return true;

        const columnIndex = this.columns.indexOf(col);
        const columnType = columnIndex !== -1 ? this.columnTypes[columnIndex] : null;

        if (columnType === 'date') {
          const raw = (item as any)[col];
          if (!raw) return false;

          const itemDate = new Date(raw);
          if (isNaN(itemDate.getTime())) return false;

          // true wenn irgendein ausgewählter Range passt
          return values.some(selectedRange => this.isDateInRange(itemDate, selectedRange));
        }

        const fieldValue = (item as any)[col];
        if (Array.isArray(fieldValue)) {
          return values.some(v => fieldValue.includes(v));
        }
        return values.includes(fieldValue);
      })
    );
  }

  retryClassification(file: ProjectFile): void {
    if (!this.permissionService.hasProjectPrivilege('PROJECT_FILE_EDIT')) {
      this.bicToastService.error('Sie haben nicht die Berechtigung Dateien zu klassifizieren.');
      return;
    }

    if (!file.id || file.isFolder) {
      this.bicToastService.error('Datei konnte nicht klassifiziert werden.');
      return;
    }

    // Der Riegel fuer alle Aufrufwege - der Kreis ist nur die Ansicht. Ohne das laesst sich derselbe
    // aussichtslose Versuch ueber das Kontextmenue weiter ausloesen.
    if (this.isClassificationExhausted(file)) {
      this.bicToastService.error(
        'Diese Datei lässt sich nicht klassifizieren. Bitte löschen und erneut hochladen.',
      );
      return;
    }

    this.fileController.classify(this.stateConfigService.getProjectId(), file.id).subscribe({
      next: (response: ProjectFile) => {
        this.markFileProcessingLocally(file.id!, response);
        this.bicToastService.success('Klassifizierung wird erneut versucht.');
      },
      error: (error: unknown) => {
        console.error('Fehler beim erneuten Klassifizieren der Datei:', error);
        this.bicToastService.error('Klassifizierung konnte nicht gestartet werden. Bitte erneut versuchen.');
      }
    });
  }

  reclassifyFile(file: ProjectFile): void {
    if (!this.permissionService.hasProjectPrivilege('PROJECT_FILE_EDIT')) {
      this.notificationsService.showErrorMessage('Sie haben nicht die Berechtigung Dateien zu klassifizieren.');
      return;
    }

    // Nur IFC-Dateien können neu klassifiziert werden und nur wenn noch keine Fragments existieren
    if (file.fileType !== ProjectFile.FileTypeEnum.IFC) {
      this.notificationsService.showErrorMessage('Nur IFC-Dateien können neu klassifiziert werden.');
      return;
    }

    if (this.hasFragments(file)) {
      this.notificationsService.showErrorMessage('Diese Datei hat bereits Fragments und kann nicht neu klassifiziert werden.');
      return;
    }

    // Nicht in eine laufende Klassifizierung hineinstarten. Das war der Fehler aus dem Bericht:
    // direkt nach dem Upload laeuft die Analyse, die Datei hat noch keine Fragments - der Punkt war
    // also sichtbar, und ein Klick schob eine zweite Klassifizierung daneben, die die laufende
    // zerlegte. Der Menuepunkt ist inzwischen gesperrt (canRetryIfcClassification); dies hier ist
    // der Riegel fuer alle anderen Aufrufwege.
    //
    // Bewusst NICHT in retryClassification(): dort ist der Klick auf einen haengengebliebenen
    // Kreis ausdruecklich erwuenscht, und eine haengende Klassifizierung steht auf PROCESSING.
    if (this.isBusyWithUploadOrAnalysis(file)) {
      this.notificationsService.showErrorMessage(
        'Diese Datei wird gerade verarbeitet. Bitte warten, bis die Analyse abgeschlossen ist.',
      );
      return;
    }

    if (!file.id) {
      this.notificationsService.showErrorMessage('Datei konnte nicht klassifiziert werden.');
      return;
    }

    // Starte die Neuklassifizierung
    this.fileController.classify(this.stateConfigService.getProjectId(), file.id).subscribe({
      next: (response: ProjectFile) => {
        this.markFileProcessingLocally(file.id!, response);
        this.notificationsService.showSuccessMessage(`Datei wird neu klassifiziert.`);
      },
      error: (error: unknown) => {
        console.error('Fehler beim Klassifizieren der Datei:', error);
        this.notificationsService.showErrorMessage('Datei konnte nicht klassifiziert werden. Bitte erneut versuchen.');
      }
    });
  }

  onFiltersChange(activeFilters: { [key: string]: any[] }) {
    this.columnFilters = activeFilters;
    this.activeFilters = Object.fromEntries(
      Object.entries(this.columnFilters).filter(([_, v]) => v && v.length > 0)
    );
    this.applyColumnFilters();
  }

  private buildChildrenMap(dataset: ProjectFile[]): Map<string, ProjectFile[]> {
    const map = new Map<string, ProjectFile[]>();
    for (const item of dataset) {
      const pid = item.parentId ?? '__root__';
      if (!map.has(pid)) map.set(pid, []);
      map.get(pid)!.push(item);
    }
    return map;
  }

  private computeScopeDataset(): ProjectFile[] {
    // Root: global suchen
    if ((this.currentDepth || 0) === 0 || !this.currentParent?.id) {
      return this.originalData;
    }

    // In Ordner: nur dieser Ordner + alle Nachkommen
    const folderId = this.currentParent.id;
    const childrenMap = this.buildChildrenMap(this.originalData);

    const result: ProjectFile[] = [];
    const stack: ProjectFile[] = [...(childrenMap.get(folderId) ?? [])];

    while (stack.length > 0) {
      const node = stack.pop()!;
      result.push(node);

      if (node.id) {
        const children = childrenMap.get(node.id);
        if (children && children.length) stack.push(...children);
      }
    }

    return result;
  }

  private computeMatchingIdsByScope(): Set<string> {
    const scope = this.computeScopeDataset();

    // erst Filter (global aktiv), dann Suche innerhalb des Scopes
    const filtered = this.applyFiltersToDataset(scope);
    const searched = this.applySearchToDataset(filtered, this.searchTerm);

    return new Set(searched.map(x => x.id).filter((id): id is string => !!id));
  }

  renameFile(file: ProjectFile): void {
    if(!this.permissionService.hasProjectPrivilege('PROJECT_FILE_EDIT')){
      this.notificationsService.showErrorMessage('Sie haben nicht die Berechtigung Dateien umzunennen.');
      return;
    }

    if (this.isPendingUploadRow(file)) {
      this.notificationsService.showErrorMessage(
        'Diese Datei wird noch hochgeladen und kann erst danach umbenannt werden.',
      );
      return;
    }
    // Auch ein ORDNER, in den gerade hochgeladen wird, ist gesperrt - nicht nur die Zeile selbst.
    // createFolderStructure ordnet Ordner ueber ihren NAMEN zu: wird er mitten im Upload geaendert,
    // legt ein spaeterer Block einen zweiten Ordner mit dem alten Namen an und die Dateien
    // verteilen sich auf zwei Ordner.
    if (file.isFolder && this.blockedByPendingUpload(file, 'umbenannt')) return;

    const oldName = file.name ?? '';
    let filename = oldName;
    const extension = filename.substring(filename.lastIndexOf('.'));
    if (!file.isFolder) {
      filename = filename.substring(0, filename.lastIndexOf('.'));
    }

    const dialogRef = this.dialog.open(RenameComponent, {
      hasBackdrop: false,
      data: {
        oldName: filename,
        // Geschwister des Ziels, nicht die aktuelle Ebene: bei aktiver Suche zeigt die Tabelle eine
        // flache, projektweite Trefferliste - die Dublettenpruefung lief dann gegen fremde Ordner.
        otherNames: this.siblingNamesOf(file),
        isFolder: file.isFolder,
      },
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result == undefined) return;

      const newName = file.isFolder ? result.renameName : result.renameName + extension;
      this.applyRenameLocally(file.id, newName);

      this.fileController.renameProjectFile(newName, this.stateConfigService.getProjectId(), file.id ?? '').subscribe({
        next: () => {
          this.notificationsService.showSuccessMessage(`Datei wurde erfolgreich umbenannt.`);
        },
        error: (error: unknown) => {
          // Ohne Rollback blieb der neue Name lokal stehen, obwohl das Backend den alten behielt.
          console.error('Fehler beim Umbenennen:', error);
          this.applyRenameLocally(file.id, oldName);
          this.notificationsService.showErrorMessage(
            'Umbenennen fehlgeschlagen. Der bisherige Name bleibt bestehen.',
          );
        },
      });
    });
  }

  calculateSizeInMB(size: number): number {
    return size / 1024 / 1024;
  }

  calculateSizeInGB(size: number): number {
    return size / 1024 / 1024 / 1024;
  }

  moveFile(file?: ProjectFile): void {
    if(!this.permissionService.hasProjectPrivilege('PROJECT_FILE_EDIT')){
      this.notificationsService.showErrorMessage('Sie haben nicht die Berechtigung Dateien zu verschieben.');
      return;
    }

    if (this.blockedByPendingUpload(file, 'verschoben')) return;
    if (this.blockedByPendingClassification(file, 'verschoben')) return;

    let filesToMove: ProjectFile[] = [];
    if (file != undefined) {
      filesToMove.push(file);
    } else {
      const visible = this.visibleSelectedFiles();
      if (visible.length) {
        filesToMove = visible;
      }
    }

    const dialogRef = this.dialog.open(CopyMoveComponent, {
      hasBackdrop: false,
      data: {
        copy: false,
        originalData: this.getDialogTreeData(),
        filesToMove: filesToMove,
        // Hinweis bei Suche/Filter: Ordner gehen samt ausgeblendetem Inhalt mit (#2770).
        searchOrFilterActive: isSearchOrFilterActive(this.searchTerm, this.columnFilters),
      },
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result === undefined) {
        return;
      }

      const newParentId: string | null = result?.id ?? null;
      const newParentIdOrUndef: string | undefined = newParentId ?? undefined;
      const projectId = this.stateConfigService.getProjectId();

      const idsToMove = filesToMove.map(f => f.id).filter((id): id is string => !!id);
      if (idsToMove.length === 0) {
        this.selectedFiles = [];
        this.detailedInfos = false;
        return;
      }

      // Hilfsmap damit wir schnell auf Elemente zugreifen knnen
      const byId = new Map<string, ProjectFile>();
      for (const pf of this.originalData) {
        if (pf.id) byId.set(pf.id, pf);
      }

      // Nur Wurzel-Elemente verschieben: Elemente herausfiltern, deren Vorfahren bereits in
      // der Verschiebe-Liste enthalten sind. Unterordner folgen ihrem Elternordner
      // automatisch mit, sodass die Ordnerhierarchie erhalten bleibt.
      const idsToMoveSet = new Set(idsToMove);
      const isDescendantOfMovedItem = (id: string): boolean => {
        let currentId: string | undefined = byId.get(id)?.parentId;
        while (currentId) {
          if (idsToMoveSet.has(currentId)) return true;
          currentId = byId.get(currentId)?.parentId;
        }
        return false;
      };
      const rootIdsToMove = idsToMove.filter(id => !isDescendantOfMovedItem(id));

      // --- Optimistisches UI-Update (nur Wurzel-Elemente) ---
      for (const pfId of rootIdsToMove) {
        const idx = this.originalData.findIndex(x => x.id === pfId);
        if (idx === -1) continue;

        const pf = this.originalData[idx];
        const oldParentId = pf.parentId ?? null;

        const updated: ProjectFile = {
          ...pf,
          parentId: newParentIdOrUndef,
        };
        this.originalData[idx] = updated;
        if (updated.id) byId.set(updated.id, updated);

        // aus altem Parent lschen
        if (oldParentId) {
          this.setOrRemoveParentInOriginalData(pf, oldParentId, true);
        }
        // in neuem Parent einfgen
        if (newParentId) {
          this.setOrRemoveParentInOriginalData(updated, newParentId, false);
        }

        // Bewusst KEINE Nachbearbeitung der Nachkommen: beim Verschieben eines Ordners aendert
        // sich die parentId keines einzigen von ihnen. Der Ordner wechselt den Platz, sein Inhalt
        // bleibt relativ zu seinem eigenen Elternteil, wo er war.
        //
        // Hier stand eine Schleife, die genau das tat - und zwar falsch: sie schrieb JEDEM
        // Nachkommen die ID des verschobenen Ordners ins parentId-Feld, auch Enkeln und Urenkeln
        // (die Variable `folder` aenderte sich in der Schleife nie). Da die Tabelle nach parentId
        // filtert, klappte der Teilbaum damit auf eine Ebene zusammen: alles aus tieferen Ebenen
        // lag ploetzlich direkt im verschobenen Ordner, bis das naechste loadFiles() den
        // Serverstand nachlieferte.
      }

      // Daten neu berechnen (damit filteredData/Filter sofort korrekt sind)
      this.selectedFiles = [];
      this.detailedInfos = false;
      this.updateCurrentFolderData();

      // --- Backend Call im Hintergrund ---
      const finishOk = () => {
        this.notificationsService.showSuccessMessage(`Datei${rootIdsToMove.length > 1 ? 'en wurden' : ' wurde'} erfolgreich verschoben.`);
      };

      const finishErr = (error: unknown) => {
        this.notificationsService.showErrorMessage('Verschieben fehlgeschlagen. Daten werden neu geladen.');
        this.loadFiles();
        this.navigateToSpecificFolder(this.parentDepthMap.get(this.currentDepth), this.currentDepth);
      };

      if (rootIdsToMove.length > 1) {
        this.fileController
          .moveMultipleProjectFiles(rootIdsToMove, projectId, newParentId ?? ' ')
          .subscribe({
            next: () => finishOk(),
            error: (e) => finishErr(e),
          });
      } else {
        this.fileController
          .moveProjectFile(newParentId ?? ' ', projectId, rootIdsToMove[0])
          .subscribe({
            next: () => finishOk(),
            error: (e) => finishErr(e),
          });
      }
    });
  }

  onSelectAllChange(event: any): void {
    const isChecked = !!event?.target?.checked;

    if (isChecked) {
      // Selektiere alle sichtbaren Zeilen, aber mit derselben Rekursions-/Elternlogik wie beim Single-Select
      for (const row of this.filteredData) {
        this.addElementToSelectedFiles(row);
      }
    } else {
      // Deselektiere alle sichtbaren Zeilen inkl. deren Kinder
      for (const row of this.filteredData) {
        this.removeElementFromSelectedFiles(row);
      }

      // Danach: Eltern ggf. "aufräumen", wenn sie nur wegen Vollständigkeit drin waren
      // (d.h. wenn nach dem Entfernen keine selektierten Nachkommen mehr existieren)
      const visibleParentIds = new Set(
        this.filteredData
          .map(r => r.parentId)
          .filter((id): id is string => !!id)
      );

      const hasAnySelectedDescendant = (folderId: string): boolean => {
        const stack: ProjectFile[] = this.originalData.filter(pf => pf.parentId === folderId);
        while (stack.length > 0) {
          const node = stack.pop() as ProjectFile;
          if (this.selectedFiles.some(sf => sf.id === node.id)) return true;
          if (node.isFolder) {
            stack.push(...this.originalData.filter(pf => pf.parentId === node.id));
          }
        }
        return false;
      };

      const pruneParentsUpwards = (parentId: string | undefined | null): void => {
        let currentId = parentId ?? null;

        while (currentId) {
          const parent = this.originalData.find(pf => pf.id === currentId);
          if (!parent) break;

          // Wenn unter dem Parent nichts mehr selektiert ist, Parent aus selectedFiles entfernen
          if (!hasAnySelectedDescendant(currentId)) {
            this.selectedFiles = this.selectedFiles.filter(sf => sf.id !== currentId);
            currentId = parent.parentId ?? null;
          } else {
            break;
          }
        }
      };

      for (const pid of visibleParentIds) {
        pruneParentsUpwards(pid);
      }
    }

    this.selectedFilesChange.emit(this.selectedFiles);
  }


  areAllRowsSelected(): boolean {
    const filteredIds = new Set(this.filteredData.map(r => r.id).filter((id): id is string => !!id));
    if (filteredIds.size === 0) return false;

    const selectedIds = new Set(this.selectedFiles.map(f => f.id).filter((id): id is string => !!id));
    return Array.from(filteredIds).every(id => selectedIds.has(id));
  }

  areSomeRowsSelected(): boolean {
    const filteredIds = new Set(this.filteredData.map(r => r.id).filter((id): id is string => !!id));
    if (filteredIds.size === 0) return false;

    const selectedIds = new Set(this.selectedFiles.map(f => f.id).filter((id): id is string => !!id));
    const selectedInFilteredCount = Array.from(filteredIds).filter(id => selectedIds.has(id)).length;

    return selectedInFilteredCount > 0 && selectedInFilteredCount < filteredIds.size;
  }

  copyFile(file?: ProjectFile): void {
    if(!this.permissionService.hasProjectPrivilege('PROJECT_FILE_EDIT')){
      this.notificationsService.showErrorMessage('Sie haben nicht die Berechtigung Dateien zu kopieren.');
      return;
    }

    if (this.blockedByPendingUpload(file, 'kopiert')) return;
    if (this.blockedByPendingClassification(file, 'kopiert')) return;

    let filesToCopy: ProjectFile[] = [];
    if (file != undefined) {
      filesToCopy.push(file);
    } else {
      const visible = this.visibleSelectedFiles();
      if (visible.length) {
        filesToCopy = visible;
      }
    }

    const dialogRef = this.dialog.open(CopyMoveComponent, {
      hasBackdrop: false,
      data: {
        copy: true,
        originalData: this.getDialogTreeData(),
        filesToMove: filesToCopy,
        // Hinweis bei Suche/Filter: Ordner gehen samt ausgeblendetem Inhalt mit (#2770).
        searchOrFilterActive: isSearchOrFilterActive(this.searchTerm, this.columnFilters),
      },
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result === undefined) {
        return;
      }

      const newParentId: string | null = result?.id ?? null;
      const newParentIdOrUndef: string | undefined = newParentId ?? undefined;
      const projectId = this.stateConfigService.getProjectId();

      const idsToCopy = filesToCopy.map(f => f.id).filter((id): id is string => !!id);
      if (idsToCopy.length === 0) {
        this.selectedFiles = [];
        this.detailedInfos = false;
        return;
      }

      // Copy-Batch-Key, um später alle Dummies (Ordner + rekursive Children) sauber entfernen zu können.
      const copyBatchKey = `copy-${Date.now()}-${Math.random().toString(16).slice(2)}`;

      // --- Optimistisches UI-Update: Dummy-Copies sofort anzeigen ---
      const dummyCopies: Array<{ sourceId: string; dummy: ProjectFile }> = [];
      const dummyCopiesForCurrentView: ProjectFile[] = [];

      // Windows-Style: Namen im Zielordner eindeutig machen (inkl. Kollisionen innerhalb des Copy-Batches)
      const existingTargetNamesLower = this.buildExistingNameSet(this.getEntriesInTargetFolder(newParentIdOrUndef), false);
      const existingTargetFolderNamesLower = this.buildExistingNameSet(this.getEntriesInTargetFolder(newParentIdOrUndef), true);

      // Hilfsfunktion: Dummy-Subtree (Ordner-Inhalt) sofort mit in originalData spiegeln,
      // damit die UI beim Öffnen des kopierten Ordners nicht leer ist.
      const addFolderSubtreeDummies = (
        sourceFolder: ProjectFile,
        dummyFolderId: string,
        parentDepth: number
      ): void => {
        if (!sourceFolder?.isFolder || !Array.isArray(sourceFolder.children) || sourceFolder.children.length === 0) {
          return;
        }

        for (const child of sourceFolder.children) {
          const childDummyId = `${copyBatchKey}:${dummyFolderId}:${child.id ?? child.name ?? Math.random().toString(16).slice(2)}`;

          const childDummy: ProjectFile = {
            ...child,
            id: childDummyId,
            parentId: dummyFolderId,
            // Damit Filter/Status-UI die Dummies nicht wegwirft
            fileStatus: child.isFolder
              ? ProjectFile.FileStatusEnum.READY
              : (child.fileStatus ?? ProjectFile.FileStatusEnum.READY),
            // Ordner mit eigener children-Liste (wird rekursiv aufgebaut)
            children: child.isFolder ? [] : (child.children ?? []),
            created: new Date(),
            lastModified: new Date(),
            // @ts-ignore - Hilfsmarker nur im Frontend
            copyBatchKey,
            // @ts-ignore
            isCopyDummy: true,
            // @ts-ignore
            copyDummyDepth: parentDepth + 1,
          } as ProjectFile;

          this.addOrRemoveFileToOrFromOriginalData(childDummy);
          this.setOrRemoveParentInOriginalData(childDummy, dummyFolderId, false);

          if (child.isFolder) {
            addFolderSubtreeDummies(child, childDummyId, parentDepth + 1);
          }
        }
      };

      for (const sourceId of idsToCopy) {
        const source = this.originalData.find(pf => pf.id === sourceId);
        if (!source) continue;

        const targetName = this.makeNameUniqueWindowsStyle(
          source.name ?? '',
          source.isFolder ? existingTargetFolderNamesLower : existingTargetNamesLower,
          !!source.isFolder
        );

        if (source.isFolder) {
          existingTargetFolderNamesLower.add(targetName.toLocaleLowerCase());
        } else {
          existingTargetNamesLower.add(targetName.toLocaleLowerCase());
        }

        const dummyId = `${copyBatchKey}:${sourceId}`;

        const dummy: ProjectFile = {
          ...source,
          id: dummyId,
          parentId: newParentId,
          name: targetName,
          fileStatus: ProjectFile.FileStatusEnum.PROCESSING,
          children: source.isFolder ? [] : (source.children ?? []),
          created: new Date(),
          lastModified: new Date(),
          // @ts-ignore
          copyBatchKey,
          // @ts-ignore
          isCopyDummy: true,
        } as ProjectFile;

        this.addOrRemoveFileToOrFromOriginalData(dummy);
        if (newParentId) {
          this.setOrRemoveParentInOriginalData(dummy, newParentId, false);
        }

        // Wenn wir einen vollen Ordner kopieren: Subtree sofort als Dummies spiegeln.
        if (source.isFolder) {
          addFolderSubtreeDummies(source, dummyId, 0);
        }

        dummyCopies.push({ sourceId, dummy });

        if ((this.currentParent?.id ?? null) === (newParentId ?? null)) {
          dummyCopiesForCurrentView.push(dummy);
        }
      }

      if (dummyCopiesForCurrentView.length > 0) {
        this.filteredData = [...dummyCopiesForCurrentView, ...this.filteredData];
      }

      this.selectedFiles = [];
      this.detailedInfos = false;
      this.updateCurrentFolderData();

      // Helper: entfernt ALLE Dummies dieses Batches (Ordner + rekursive Children)
      const removeCopyBatchDummies = (): void => {
        const toRemove = (this.originalData ?? []).filter((pf: any) => pf?.copyBatchKey === copyBatchKey);
        for (const d of toRemove) {
          this.addOrRemoveFileToOrFromOriginalData(d, true);
          this.setOrRemoveParentInOriginalData(d, d.parentId ?? null, true);
        }
      };

      // --- Backend Calls im Hintergrund (pro Datei) ---
      let finished = 0;
      let failed = 0;

      // Helper: Response-Tree flatten, so dass unsere UI (die nach parentId filtert) die Children sofort findet.
      const flattenResponseTree = (root: ProjectFile): ProjectFile[] => {
        const out: ProjectFile[] = [];
        const stack: Array<{ node: ProjectFile; parentId: string | null }> = [{ node: root, parentId: root.parentId ?? null }];

        while (stack.length > 0) {
          const { node, parentId } = stack.pop()!;

          // parentId ggf. vom Traversal überschreiben (falls Backend es bei Children nicht setzt)
          const normalized: ProjectFile = {
            ...node,
            parentId: (node.parentId ?? parentId ?? null) as any,
          } as ProjectFile;

          out.push(normalized);

          if (node.isFolder && Array.isArray(node.children) && node.children.length > 0) {
            for (const child of node.children) {
              stack.push({ node: child, parentId: normalized.id ?? null });
            }
          }
        }

        return out;
      };

      const finalize = () => {
        if (failed === 0) {
          this.notificationsService.showSuccessMessage(`Datei${idsToCopy.length > 1 ? 'en wurden' : ' wurde'} erfolgreich kopiert.`);
          return;
        }

        if (failed === idsToCopy.length) {
          this.notificationsService.showErrorMessage('Kopieren fehlgeschlagen. Daten werden neu geladen.');
        } else {
          this.notificationsService.showErrorMessage(`Kopieren teilweise fehlgeschlagen (${failed}/${idsToCopy.length}). Daten werden neu geladen.`);
        }

        this.loadFiles();
        this.navigateToSpecificFolder(this.parentDepthMap.get(this.currentDepth), this.currentDepth);
      };

      const onOneFinished = () => {
        finished++;
        if (finished === idsToCopy.length) {
          finalize();
        }
      };

      for (const sourceId of idsToCopy) {
        const entry = dummyCopies.find(x => x.sourceId === sourceId);
        const desiredName = entry?.dummy?.name;

        this.fileController.copyProjectFile(newParentId ?? ' ', projectId, sourceId, desiredName).subscribe({
          next: (response: ProjectFile) => {
            // komplette Dummy-Struktur entfernen
            removeCopyBatchDummies();

            if (response) {
              // Wichtig: bei Ordnern alle Children als flache Einträge in originalData ablegen,
              // sonst wirkt der neue Ordner leer bis zum Reload.
              const nodes = flattenResponseTree(response);

              for (const node of nodes) {
                if (!node) continue;

                // Dedupe über id
                if (node.id && this.originalData.some(pf => pf.id === node.id)) {
                  continue;
                }

                this.addOrRemoveFileToOrFromOriginalData(node);
                if (node.parentId) {
                  this.setOrRemoveParentInOriginalData(node, node.parentId, false);
                }
              }

              // Tree für Dialoge/Selektion aktualisieren
              this.rebuildChildrenRelations();
            }

            this.updateCurrentFolderData();
            onOneFinished();
          },
          error: () => {
            failed++;

            removeCopyBatchDummies();
            this.rebuildChildrenRelations();
            this.updateCurrentFolderData();

            onOneFinished();
          },
        });
      }
    });
  }

  private toTimestamp(value: any): number {
    const time = value instanceof Date ? value.getTime() : new Date(value).getTime();
    return isNaN(time) ? 0 : time;
  }

  selectedRow(row: any): void {
    // Clear any existing timer
    if (this.clickTimer) {
      clearTimeout(this.clickTimer);
    }

    // Set up delayed execution for single click
    this.clickTimer = setTimeout(() => {
      if (this.selectionFields) {
        if (this.selectedFiles.map(file => file.id).includes(row.id)) {
          this.removeElementFromSelectedFiles(row)
        } else {
          this.addElementToSelectedFiles(row);
        }
        this.selectedFilesChange.emit(this.selectedFiles);
      }

      // Automatically manage detailed info visibility based on selection
      this.clickTimer = null;
    }, this.clickDelay);
  }

  /**
   * Checkbox-Klick: sofort und ohne Doppelklick-Verzögerung übernehmen. Der Zielzustand kommt
   * direkt aus dem Event, damit schnelle Klicks auf mehrere Zeilen sich nicht gegenseitig
   * überschreiben (der Zeilen-Timer in selectedRow verwirft vorherige Klicks).
   */
  onRowCheckboxClick(element: ProjectFile, event: Event): void {
    event.stopPropagation();
    if (this.clickTimer) {
      clearTimeout(this.clickTimer);
      this.clickTimer = null;
    }
    const input = event.target as HTMLInputElement | null;
    const shouldSelect = input ? input.checked : !this.isElementInSelectedFiles(element);
    if (this.selectionFields) {
      if (shouldSelect) {
        this.addElementToSelectedFiles(element);
      } else {
        this.removeElementFromSelectedFiles(element);
      }
      this.selectedFilesChange.emit(this.selectedFiles);
    }
    if (input) {
      // DOM-Zustand an die tatsächliche Auswahl angleichen (z. B. wenn ein Filter die Auswahl verhindert)
      input.checked = this.isElementInSelectedFiles(element);
    }
  }

  private addElementToSelectedFiles(element: ProjectFile): void {
    if (this.selectedFiles.map(file => file.id).includes(element.id)) {
      return;
    }

    const hasAnyFilterOrSearch = isSearchOrFilterActive(this.searchTerm, this.columnFilters);

    // Helper: nimmt ein ProjectFile und sagt, ob es unter aktuellen Filtern + Suche "matcht"
    const matchesCurrentFilterAndSearch = (pf: ProjectFile): boolean => {
      // Laufende Uploads lassen sich weiterhin auswaehlen - dieselbe Ausnahme wie in der Tabelle
      // (isRunningUpload), sonst zaehlte hiddenSelectedFileCount() Gewaehltes als ausgeblendet.
      if (this.isRunningUpload(pf)) {
        return true;
      }

      return (
        this.applySearchToDataset(
          this.applyFiltersToDataset([pf]),
          this.searchTerm
        ).length === 1
      );
    };

    if (element.isFolder) {
      // Ordner selbst immer selektieren (damit UI/Move-Dialog etc. ihn als selektiert sieht)
      this.selectedFiles.push(element);


      // Wenn kein Filter/Suche aktiv ist: altes Verhalten (alles darunter)
      if (!hasAnyFilterOrSearch) {
        if (element.children) {
          for (const child of element.children || []) {
            this.addElementToSelectedFiles(child);
          }
        }
        return;
      }

      // Filter/Suche aktiv: selektiere nur die Nachkommen, die wirklich matchen.
      // Wir traversieren ber originalData, nicht ber element.children (kann stale sein).
      const folderId = element.id;
      if (!folderId) {
        // Dummy folder ohne id -> fallback: children falls vorhanden
        if (element.children) {
          for (const child of element.children || []) {
            if (!child.isFolder && matchesCurrentFilterAndSearch(child)) {
              this.addElementToSelectedFiles(child);
            }
            if (child.isFolder) {
              this.addElementToSelectedFiles(child);
            }
          }
        }
        return;
      }

      const stack: ProjectFile[] = this.originalData.filter(pf => pf.parentId === folderId);
      while (stack.length > 0) {
        const node = stack.pop() as ProjectFile;

        if (node.isFolder) {
          // Ordner nur dann selektieren, wenn er (a) selbst matcht oder (b) matchende Nachkommen hat.
          // Da wir eh komplett traversieren, selektieren wir ihn, sobald wir ihn sehen.
          this.selectedFiles.push(node);
          stack.push(...this.originalData.filter(pf => pf.parentId === node.id));
        } else {
          if (matchesCurrentFilterAndSearch(node)) {
            this.selectedFiles.push(node);
          }
        }
      }

      // Duplikate entfernen (kann bei verschachtelten Ordnern auftreten)
      const seen = new Set<string>();
      this.selectedFiles = this.selectedFiles.filter(pf => {
        const key = pf.id ?? `${pf.parentId ?? ''}::${pf.name ?? ''}::${pf.size ?? 0}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });

      return;
    }

    // Datei: nur selektieren, wenn sie im Filter/Suche sichtbar sein drfte
    if (hasAnyFilterOrSearch && !matchesCurrentFilterAndSearch(element)) {
      return;
    }

    this.selectedFiles.push(element);

    if (!element.isFolder && !this.selectedFiles.map(file => file.id).includes(element.parentId)) {
      // If it's a file, ensure its parent folder is also selected
      const parentFolder = this.originalData.find(file => file.id === element.parentId);
      if (parentFolder) {
        // check if all children are in selectedFiles
        const allChildrenSelected = parentFolder.children?.every(child => this.selectedFiles.map(file => file.id).includes(child.id));
        if (allChildrenSelected && this.calledFromCheck) {
          this.selectedFiles.push(parentFolder);
        }
      }
    }
  }

  private removeElementFromSelectedFiles(element: ProjectFile): void {
    if (this.selectedFiles.map(file => file.id).includes(element.id)) {
      if (element.isFolder && element.children) {
        // If it's a folder, remove all its children as well
        for (const child of element.children || []) {
          this.removeElementFromSelectedFiles(child)
        }
      }
      if (!element.isFolder && element.parentId && this.selectedFiles.map(file => file.id).includes(element.parentId)) {
        // If it's a file, check if its parent folder should also be deselected
        const parentFolder = this.originalData.find(file => file.id === element.parentId);
        if (parentFolder) {
          const allChildrenSelected = parentFolder.children?.every(child => this.selectedFiles.map(file => file.id).includes(child.id));
          if (allChildrenSelected) {
            this.selectedFiles = this.selectedFiles.filter((item) => item.id !== parentFolder.id);
          }
        }
      }
      this.selectedFiles = this.selectedFiles.filter((item) => item.id !== element.id);
    }
  }

  /**
   * Baut den Folder-Tree (children) aus originalData neu auf.
   * Wichtig nach Create/Rename/Move/Copy, damit Popups (CopyMoveComponent) nicht mit stale children arbeiten.
   */
  private rebuildChildrenRelations(): void {
    // children sauber zurücksetzen
    for (const pf of this.originalData) {
      if (pf && pf.isFolder) {
        pf.children = [];
      }
    }

    const byId = new Map<string, ProjectFile>();
    for (const pf of this.originalData) {
      if (pf?.id) byId.set(pf.id, pf);
    }

    for (const pf of this.originalData) {
      const parentId = pf?.parentId ?? null;
      if (!parentId) continue;

      const parent = byId.get(parentId);
      if (parent && parent.isFolder) {
        parent.children = parent.children ?? [];
        // Doppelte vermeiden
        if (!parent.children.some(c => c.id != null && c.id === pf.id) && !(!pf.id && parent.children.some(c => !c.id && c.name === pf.name))) {
          parent.children.push(pf);
        }
      }
    }
  }

  /** Liefert eine frische Kopie des aktuellen Trees für Dialoge. */
  private getDialogTreeData(): ProjectFile[] {
    this.rebuildChildrenRelations();
    return this.originalData;
  }

  addFolder(): void {
    if (!this.permissionService.hasProjectPrivilege('PROJECT_FILE_EDIT')) {
      this.notificationsService.showErrorMessage('Sie haben nicht die Berechtigung Ordner zu erstellen.');
      return;
    }
    const dialogRef = this.dialog.open(NewFolderComponent, {
      hasBackdrop: false,
      data: {
        otherNames: this.currentFolderData.filter(item => item.isFolder).map(item => item.name),
      },
    });

    dialogRef.afterClosed().subscribe((result: any) => {
      if (result == undefined) return;

      const parentId = this.currentParent ? this.currentParent.id ?? '' : undefined;
      const existingFolderNames = this.buildExistingNameSet(this.getEntriesInTargetFolder(parentId), true);
      const uniqueFolderName = this.makeNameUniqueWindowsStyle(result.folderName, existingFolderNames, true);

      const tempFolder: ProjectFile = {
        name: uniqueFolderName,
        fileType: ProjectFile.FileTypeEnum.FOLDER,
        isFolder: true,
        size: 0,
        lastModified: new Date(),
        description: result.folderDescription,
        parentId,
      };

      // UI: temporären Folder sofort anzeigen
      this.filteredData = [tempFolder, ...this.filteredData];
      this.addOrRemoveFileToOrFromOriginalData(tempFolder);
      this.rebuildChildrenRelations();

      this.fileController
        .createFolder(
          tempFolder,
          this.currentParent ? this.currentParent.id ?? '' : '',
          this.stateConfigService.getProjectId()
        )
        .subscribe({
          next: (response: ProjectFile) => {
            if (!response) return;

            // temporären Eintrag in filteredData ersetzen
            const fdIndex = this.filteredData.findIndex((pf) => pf.name === tempFolder.name && !pf.id);
            if (fdIndex !== -1) {
              this.filteredData.splice(fdIndex, 1, response);
              this.filteredData = [...this.filteredData]; // Change Detection
            }

            // temporären Eintrag in originalData ersetzen
            const odIndex = this.originalData.findIndex((pf) => pf.name === tempFolder.name && !pf.id);
            if (odIndex !== -1) {
              // Dummy raus
              const dummy = this.originalData[odIndex];
              this.addOrRemoveFileToOrFromOriginalData(dummy, true);

              // Real rein
              this.addOrRemoveFileToOrFromOriginalData(response);
            } else {
              this.addOrRemoveFileToOrFromOriginalData(response);
            }

            if (response.parentId) {
              this.setOrRemoveParentInOriginalData(response, response.parentId);
            }

            this.rebuildChildrenRelations();
            // aktuellen Folder-View synchron halten
            this.updateCurrentFolderData();
          },
          error: () => {
            // temporären Folder wieder entfernen
            this.filteredData = this.filteredData.filter((pf) => !(pf.name === tempFolder.name && !pf.id));
            this.filteredData = [...this.filteredData];

            this.originalData = this.originalData.filter((pf) => !(pf.name === tempFolder.name && !pf.id));

            this.rebuildChildrenRelations();
            this.updateCurrentFolderData();
            window.alert('Folder could not be created. Please try again.');
          },
        });
    });
  }

  /**
   * Berechnet den Upload-Fortschritt eines Ordners (0-100), basierend auf allen Datei-Nachkommen.
   *
   * Wichtig: Es werden NUR Dateien berücksichtigt, die zum aktuellen Upload-Vorgang gehören
   * (= UPLOADING/PROCESSING oder (Fallback) ein Eintrag in `uploadingDocuments`).
   * Bereits fertige Dateien (READY/CHECKED) fallen aus der Rechnung raus.
   */
  getFolderUploadProgress(folder: ProjectFile): number {
    if (!folder?.isFolder) return 0;

    const cacheKey = folder.id ?? '';
    const cached = this.folderUploadProgressCache.get(cacheKey);
    if (cached !== undefined) return cached;

    const progress = this.computeFolderUploadProgress(folder);
    this.folderUploadProgressCache.set(cacheKey, progress);
    return progress;
  }

  /**
   * trackBy der Dateitabelle.
   *
   * Ohne trackBy diffed die cdk-table ueber Objektidentitaet. Die Tabelle bekommt aber an vielen
   * Stellen ein NEUES Array (`filteredData = [...filteredData]`), unter anderem bei jedem
   * Upload-Fortschritt \- ohne stabilen Schluessel baut sie dann alle Zeilen samt Zellen neu auf.
   * Ueber die id bleiben die Zeilen erhalten.
   *
   * Dummy-Zeilen frisch hochgeladener Dateien haben noch keine id. Fuer sie bleibt es bewusst bei der
   * Objektidentitaet und NICHT bei einem Schluessel aus name+size+parentId: zwei gleichnamige,
   * gleich grosse Dateien aus verschiedenen Quellordnern landen im selben Zielordner und haetten
   * damit denselben Schluessel \- doppelte Schluessel bringen den IterableDiffer aus dem Tritt.
   */
  trackRow = (_index: number, row: ProjectFile): unknown => row.id ?? row;

  /** Stößt die KI-Klassifizierung der Dokumentenklasse für eine einzelne Datei an. */
  reclassifyDocumentClass(file: ProjectFile): void {
    if (!this.permissionService.hasProjectPrivilege('PROJECT_FILE_EDIT')) {
      this.bicToastService.error('Sie haben nicht die Berechtigung Dateien zu klassifizieren.');
      return;
    }
    if (!file.id || file.isFolder) {
      return;
    }
    // s. reclassifyFile(): waehrend Upload oder Analyse gehoert die Datei noch der laufenden
    // Verarbeitung. Das Menue bietet den Punkt ohnehin nur bei READY an - der Riegel steht hier
    // fuer alle anderen Aufrufwege.
    if (this.isBusyWithUploadOrAnalysis(file)) {
      this.bicToastService.error('Diese Datei wird gerade verarbeitet. Bitte warten.');
      return;
    }
    this.documentClassService
      .reclassifyFile(this.stateConfigService.getProjectId(), file.id)
      .subscribe({
        next: () => {
          // Sofort PROCESSING setzen damit der User Feedback bekommt (analog reclassifyFile/IFC-Pfad),
          // statt erst auf das (u.U. verpasste) SSE-Event oder einen manuellen Reload zu warten.
          this.markFileProcessingLocally(file.id!);
          this.bicToastService.success('Dokumentenklasse wird neu zugewiesen.');
        },
        error: (error: unknown) => {
          if ((error as { status?: number })?.status === 409) {
            this.bicToastService.error('Datei wird bereits klassifiziert.');
            return;
          }
          this.bicToastService.error('Dokumentenklasse konnte nicht zugewiesen werden.');
        },
      });
  }

  /** Zählt die Ordner, die für eine Ordner-Auswahl angelegt werden (inkl. aller Zwischenebenen). */
  private countFolderPathsInSelection(files: File[]): number {
    const folderPaths = new Set<string>();
    for (const file of files) {
      const relativePath = ((file as any).webkitRelativePath as string | undefined) ?? '';
      const parts = relativePath.replace(/\\/g, '/').split('/');
      let pathBuilder = '';
      for (let i = 0; i < parts.length - 1; i++) {
        pathBuilder = pathBuilder ? pathBuilder + '/' + parts[i] : parts[i];
        folderPaths.add(pathBuilder);
      }
    }
    return folderPaths.size;
  }

  /**
   * Papierkorb öffnen.
   *
   * Nötig geworden, weil Löschen seit 2026-09-07 nur noch vormerkt: die Datei ist sofort aus der
   * Liste, liegt aber sieben Tage im Papierkorb und lässt sich von dort zurückholen. Ohne diesen
   * Zugang wäre der Papierkorb ein Datenbankzustand, den niemand sehen kann.
   */
  protected openTrash(): void {
    const projectId = this.stateConfigService.getProjectId();
    if (!projectId) return;

    const ref = this.dialog.open(TrashComponent, {hasBackdrop: true, data: {projectId}});
    ref.afterClosed().subscribe(changed => {
      // Nur nachladen, wenn wirklich etwas passiert ist: die Dateiliste neu zu holen ist teuer.
      if (changed) this.loadFiles();
    });
  }

  /** Fortschritts-Popup für eine Dateiauswahl öffnen (blockiert, bis alle Dateien durch sind). */
  private openUploadProgressDialog(batchId: string, expectedFileCount: number, folderCount = 0, initialJobs: UploadJob[] = []): void {
    // Kann passieren, wenn der Batch schon fehlgeschlagen ist, während noch der Ablehnungs-Hinweis offen war.
    if (this.abortedUploadBatchIds.has(batchId)) return;

    const data: UploadProgressData = {batchId, expectedFileCount, folderCount, initialJobs};
    const ref = this.dialog.open(UploadProgressComponent, {hasBackdrop: true, data});
    this.uploadProgressDialogRef = ref;
    ref.afterClosed().subscribe(result => {
      if (this.uploadProgressDialogRef === ref) {
        this.uploadProgressDialogRef = undefined;
      }

      // Abbruch während der Ordner-Anlage: cancelBatch im Service greift ins Leere, weil es noch
      // keine Jobs gibt \- den laufenden Request und die Dummy-Zeilen müssen wir hier abräumen.
      if (result === 'cancelled') {
        const cleanup = this.pendingFolderStructureByBatchId.get(batchId);
        this.pendingFolderStructureByBatchId.delete(batchId);
        this.abortedUploadBatchIds.add(batchId);
        cleanup?.();
      }
    });
  }

  private closeUploadProgressDialog(batchId: string): void {
    this.abortedUploadBatchIds.add(batchId);
    this.uploadProgressDialogRef?.close();
    this.uploadProgressDialogRef = undefined;
  }

  // ── Drag & Drop: einziger Weg, um wirklich MEHRERE Ordner in einer Aktion hochzuladen ──
  // Der native Ordner-Dialog (<input webkitdirectory>) lässt in jedem Browser nur EINEN Ordner pro
  // Öffnen wählen - das `multiple`-Attribut ändert daran nichts (gilt dort nur für Mehrfachauswahl
  // einzelner Dateien). Beim Ziehen mehrerer Ordner auf die Ablagefläche liefert `DataTransferItemList`
  // dagegen mehrere Top-Level-Einträge in einem einzigen Drop-Event.

  isDragOver = false;
  protected readonly faSquareDashedCirclePlus = faSquareDashedCirclePlus;

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    this.isDragOver = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    this.isDragOver = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    this.isDragOver = false;

    const items = event.dataTransfer?.items;
    if (!items || items.length === 0) return;

    const entries: FileSystemEntry[] = [];
    for (let i = 0; i < items.length; i++) {
      const entry = (items[i] as unknown as { webkitGetAsEntry?(): FileSystemEntry | null }).webkitGetAsEntry?.();
      if (entry) entries.push(entry);
    }
    if (entries.length === 0) return;

    const collected: { file: File; relativePath: string }[] = [];
    Promise.all(entries.map((entry) => this.readEntryRecursively(entry, '', collected)))
      .then(() => this.dispatchDroppedFiles(collected))
      .catch((error) => console.error('Fehler beim Verarbeiten der abgelegten Dateien/Ordner:', error));
  }

  /** Liest einen FileSystemEntry (Datei oder Ordner) rekursiv ein und sammelt {file, relativePath}. */
  private readEntryRecursively(
    entry: FileSystemEntry,
    basePath: string,
    out: { file: File; relativePath: string }[],
  ): Promise<void> {
    if (entry.isFile) {
      return new Promise((resolve, reject) => {
        (entry as FileSystemFileEntry).file((file) => {
          out.push({file, relativePath: basePath + file.name});
          resolve();
        }, reject);
      });
    }
    if (entry.isDirectory) {
      const dirPath = basePath + entry.name + '/';
      return this.readAllDirectoryEntries(entry as FileSystemDirectoryEntry).then((children) =>
        Promise.all(children.map((child) => this.readEntryRecursively(child, dirPath, out))).then(() => undefined),
      );
    }
    return Promise.resolve();
  }

  /** readEntries() liefert laut Spec ggf. nur einen Batch pro Aufruf - so lange lesen, bis leer. */
  private readAllDirectoryEntries(dirEntry: FileSystemDirectoryEntry): Promise<FileSystemEntry[]> {
    const reader = dirEntry.createReader();
    const allEntries: FileSystemEntry[] = [];
    const readBatch = (): Promise<FileSystemEntry[]> =>
      new Promise<FileSystemEntry[]>((resolve, reject) => {
        reader.readEntries((batch) => resolve(batch), reject);
      }).then((batch) => {
        if (batch.length === 0) return allEntries;
        allEntries.push(...batch);
        return readBatch();
      });
    return readBatch();
  }

  /**
   * Trennt abgelegte Dateien danach, ob sie aus einem Ordner stammen (relativePath enthält "/") oder
   * lose gedroppt wurden, und schickt beide Gruppen durch die bestehende, bereits getestete
   * openUploadSelection()-Logik (Typ-/Größenprüfung, Popup, Upload) - über ein synthetisches Event,
   * damit hier keine Logik dupliziert wird.
   */
  private dispatchDroppedFiles(collected: { file: File; relativePath: string }[]): void {
    const folderFiles = collected
      .filter((c) => c.relativePath.includes('/'))
      .map((c) => {
        Object.defineProperty(c.file, 'webkitRelativePath', {value: c.relativePath, configurable: true});
        return c.file;
      });
    const looseFiles = collected.filter((c) => !c.relativePath.includes('/')).map((c) => c.file);

    if (folderFiles.length > 0) this.openUploadSelection(this.toSyntheticFileInputEvent(folderFiles), true);
    if (looseFiles.length > 0) this.openUploadSelection(this.toSyntheticFileInputEvent(looseFiles), false);
  }

  private setOrRemoveParentInOriginalData(child: ProjectFile, parentId: string | null, remove: boolean = false): void {
    const parentIndex = this.originalData.findIndex(pf => pf.id === parentId);
    if (parentIndex !== -1) {
      let parentChilds = this.originalData[parentIndex].children || [];
      // find the child in childs
      const childIndex = parentChilds.findIndex(pf => {
        if (pf.id && child.id) {
          return pf.id === child.id
        } else {
          return pf.name === child.name && pf.size === child.size;
        }
      });

      if (childIndex === -1) {
        if (remove) {
          return
        } else {
          parentChilds.push(child);
        }
      } else {
        if (remove) {
          parentChilds.splice(childIndex, 1);
        } else {
          parentChilds[childIndex] = child;
        }
      }

      this.originalData[parentIndex].children = parentChilds;

      // Größe IMMER konsistent neu berechnen (verhindert temporäre Doppelzählungen bei Dummies/Kopien)
      this.originalData[parentIndex]!.size! = parentChilds.reduce((acc: number, pf: ProjectFile) => acc + (pf.size ?? 0), 0);

      // recursively update the parent's representation in its grandparent's children list.
      // Always use remove=false here: we are UPDATING (replacing) the parent entry in the grandparent,
      // never removing it. Passing `remove` here would incorrectly delete the parent folder from its
      // own ancestor when a child is being removed, causing entire folder subtrees to disappear.
      for (let grandParent of this.originalData) {
        if (grandParent.children && grandParent.children.findIndex(pf => pf.id === parentId) !== -1) {
          this.setOrRemoveParentInOriginalData(this.originalData[parentIndex], grandParent.id ?? null, false);
        }
      }
    }
  }

  loadFiles() {
    if (this.customFilesActivated) {
      this.originalData = this.customFiles;
      // Auch hier den Tree lokal aufbauen: die Aufrufer liefern die Dateien inzwischen aus der
      // Tabellenprojektion und damit ohne `children` (z.B. ifc-viewer.component).
      this.rebuildChildrenRelations();
      this.updateCurrentFolderData();
      let temp = this.selectedFiles
      this.selectedFiles = [];
      for (let file of temp) {
        this.selectedFiles.push(file)
      }
      this.loading = false;
    } else {
      const projectId = this.stateConfigService.getProjectId();
      // Vor dem Request merken, nicht erst in der Antwort: sonst wuerde jedes NavigationEnd, das
      // waehrend des Ladens eintrifft, einen weiteren Request ausloesen.
      this.loadedProjectId = projectId;
      this.loadSourceLinks(projectId);
      this.fileTableService.getTableRows(projectId).subscribe({
        next: (files) => {
          this.originalData = files;
          // Der Server hat gerade den maßgeblichen Stand geliefert - die Löschvormerkungen sind
          // damit erledigt (egal ob bestätigt oder zurückgerollt).
          this.deletedFileIds.clear();
          // Der schlanke Endpoint liefert kein `children`. Die Navigation und die Filter arbeiten
          // ohnehin ueber `parentId` (updateCurrentFolderData / buildChildrenMap), aber die
          // Ordner-Selektion und die Copy/Move-Rekursion lesen `children` direkt -- also hier einmal
          // lokal aus `parentId` aufbauen. Das ist zugleich verlaesslicher als der Serverbaum, der
          // nach Create/Rename/Move stale sein konnte.
          this.rebuildChildrenRelations();
          this.updateCurrentFolderData();
          let temp = this.selectedFiles
          this.selectedFiles = [];
          for (let file of temp) {
            this.selectedFiles.push(file)
          }
          this.loading = false;
          this.ensureClassificationFallbackPoll();
          // Laufende Loeschaktionen UND Klassifizierungen vom Server aufgreifen: nach einem
          // Reload weiss nur er noch, dass da etwas offen ist. Bei der Klassifizierung ist das der
          // einzige Weg - deren Datei-IDs sind mit dem Tab verloren.
          this.deleteService.resumeOpenBatches(projectId);
          this.classificationService.resumeOpenBatches(projectId);
        },
        error: () => {
          this.loading = false;
          this.notificationsService.showErrorMessage('Dateien konnten nicht geladen werden.');
        }
      });
    }

  }

  deleteFile(file?: ProjectFile): void {
    if(!this.permissionService.hasProjectPrivilege('PROJECT_FILE_DELETE')){
      this.notificationsService.showErrorMessage('Sie haben nicht die Berechtigung Dateien zu löschen.');
      return;
    }
    // Kein Riegel mehr, sondern ein Abbruch: laufende Uploads in den geloeschten Teilbaum werden
    // gestoppt. Hier stand eine Sperre ("Solange Dateien hochgeladen werden, kann nicht geloescht
    // werden"), und sie war die falsche Antwort auf die richtige Sorge - ein abgebrochener Upload
    // hinterlaesst eine Zeile auf UPLOADING, die nie von selbst weitergeht, und die machte ihren
    // ganzen Ordnerbaum dauerhaft unloeschbar. Ohne Eingriff an der Datenbank kam man da nicht mehr
    // raus.
    //
    // Was der Nutzer erwartet, ist ohnehin das hier: wer den Ordner loescht, will nicht, dass der
    // Upload dorthin weiterlaeuft. cancelJob() raeumt dabei auch den halb angelegten Datei-Record
    // im Backend weg, es bleibt also keine Karteileiche stehen.
    this.cancelUploadsForDeletion(file);

    // Nur sichtbare Auswahl (#2770). Bei aktiver Suche/Filter weist der Dialog darauf hin, dass ein
    // Ordner trotzdem samt ausgeblendetem Inhalt geloescht wird - auch aus dem Kontextmenue.
    const targets = file != undefined ? [file] : [...this.visibleSelectedFiles()];
    const dialogRef = this.dialog.open(DeleteFileComponent, {
      hasBackdrop: false,
      data: {files: topLevelSelection(targets, this.originalData), searchOrFilterActive: isSearchOrFilterActive(this.searchTerm, this.columnFilters)},
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result == undefined) return;

      this.startDelete(targets);
    });
  }

  stringWithoutSpaces(input: string): string {
    return input.replace(/\s+/g, '');
  }

  lengthOfOriginalFilesWithoutFolder() {
    return this.originalData.filter(file => !file.isFolder).length;
  }

  /** True, wenn die Zeile als haengengebliebene Klassifizierung gilt (nur Dateien, keine Ordner). */
  protected isClassificationStalled(element: ProjectFile): boolean {
    return !element?.isFolder && !!element?.id && this.stalledClassificationIds.has(element.id);
  }

  numberOfSelectedFilesWithoutFolder() {
    return this.visibleSelectedFiles().filter(file => !file.isFolder).length;
  }

  /**
   * Der Teil der Auswahl, auf den Zaehler, Toolbar, Infopanel, chatBIC und alle Toolbar-Aktionen
   * wirken (#2770). Aktionen auf die Auswahl laufen IMMER ueber diese Methode (bzw. {@link targetsFor}),
   * nie direkt ueber `selectedFiles` - sonst treffen sie wieder Dateien, die der Nutzer nicht sieht.
   *
   * <p>Die Auswahl selbst wird bei Suche/Filter nicht gekuerzt: nach dem Leeren der Suche ist sie
   * wieder vollstaendig da, und im Check-Modus geht sie unveraendert an den Aufrufer. Ausgeblendete
   * Eintraege zeigt {@link hiddenSelectedFileCount} an.</p>
   *
   * <p>Ohne Suche/Filter ist alles sichtbar, auch der Unterbaum eines angehakten Ordners, der nicht
   * in der aktuellen Ebene steht - dann kommt `selectedFiles` selbst zurueck (gleiche Referenz).
   * Mit Suche/Filter gilt als sichtbar, was in `filteredData` steht (inkl. laufender Uploads aus
   * {@link applyColumnFilters}); weil `filteredData` nach Upload, SSE und Klassifizierung neu
   * zugewiesen wird, folgt die Sicht jeder Neuberechnung. Eintraege ohne `id` (Upload-Platzhalter)
   * zaehlen nur, wenn genau dieses Objekt in `filteredData` steht.</p>
   *
   * <p>Das Ergebnis ist pro Change-Detection-Zyklus gecacht und hat damit eine stabile Referenz
   * (AGENTS.md, "Template Bindings & Change Detection"; die Pending-Caches vergleichen die Referenz).</p>
   */
  visibleSelectedFiles(): ProjectFile[] {
    if (!isSearchOrFilterActive(this.searchTerm, this.columnFilters)) {
      return this.selectedFiles;
    }
    const selected = this.selectedFiles ?? [];
    const filtered = this.filteredData ?? [];
    const cache = this.visibleSelectionCache;
    if (cache && cache.selected === selected && cache.length === selected.length && cache.filtered === filtered) {
      return cache.result;
    }
    const ids = new Set(filtered.map(row => row.id).filter((id): id is string => !!id));
    const result = selected.filter(sf => sf.id ? ids.has(sf.id) : filtered.includes(sf));
    this.visibleSelectionCache = {selected, length: selected.length, filtered, result};
    return result;
  }

  /** Ausgewaehlte Dateien (ohne Ordner), die Suche/Filter gerade ausblenden - fuer den Hinweis im Kopf (#2770). */
  hiddenSelectedFileCount(): number {
    return (this.selectedFiles ?? []).filter(f => !f.isFolder).length
      - (this.visibleSelectedFiles() ?? []).filter(f => !f.isFolder).length;
  }

  folderHasMatchingFileToFileType(folder: ProjectFile) {
    if (!folder.children || !Array.isArray(folder.children)) {
      return false;
    }

    const stack: ProjectFile[] = [...folder.children];

    while (stack.length > 0) {
      const child = stack.pop() as ProjectFile;

      if (child.isFolder) {
        if (child.children && Array.isArray(child.children)) {
          stack.push(...child.children);
        }
      } else {
        return true;
      }
    }
    return false;
  }

  /**
   * Upload-Stand eines Ordners aus den Jobs des UploadService - oder {@code null}, wenn zu diesem
   * Ordner gerade keiner laeuft.
   *
   * <p>Der Job traegt in {@code parentId} den Ordner, in den seine Datei gehoert. Gezaehlt werden
   * alle Jobs, deren Ziel irgendwo im Teilbaum liegt. Die Ordner-IDs des Teilbaums stehen dafuer
   * zuverlaessig zur Verfuegung, auch direkt nach einer Navigation: {@code createFolderStructure}
   * legt die Ordner VOR den Dateien an, sie sind also serverseitig vorhanden und geladen - nur die
   * Dateizeilen fehlen noch, und genau die liefern hier die Jobs.</p>
   */
  private uploadCountsFromJobs(folder: ProjectFile): {done: number, total: number} | null {
    const projectId = this.stateConfigService.getProjectId();
    if (!projectId) return null;

    const folderIds = new Set<string>();
    if (folder.id) folderIds.add(folder.id);
    for (const descendant of this.collectAllFileDescendants(folder)) {
      if (descendant.isFolder && descendant.id) folderIds.add(descendant.id);
    }
    // Auch Ordner, deren Kinder die Ansicht noch nicht kennt: ueber die parentId-Kette in originalData.
    for (const row of this.originalData ?? []) {
      if (row.isFolder && row.id && row.parentId && folderIds.has(row.parentId)) {
        folderIds.add(row.id);
      }
    }

    const jobs = this.uploadService.getJobsForProject(projectId)
      .filter(job => !!job.parentId && folderIds.has(job.parentId));
    if (jobs.length === 0) return null;

    let done = 0;
    for (const job of jobs) {
      if (job.state === 'done') {
        done++;
      } else if (job.state !== 'error') {
        done += Math.min(100, Math.max(0, job.progress ?? 0)) / 100;
      }
    }
    // Abgebrochene und fehlgeschlagene Jobs zaehlen nicht mit - sie werden nie fertig und haetten
    // den Ring dauerhaft unter 100 % gehalten.
    const total = jobs.filter(job => job.state !== 'error').length;
    return total === 0 ? null : {done, total};
  }

  /**
   * Fortschritt eines Ordners: Anteil der Dateien, deren Bytes durch sind.
   *
   * Bewusst nach ANZAHL und nicht byte-gewichtet. Die byte-gewichtete Variante war arithmetisch
   * richtig und trotzdem unbrauchbar: direkt daneben zaehlt das Klassifizierungs-Banner Dateien,
   * und bei einem Ordner mit wenigen grossen Modellen neben hunderten kleinen LVs sind 71 % der
   * Dateien eben 18 % der Bytes. Zwei Zahlen zur selben Sache, die sich widersprechen.
   *
   * Eine gerade laufende Datei geht mit ihrem eigenen Prozentwert ein. Damit verhaelt sich der
   * Ring bei einem Ordner mit einer einzigen grossen Datei weiter wie eine Byte-Anzeige und bei
   * vielen Dateien wie ein Zaehler - beides ist dort jeweils das Erwartete.
   *
   * Der Nenner waechst, solange der Upload noch Zeilen anlegt (die Gesamtzahl steht erst fest, wenn
   * die Ordnerstruktur aufgeloest ist). Der Wert kann dadurch kurz zurueckgehen; nach Dateien
   * gezaehlt ist das aber ein kleiner Effekt und nicht mehr der Sprung von 8 % auf 3 %.
   */
  private computeFolderUploadProgress(folder: ProjectFile): number {
    const counts = this.folderUploadCounts(folder);
    if (counts.total === 0) return 0;
    return Math.round(Math.min(100, Math.max(0, (counts.done / counts.total) * 100)));
  }

  /**
   * Wie viele Dateien eines Ordners hochgeladen sind und wie viele dazugehoeren.
   *
   * Bezugsmenge ist alles, was zu diesem Upload gehoert - laufend, hochgeladen, in Analyse oder
   * fertig. Nur ERROR faellt raus: eine gescheiterte Datei wird nie fertig und wuerde den Wert
   * dauerhaft unter 100 % halten.
   */
  private folderUploadCounts(folder: ProjectFile): {done: number, total: number} {
    const cacheKey = folder.id ?? '';
    const cached = this.folderUploadCountsCache.get(cacheKey);
    if (cached) return cached;

    // Erst die Upload-Jobs fragen, denn nur die kennen die WAHRE Gesamtzahl.
    //
    // Die Zeilen der noch nicht hochgeladenen Dateien existieren nur lokal als Dummy. Wer waehrend
    // des Uploads wegnavigiert, verliert sie: loadFiles() holt den Serverstand, und dort gibt es
    // die Datei noch nicht. Der Ordner zaehlte danach "120 von 130" statt "120 von 381", und mit
    // jedem angelegten Block wanderte der Nenner mit ("135 von 140"). Die Jobs liegen dagegen im
    // UploadService (providedIn: 'root') und ueberleben jede Navigation.
    const fromJobs = this.uploadCountsFromJobs(folder);
    if (fromJobs) {
      this.folderUploadCountsCache.set(cacheKey, fromJobs);
      return fromJobs;
    }

    let done = 0;
    let total = 0;
    for (const file of this.collectAllFileDescendants(folder)) {
      const status = this.effectiveStatus(file);
      const trackedProgress = this.uploadingDocuments.get(this.uploadProgressKey(file));

      if (status === ProjectFile.FileStatusEnum.PROCESSING
        || status === ProjectFile.FileStatusEnum.READY
        || status === ProjectFile.FileStatusEnum.CHECKED) {
        // Bytes sind durch - egal, was danach noch laeuft.
        done++;
        total++;
      } else if (status === ProjectFile.FileStatusEnum.UPLOADING || trackedProgress !== undefined) {
        done += Math.min(100, Math.max(0, trackedProgress ?? 0)) / 100;
        total++;
      }
    }

    const counts = {done, total};
    this.folderUploadCountsCache.set(cacheKey, counts);
    return counts;
  }

  /**
   * @param deferRefresh Laesst updateCurrentFolderData() weg. Fuer Schleifen, die viele Zeilen auf
   *   einmal einfuegen und den Refresh danach selbst EINMAL machen: der Refresh scannt originalData
   *   (das ganze Projekt) und rechnet alle Spaltenfilter neu, pro Datei aufgerufen ist das bei einem
   *   Upload von N Dateien O(N^2) \- und zwar noch bevor das erste Byte rausgeht.
   */
  private addOrRemoveFileToOrFromOriginalData(newFile: ProjectFile, remove: boolean = false,
                                              deferRefresh: boolean = false): void {
    if (remove) {
      // Referenz-Vergleich ist hier load-bearing: Dummy-Zeilen frisch hochgeladener Dateien haben
      // noch keine id und lassen sich nur über die Objekt-Identität treffen. Für Zeilen MIT id
      // ist removeFilesLocally() der richtige Weg (siehe dort).
      this.originalData = this.originalData.filter(pf => pf !== newFile);
      this.filteredData = this.filteredData.filter(pf => pf !== newFile);
    } else {
      this.originalData.push(newFile);
    }

    // keep currentFolderData in sync so filters/search include the new item
    if (!deferRefresh) {
      this.updateCurrentFolderData();
    }
  }

  updateCurrentFolderData(): void {
    // Update currentFolderData based on current folder navigation
    this.currentFolderData = this.currentParent
      ? this.originalData.filter(file => file.parentId === this.currentParent?.id)
      : this.originalData.filter(file => file.parentId === null || file.parentId === undefined);
    this.updateCurrentLink();
    this.applyColumnFilters();
  }

  // --- Verknuepfte Ordner (BIC-Frontend#2720) ---

  /** Grund, warum hier nichts hochgeladen oder angelegt werden darf; null = erlaubt. */
  get writeBlockedReason(): string | null {
    if (!this.permissionService.hasProjectPrivilege('PROJECT_FILE_EDIT')) {
      return 'Keine Berechtigung';
    }
    return this.currentParent?.sourceLinkId ? this.mirroredHint : null;
  }

  selectionHasMirrored(): boolean {
    return this.visibleSelectedFiles().some(file => !!file.sourceLinkId);
  }

  get syncLabel(): string {
    const link = this.currentLink;
    if (!link) {
      return '';
    }
    if (this.currentLinkSyncing) {
      const progress = this.sourceLinkSync.state(link.id)?.progress;
      return progress && progress.phase !== 'LISTING' && progress.filesTotal > 0
        ? `Wird synchronisiert · ${progress.filesDone}/${progress.filesTotal}`
        : 'Wird synchronisiert …';
    }
    if (link.status === SourceLinkView.StatusEnum.ERROR) {
      return 'Sync-Fehler';
    }
    if (link.status === SourceLinkView.StatusEnum.PAUSED) {
      return 'Sync pausiert';
    }
    return 'Synchronisiert · ' + this.relativeTime(link.lastSyncAt);
  }

  /** Läuft für den aktuellen Ordner ein Abgleich? Laut Link oder laut den Meldungen dieses Tabs. */
  get currentLinkSyncing(): boolean {
    const link = this.currentLink;
    if (!link) {
      return false;
    }
    const state = this.sourceLinkSync.state(link.id);
    return link.syncing || link.status === SourceLinkView.StatusEnum.INITIALSYNC || (!!state && !state.finished);
  }

  /** Fortschritts-Popup des Abgleichs, im Aufbau wie das Upload-Popup. */
  openSyncProgress(link: SourceLinkView | undefined = this.currentLink): void {
    if (!link?.id) {
      return;
    }
    this.dialog.open(SourceLinkSyncProgressDialogComponent, {hasBackdrop: true, data: {link}});
  }

  get syncTooltip(): string {
    const link = this.currentLink;
    if (!link) {
      return '';
    }
    const source = `${link.siteName ?? 'SharePoint'} › ${link.folderPath}`;
    const counts = `${link.fileCount} Dateien` + (link.skippedCount ? `, ${link.skippedCount} übersprungen` : '');
    return link.lastError ? `${source}\n${link.lastError}` : `${source}\n${counts}`;
  }

  linkSourceFolder(): void {
    if (this.writeBlockedReason) {
      this.notificationsService.showErrorMessage(this.writeBlockedReason);
      return;
    }
    const projectId = this.stateConfigService.getProjectId();
    this.dialog.open(LinkSourceFolderDialogComponent, {
      hasBackdrop: true,
      data: {
        projectId,
        projectName: this.stateConfigService.selectedProject?.name ?? 'Projekt',
        parentFolderId: this.currentParent?.id ?? null,
        parentFolderName: this.currentParent?.name ?? 'Projektwurzel'
      }
    }).afterClosed().subscribe((link?: SourceLinkView) => {
      if (link) {
        this.loadFiles();
        // Der erste Abgleich startet sofort; wie beim Upload gleich den Fortschritt zeigen.
        this.sourceLinkSync.started(link);
        this.openSyncProgress(link);
      }
    });
  }

  syncCurrentLink(): void {
    const link = this.currentLink;
    if (!link) {
      return;
    }
    this.sourceLinkService.syncSourceLink(this.stateConfigService.getProjectId(), link.id!).subscribe({
      next: updated => {
        this.sourceLinkSync.started(updated);
        this.setSourceLinks([...[...this.linkByTargetFolder.values()].filter(l => l.id !== updated.id), updated]);
        this.openSyncProgress(updated);
      },
      error: (err: HttpErrorResponse) =>
        this.notificationsService.showErrorMessage(err?.error?.message ?? 'Der Abgleich konnte nicht angestoßen werden.')
    });
  }

  unlinkCurrentLink(): void {
    const link = this.currentLink;
    if (!link) {
      return;
    }
    this.dialog.open(UnlinkSourceFolderDialogComponent, {
      hasBackdrop: true,
      data: {
        projectId: this.stateConfigService.getProjectId(),
        link,
        canDelete: this.permissionService.hasProjectPrivilege('PROJECT_FILE_DELETE')
      }
    }).afterClosed().subscribe((unlinked?: boolean) => {
      if (unlinked) {
        // Der Zielordner kann im Papierkorb liegen: zur Projektwurzel zurueck.
        this.navigateToSpecificFolder(undefined, 0);
        this.loadFiles();
      }
    });
  }

  openExternal(url: string | undefined): void {
    if (url) {
      window.open(url, '_blank', 'noopener');
    }
  }

  private loadSourceLinks(projectId: number): void {
    this.sourceLinkService.listSourceLinks(projectId).subscribe({
      next: links => this.setSourceLinks(links),
      // Ohne Links laeuft die Dateiansicht normal weiter; der Fehler ist trotzdem sichtbar.
      error: () => this.notificationsService.showErrorMessage('Verknüpfte Ordner konnten nicht geladen werden.')
    });
  }

  private setSourceLinks(links: SourceLinkView[]): void {
    links.forEach(link => this.sourceLinkSync.linkLoaded(link));
    this.linkByTargetFolder = new Map(links.map(link => [link.targetFolderId!, link]));
    this.updateCurrentLink();
  }

  /** Verknuepfung des aktuellen Ordners: der Ordner selbst oder ein Vorfahr ist Zielordner eines Links. */
  private updateCurrentLink(): void {
    const byId = new Map(this.originalData.map(file => [file.id, file]));
    let folder: ProjectFile | undefined = this.currentParent;
    const seen = new Set<string>();
    while (folder?.id && !seen.has(folder.id)) {
      const link = this.linkByTargetFolder.get(folder.id);
      if (link) {
        this.currentLink = link;
        return;
      }
      seen.add(folder.id);
      folder = folder.parentId ? byId.get(folder.parentId) : undefined;
    }
    this.currentLink = undefined;
  }

  private relativeTime(value: Date | string | undefined): string {
    if (!value) {
      return 'noch nie';
    }
    const minutes = Math.max(0, Math.round((Date.now() - new Date(value).getTime()) / 60000));
    if (minutes < 1) {
      return 'gerade eben';
    }
    if (minutes < 60) {
      return `vor ${minutes} Min.`;
    }
    const hours = Math.round(minutes / 60);
    return hours < 24 ? `vor ${hours} Std.` : `vor ${Math.round(hours / 24)} Tagen`;
  }

  /**
   * Entscheidet, welcher Status-Kreis eine Zeile bekommt. Die Reihenfolge ist die Prioritaet und
   * gibt das bisherige Verhalten wieder - nur eben eindeutig:
   *
   * 1. laufender Upload (auch aggregiert ueber einen Ordner)
   * 2. haengengebliebene Klassifizierung (Retry) - hat Vorrang vor 3., damit eine IFC-Datei, deren
   *    Poll aufgegeben hat, nicht im Zustand "erfordert Klassifizierung" stehen bleibt
   * 3. IFC ohne Fragments
   * 4. laufende Analyse
   * 5. Fehler (bei Ordnern nur, wenn dort nichts mehr laeuft)
   * 6. fertig
   */
  protected statusCircle(element: ProjectFile): FileStatusCircleKind | null {
    if (!element) return null;

    if (this.effectiveStatus(element) === ProjectFile.FileStatusEnum.UPLOADING
      || this.folderState(element, ProjectFile.FileStatusEnum.UPLOADING)) {
      return 'uploading';
    }

    if (this.isClassificationStalled(element)) return 'stalled';

    if (element.fileType === ProjectFile.FileTypeEnum.IFC
      && !this.hasFragments(element)
      && element.fileStatus !== ProjectFile.FileStatusEnum.UPLOADING
      && element.fileStatus !== ProjectFile.FileStatusEnum.PROCESSING) {
      return 'ifcUnclassified';
    }

    if (this.isAnalysing(element)) return 'analysing';
    if (this.isErrored(element)) return 'error';
    if (this.isReady(element)) return 'ready';

    return null;
  }

  /** Tooltip des Status-Kreises. Speist sich aus FILE_STATUS_LABELS, damit Hover und Detailpanel nicht auseinanderlaufen. */
  protected statusCircleTooltip(element: ProjectFile, kind: FileStatusCircleKind): string {
    switch (kind) {
      case 'uploading': {
        // Ordner in Dateien, einzelne Datei in Prozent - so steht neben dem Banner ("… von 381")
        // nie eine Zahl in einer anderen Einheit.
        if (element.isFolder) {
          const counts = this.folderUploadCounts(element);
          return `${this.statusLabelUploading} · ${Math.floor(counts.done)} von ${counts.total} Dateien`;
        }
        return `${this.statusLabelUploading} · ${this.uploadProgressOf(element)} %`;
      }
      case 'stalled':
        return 'Klassifizierung reagiert nicht mehr. Klicken, um es erneut zu versuchen.';
      case 'ifcUnclassified':
        return 'IFC-Datei erfordert Klassifizierung. Bitte Klassifizierung erneut versuchen.';
      case 'analysing':
        return this.statusLabelProcessing;
      case 'error': {
        if (element.isFolder) {
          const retryable = this.folderStatusCounts(element).retryable;
          if (retryable === 0) {
            return 'Mindestens eine Datei konnte nicht klassifiziert werden. Weitere Versuche sind '
              + 'nicht möglich.';
          }
          return 'Mindestens eine Datei konnte nicht klassifiziert werden. Klicken, um '
            + (retryable === 1 ? 'sie' : `alle ${retryable}`) + ' erneut zu klassifizieren.';
        }
        // Der Grund vom Server, wenn es einen gibt - er sagt genauer, was los ist, als unser
        // Sammelbegriff. Und bei erschoepften Versuchen keine Aufforderung mehr zum Klicken.
        if (this.isClassificationExhausted(element)) {
          return element.errorReason
            ? `${element.errorReason} Weitere Versuche sind nicht möglich - bitte die Datei löschen `
              + 'und erneut hochladen.'
            : `${this.statusLabelError}. Weitere Versuche sind nicht möglich - bitte die Datei `
              + 'löschen und erneut hochladen.';
        }
        return `${this.statusLabelError}. Klicken, um es erneut zu versuchen.`;
      }
      case 'ready':
        return this.statusLabelReady;
    }
  }

  /** Fortschritt in Prozent, den die Zeile anzeigen soll (Ordner: gewichtet ueber die Nachkommen). */
  protected uploadProgressOf(element: ProjectFile): number {
    if (element?.isFolder) return this.getFolderUploadProgress(element);
    return this.uploadingDocuments.get(this.uploadProgressKey(element)) ?? 0;
  }

  /**
   * Besorgt die präsignierte Download-URL. Die Tabellendaten enthalten sie nicht mehr: sie wurde für
   * jede Zeile signiert (~800 Zeichen pro Datei), gebraucht wird sie aber nur, wenn der Nutzer eine
   * einzelne Datei herunterlädt. Für Dateien, die noch aus einem Vollobjekt stammen (z.B. nach einem
   * SSE-Reload), wird der vorhandene Link direkt genutzt.
   */
  private async resolveDownloadLink(file: ProjectFile): Promise<string | undefined> {
    if (file.link) return file.link;
    if (!file.id) return undefined;

    const projectId = this.stateConfigService.getProjectId();
    if (!projectId) return undefined;

    try {
      const full = await firstValueFrom(this.fileController.getFile(projectId, file.id));
      return full?.link ?? undefined;
    } catch {
      return undefined;
    }
  }

  /**
   * Löst den Download aus, ohne einen neuen Tab zu oeffnen. Ein Klick auf ein Anchor-Element mit
   * target="_blank" wird vom Popup-Blocker verworfen, wenn er - wie beim ZIP-Download - erst
   * Sekunden nach der Nutzergeste im Callback passiert. Eine Navigation im gleichen Tab auf eine
   * presigned URL mit Attachment-Content-Type ist davon nicht betroffen.
   */
  private triggerDownload(url: string, fileName?: string): void {
    const a = document.createElement('a');
    a.href = url;
    if (fileName) a.download = fileName;
    a.rel = 'noopener';
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    a.remove();
  }

  async downloadFile(file: ProjectFile): Promise<void> {
    if (!this.permissionService.hasProjectPrivilege('PROJECT_VIEWER')) {
      this.notificationsService.showErrorMessage('Sie haben nicht die Berechtigung Dateien oder Ordner herunterzuladen.');
      return;
    }
    if (!file || this.downloadingFileId === file.id) return;
    if (this.hasPendingClassificationInSelection([file])) {
      this.notificationsService.showErrorMessage(
        'Solange die Datei klassifiziert wird, kann sie nicht heruntergeladen werden. Bitte warten, bis die Klassifizierung abgeschlossen ist.',
      );
      return;
    }

    this.downloadingFileId = file.id ?? null;
    // Große Dateien werden erst komplett in einen Blob geladen; ohne Toast passiert bis dahin
    // sichtbar nichts.
    // durationMs: 0 => bleibt offen, bis der Download tatsächlich startet.
    const toast = this.bicToastService.infoRef('Download wird vorbereitet …', {durationMs: 0});

    try {
      const link = await this.resolveDownloadLink(file);
      if (!link) {
        toast.dismiss();
        this.notificationsService.showErrorMessage('Die Datei konnte nicht heruntergeladen werden.');
        return;
      }

      try {
        const res = await fetch(link, {credentials: 'include'});
        if (!res.ok) {
          toast.dismiss();
          this.notificationsService.showErrorMessage('Die Datei konnte nicht heruntergeladen werden.');
          return;
        }
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        this.triggerDownload(url, file.name ?? 'download');
        setTimeout(() => {
          URL.revokeObjectURL(url);
        }, 200);
      } catch {
        // Fallback: direkt auf die presigned URL, falls der fetch (z.B. CORS) scheitert.
        this.triggerDownload(link);
      }

      this.bicToastService.update(toast.id, {message: 'Download gestartet.', type: 'success', durationMs: 4000});
    } finally {
      this.downloadingFileId = null;
    }
  }

  async downloadSelectedFiles(): Promise<void> {
    if (!this.permissionService.hasProjectPrivilege('PROJECT_VIEWER')) {
      this.notificationsService.showErrorMessage('Sie haben nicht die Berechtigung Dateien oder Ordner herunterzuladen.');
      return;
    }
    // Nur sichtbare Auswahl (#2770).
    const selection = this.visibleSelectedFiles();
    if (selection.length === 0 || this.downloadInProgress) return;
    if (this.hasPendingClassificationInSelection(selection)) {
      this.notificationsService.showErrorMessage(
        'Solange Dateien klassifiziert werden, können sie nicht heruntergeladen werden. Bitte warten, bis die Klassifizierung abgeschlossen ist.',
      );
      return;
    }

    if (selection.length === 1) {
      await this.downloadFile(selection[0]);
      return;
    }

    this.downloadInProgress = true;
    // Das ZIP wird serverseitig synchron gebaut (Lambda) und dauert bei vielen Dateien mehrere
    // Sekunden - ohne Toast wirkt der Button wie ohne Funktion.
    // Ordner gehen samt ausgeblendetem Inhalt ins ZIP; bei Suche/Filter sagt der Toast das (#2770).
    // Gezaehlt wird, was angehakt ist (oberste Elemente), wie im Loeschdialog.
    const folderHint = zipFolderHint(
      topLevelSelection(selection, this.originalData),
      isSearchOrFilterActive(this.searchTerm, this.columnFilters),
    );
    const withHint = (message: string) => folderHint ? `${message} ${folderHint}` : message;
    const toast = this.bicToastService.infoRef(withHint('ZIP wird erstellt …'), {durationMs: 0});

    this.fileController.downloadZip(selection, this.stateConfigService.getProjectId(), 'Projektdateien_' + this.stateConfigService.getProject()!.name!.replace(/[^A-Za-z0-9]+/g, '_') + '_' + (() => {
      const d = new Date();
      const dd = String(d.getDate()).padStart(2, '0');
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const yyyy = d.getFullYear();
      const hh = String(d.getHours()).padStart(2, '0');
      const min = String(d.getMinutes()).padStart(2, '0');
      return `${dd}_${mm}_${yyyy} ${hh}_${min} Uhr`;
    })() + '.zip').subscribe({
      next: (res) => {
        this.downloadInProgress = false;
        // Der Backend-Endpunkt gibt das rohe Lambda-Payload zurück; schlägt das Zippen fehl,
        // steht darin ein Fehlertext statt einer URL.
        const url = res?.string ? String(res.string) : '';
        if (!url.startsWith('http')) {
          toast.dismiss();
          this.notificationsService.showErrorMessage('Das ZIP-Archiv konnte nicht erstellt werden.');
          return;
        }
        this.triggerDownload(url);
        // Mit Hinweis laenger stehen lassen, damit er sich lesen laesst.
        this.bicToastService.update(toast.id, {message: withHint('Download gestartet.'), type: 'success', durationMs: folderHint ? 8000 : 4000});
      },
      error: () => {
        this.downloadInProgress = false;
        toast.dismiss();
        this.notificationsService.showErrorMessage('Die Dateien konnten nicht heruntergeladen werden.');
      }
    });
  }

  showInfo(file: ProjectFile): void {
    this.selectedFiles = [file];
    this.detailedInfos = true;
  }

  showSelectedInfo(): void {
    const selectedFolder = this.visibleSelectedFiles().find(file => file.isFolder);
    if (selectedFolder) {
      this.showInfo(selectedFolder);
      return;
    }

    this.detailedInfos = !this.detailedInfos;
  }

  open(file?: ProjectFile): void {
    const targetFile = file ?? this.visibleSelectedFiles()[0];
    if (!targetFile) return;
    if (targetFile.isFolder) {
      this.navigateIntoFolder(targetFile);
    } else {
      this.openFile(targetFile);
    }
  }

  /**
   * Ob eine IFC-Datei konvertierte Fragments hat (Voraussetzung fürs Öffnen im Viewer).
   *
   * Früher wurde dafür das Vorhandensein von `fragmentsLink` geprüft. Diese präsignierte URL setzt das
   * Backend in `@PostLoad` genau dann, wenn `fragmentsCreated` true ist — die Tabellenprojektion liefert
   * deshalb direkt `fragmentsCreated`, statt für jede Zeile eine URL zu signieren, die anschließend nur
   * auf Vorhandensein geprüft wird.
   */
  protected hasFragments(file: ProjectFile | undefined | null): boolean {
    return file?.fragmentsCreated === true;
  }

  /**
   * Ob zu dieser Zeile serverseitig noch Arbeit laeuft - Upload oder Klassifizierung.
   *
   * Die gemeinsame Sperre fuer alles, was eine Datei anfasst, die noch niemandem gehoert. Vorher
   * hing das an den einzelnen Menuepunkten, und zwei fehlten: "Klassifizierung erneut versuchen"
   * legte sich mitten in eine laufende Klassifizierung (die dann abbrach), und "Koordinationsmodell
   * erstellen" nahm IFC-Dateien an, die noch analysiert wurden - das Modell entstand, aber der
   * Viewer blieb weiss, weil es die Fragments noch nicht gab.
   *
   * Bewusst der ROHE fileStatus und nicht effectiveStatus(): letzterer zeigt eine haengengebliebene
   * UPLOADING-Zeile als PROCESSING an, damit die Anzeige nicht luegt. Fuer eine Sperre zaehlt aber,
   * was der Server weiss - und der hat die Bytes in diesem Fall tatsaechlich nicht.
   */
  protected isBusyWithUploadOrAnalysis(file: ProjectFile | undefined | null): boolean {
    if (!file) return false;
    return file.fileStatus === ProjectFile.FileStatusEnum.UPLOADING
      || file.fileStatus === ProjectFile.FileStatusEnum.UPLOADED
      || file.fileStatus === ProjectFile.FileStatusEnum.PROCESSING;
  }

  /**
   * Ob der Menuepunkt "Klassifizierung erneut versuchen" fuer eine IFC-Datei angeboten wird.
   *
   * Zwei Bedingungen, die vorher beide fehlten:
   *
   * 1. Nicht waehrend Upload oder Analyse. Genau das war der Fehler aus dem Bericht: direkt nach
   *    dem Upload laeuft die Analyse, der Punkt stand aber schon da - und ein Klick startete eine
   *    zweite Klassifizierung neben der laufenden.
   * 2. Nicht bei ERROR. Dort bietet der naechste Block denselben Text an (ueber
   *    retryClassification), und eine IFC-Datei ohne Fragments im Status ERROR erfuellt beide
   *    Bedingungen - der Eintrag stand dann doppelt im Menue.
   */
  protected canRetryIfcClassification(file: ProjectFile): boolean {
    return file.fileType === ProjectFile.FileTypeEnum.IFC
      && !this.hasFragments(file)
      && file.fileStatus !== ProjectFile.FileStatusEnum.ERROR
      && !this.isBusyWithUploadOrAnalysis(file);
  }

  /**
   * Die IFC-Dateien der Auswahl, aus denen sich ein Koordinationsmodell bauen laesst.
   *
   * Ein Koordinationsmodell ueberlagert die FRAGMENTS mehrerer IFC-Dateien; eine Datei, deren
   * Klassifizierung noch laeuft, hat noch keine. Im Bericht war genau das zu sehen: das Modell liess
   * sich anlegen, obwohl jede andere Aktion der Zeile ausgegraut war, und der Viewer zeigte
   * anschliessend eine weisse Seite. Dieselbe Bedingung sperrt im Kontextmenue bereits "Oeffnen".
   */
  protected coordinationReadyIfcFiles(): ProjectFile[] {
    return this.getSelectedIfcFiles().filter(file => this.hasFragments(file));
  }

  /**
   * Warum mit dieser Auswahl gerade kein Koordinationsmodell geht - oder null.
   *
   * Als Grund und nicht als verschwindender Knopf: ein Knopf, der ohne Erklaerung mal da ist und mal
   * nicht, ist genau die Art von Verhalten, ueber die in den Berichten gestolpert wurde. Er bleibt
   * also stehen, ist gesperrt, und der Tooltip sagt, worauf zu warten ist.
   */
  protected coordinationBlockedReason(): string | null {
    const selected = this.getSelectedIfcFiles();
    const pending = selected.length - this.coordinationReadyIfcFiles().length;
    if (pending <= 0) return null;
    return pending === 1
      ? 'Eine der gewählten IFC-Dateien wird noch analysiert.'
      : `${pending} der gewählten IFC-Dateien werden noch analysiert.`;
  }

  openFile(file: ProjectFile) {
    // Verhindere Öffnen von IFC-Dateien ohne Fragments
    if (file.fileType === ProjectFile.FileTypeEnum.IFC && !this.hasFragments(file)) {
      this.notificationsService.showErrorMessage('Diese IFC-Datei kann nicht geöffnet werden. Sie muss zuerst klassifiziert werden. Bitte nutzen Sie die Option "Klassifizierung erneut versuchen".');
      return;
    }

    if (file.fileType === ProjectFile.FileTypeEnum.COORDINATION) {
      this.openCoordinationModelFile(file);
      return;
    }
    let baseUrl = window.location.origin + '/projects/' + this.stateConfigService.getProjectId() + '/files/' + file.id;
    this.popoutContentService.open(baseUrl, file.name ?? '');
  }

  /** Klick auf den Kreis: nur die klickbaren Zustaende stossen eine erneute Klassifizierung an. */
  protected isRetryableCircle(element: ProjectFile, kind: FileStatusCircleKind): boolean {
    // Ordner: der Kreis steht fuer "mindestens eine Datei darin ist gescheitert" - der Klick
    // wiederholt genau diese Dateien. Die Zahl kommt aus dem zwischengespeicherten Ordnerzaehler,
    // der Teilbaum wird dafuer nicht bei jedem Zyklus erneut durchlaufen.
    if (element?.isFolder) {
      return kind === 'error' && this.folderStatusCounts(element).retryable > 0;
    }
    // Nach genug Fehlschlaegen ist Schluss. Ein Kreis, der zum Klicken einlaedt und dabei jedes Mal
    // denselben Fehler produziert, ist schlechter als einer, der sagt, was wirklich zu tun ist -
    // und jeder Klick kostet einen vollen Lauf durch die KI-Dienste. Ausloeser war eine Datei,
    // deren Blob nie in S3 ankam: "Could not download the file from the given URL", bei jedem
    // Versuch, beliebig oft.
    if (this.isClassificationExhausted(element)) return false;
    return kind === 'error' || kind === 'stalled';
  }

  /**
   * Ob der Server fuer diese Datei keine weiteren Klassifizierungsversuche mehr vorsieht.
   *
   * Die Schwelle liegt bewusst serverseitig; hier wird nur das Ergebnis gelesen. Fehlt das Feld
   * (aelterer Server, Zeile aus einer anderen Quelle), gilt die Datei als nicht erschoepft - im
   * Zweifel lieber einen Versuch zu viel anbieten als einen zu wenig.
   */
  protected isClassificationExhausted(element: ProjectFile | undefined | null): boolean {
    return element?.classificationExhausted === true;
  }

  /**
   * Schluessel, unter dem der Upload-Fortschritt einer Zeile in `uploadingDocuments` steht.
   *
   * Bewusst NICHT der Dateiname: bei einem Ordner-Upload existiert derselbe Name in vielen
   * Unterordnern. Alle diese Zeilen teilten sich dann einen Fortschrittswert, und das `delete` beim
   * ersten fertigen Job raeumte den Eintrag fuer alle weg - die uebrigen Ringe blieben bei 0 %
   * stehen und drehten weiter. Solange die Zeile noch ein Dummy ohne id ist, identifiziert sie
   * dieselbe Kombination wie {@link getDummyKey}; danach ihre Server-id.
   */
  protected uploadProgressKey(row: ProjectFile): string {
    if (row?.id) return row.id;
    return `${row?.name ?? ''}__${row?.size ?? 0}__${row?.parentId ?? ''}`;
  }

  /**
   * Sucheingabe. `bic-input` liefert den Wert als String über `valueChange`; das native
   * `input`-Event wird bewusst nicht zusätzlich gebunden, sonst lief die Suche zweimal je
   * Anschlag - einmal davon mit leerem Begriff, was kurz die ungefilterte Liste rendert.
   */
  search(query: Event | string): void {
    const searchTerm = typeof query === 'string'
      ? query
      : (query.target as HTMLInputElement)?.value ?? '';

    this.searchInput$.next(searchTerm);
  }

  editCoordinationModel(file: ProjectFile): void {
    if (!this.permissionService.hasProjectPrivilege('PROJECT_FILE_EDIT')) {
      this.notificationsService.showErrorMessage('Sie haben nicht die Berechtigung Koordinationsmodelle zu bearbeiten.');
      return;
    }

    const allIfcFiles = this.originalData.filter(f => !f.isFolder && f.fileType === ProjectFile.FileTypeEnum.IFC);
    const linkedFileIds = (file.linkedFiles ?? []).map(lf => lf.id).filter((id): id is string => !!id);

    const dialogRef = this.dialog.open(EditCoordinationModelComponent, {
      hasBackdrop: false,
      data: {
        ifcFiles: allIfcFiles,
        linkedFileIds,
      },
    });

    dialogRef.afterClosed().subscribe((result: { linkedFileIds: string[] } | undefined) => {
      if (!result) return;

      const projectId = this.stateConfigService.getProjectId();

      this.fileController.updateCoordinationModel(
        result.linkedFileIds,
        projectId,
        file.id ?? ''
      ).subscribe({
        next: (response: ProjectFile) => {
          if (!response) return;
          const odIdx = this.originalData.findIndex(pf => pf.id === response.id);
          if (odIdx !== -1) {
            this.originalData[odIdx] = response;
          } else {
            this.addOrRemoveFileToOrFromOriginalData(response);
          }
          const fdIdx = this.filteredData.findIndex(pf => pf.id === response.id);
          if (fdIdx !== -1) {
            this.filteredData[fdIdx] = response;
            this.filteredData = [...this.filteredData];
          }
          // Sync selectedFiles if the edited file is currently selected
          const sfIdx = this.selectedFiles.findIndex(pf => pf.id === response.id);
          if (sfIdx !== -1) {
            this.selectedFiles[sfIdx] = response;
          }
          this.rebuildChildrenRelations();
          this.notificationsService.showSuccessMessage('Koordinationsmodell wurde erfolgreich aktualisiert.');
        },
        error: () => {
          this.notificationsService.showErrorMessage('Koordinationsmodell konnte nicht aktualisiert werden. Bitte erneut versuchen.');
        },
      });
    });
  }

  getSelectedIfcFiles(): ProjectFile[] {
    return this.visibleSelectedFiles().filter(f => !f.isFolder && f.fileType === ProjectFile.FileTypeEnum.IFC);
  }

  openCoordinationModel(): void {
    // s. createCoordinationModelFile(): ohne Fragments zeigt der Viewer eine weisse Seite.
    const ifcFiles = this.coordinationReadyIfcFiles();
    if (ifcFiles.length < 2) {
      const reason = this.coordinationBlockedReason();
      if (reason) this.notificationsService.showErrorMessage(reason);
      return;
    }

    const primaryFile = ifcFiles[0];
    const additionalIds = ifcFiles.slice(1).map(f => f.id).join(',');
    const baseUrl = window.location.origin + '/projects/' + this.stateConfigService.getProjectId() + '/files/' + primaryFile.id
      + '?coordination=' + additionalIds;
    this.popoutContentService.open(baseUrl, 'Koordinationsmodell');
  }

  /**
   * Entfernt Dateien inkl. aller Nachkommen lokal aus dem Datenbestand.
   *
   * Bewusst über die `id` statt über die Objekt-Identität: die Tabelle rendert `filteredData`, und
   * die SSE-/Poll-Handler ersetzen Zeilen dort teils durch frische Objekte. Ein Referenz-Filter
   * träfe dann in `originalData` nichts, die Datei bliebe stehen und käme beim nächsten
   * `updateCurrentFolderData()` zurück.
   *
   * Ebenso bewusst rekursiv: beim Löschen eines Ordners entfernt der Server den ganzen Teilbaum.
   * Blieben die Kinder in `originalData`, tauchten sie verwaist in der Root-Suche auf (die filtert
   * direkt über `originalData`, nicht über `currentFolderData`).
   *
   * @returns die tatsächlich entfernten IDs (Ordner + Nachkommen)
   */
  private removeFilesLocally(ids: string[]): string[] {
    const childrenMap = this.buildChildrenMap(this.originalData);

    const idsToRemove = new Set<string>();
    const stack = [...ids];
    while (stack.length > 0) {
      const id = stack.pop()!;
      if (idsToRemove.has(id)) continue;
      idsToRemove.add(id);
      for (const child of childrenMap.get(id) ?? []) {
        if (child.id) stack.push(child.id);
      }
    }

    // Parent-Verknüpfung + Ordnergrößen aktualisieren, solange die Objekte noch vorhanden sind.
    for (const pf of this.originalData) {
      if (pf.id && idsToRemove.has(pf.id)) {
        this.setOrRemoveParentInOriginalData(pf, pf.parentId ?? null, true);
      }
    }

    const isRemoved = (pf: ProjectFile) => !!pf.id && idsToRemove.has(pf.id);
    this.originalData = this.originalData.filter(pf => !isRemoved(pf));
    this.filteredData = this.filteredData.filter(pf => !isRemoved(pf));

    this.updateCurrentFolderData();

    return [...idsToRemove];
  }

  /**
   * True, solange eine Zeile serverseitig noch nicht existiert: ein Dummy aus einem laufenden
   * Upload (Ordner-Dummies tragen eine `__folder_dummy_`-ID, Datei-Dummies gar keine).
   *
   * Umbenennen war dafuer bisher erlaubt und schlug lautlos fehl - der Rename-Call ging mit der
   * Dummy-ID ans Backend, der lokale Name blieb trotzdem stehen. Nach dem naechsten loadFiles()
   * stand der Ordner wieder unter dem alten Namen da, waehrend die inzwischen echt angelegte
   * Struktur darunter hing: genau das Bild "Struktur kaputt / neuer Ordner".
   */
  protected isPendingUploadRow(file: ProjectFile): boolean {
    if (!file) return true;
    if (!file.id) return true;
    if (file.id.startsWith(FilesComponent.FOLDER_DUMMY_ID_PREFIX)) return true;
    if (file.fileStatus !== ProjectFile.FileStatusEnum.UPLOADING) return false;

    // Nicht "der Server sagt UPLOADING", sondern "dieser Tab uebertraegt gerade Bytes dafuer".
    //
    // Die Gruende, aus denen Verschieben/Kopieren/Umbenennen waehrend eines Uploads gesperrt sind,
    // sind allesamt tab-lokal: die laufenden Jobs tragen die alte parentId, und eine Zeile ohne
    // Server-ID faellt beim Mappen auf IDs stillschweigend heraus. Der Serverstatus taugt als
    // Kriterium nicht - eine Zeile bleibt auf UPLOADING stehen, wenn der Tab weggebrochen ist, der
    // die Bytes hatte. Vorher sperrte so eine Karteileiche ihren Ordnerbaum dauerhaft, ohne dass
    // irgendwo noch etwas lief, das den Zustand haette aufloesen koennen.
    return this.uploadService.hasActiveJobForFileId(file.id);
  }

  /**
   * Löst die Auswahl in die Reihenfolge auf, in der gelöscht wird: erst alle enthaltenen DATEIEN,
   * danach die Ordner von innen nach außen.
   *
   * Warum nicht einfach den Ordner schicken und den Server rekursiv löschen lassen: dann gäbe es genau
   * einen Job und der Dialog könnte nur "läuft …" zeigen. Der Nutzer will den Fortschritt auf
   * Dateiebene sehen, mit den Ordnern darüber - wie beim Upload. Ein Job pro Datei liefert genau das,
   * und weil jeder Request einzeln committet, ist der Fortschritt auch echt und nicht nur gemalt.
   *
   * Ordner zuletzt und von innen nach außen, damit ein Ordner beim Löschen schon leer ist. Doppelte
   * IDs sind ausgeschlossen, deshalb kann kein Request auf ein bereits gelöschtes Kind laufen.
   */
  private expandDeleteTargets(targets: ProjectFile[]): ProjectFile[] {
    const childrenMap = this.buildChildrenMap(this.originalData);

    const files: ProjectFile[] = [];
    const foldersByDepth: { file: ProjectFile; depth: number }[] = [];
    const seen = new Set<string>();

    const walk = (node: ProjectFile, depth: number): void => {
      if (!node.id || seen.has(node.id)) return;
      seen.add(node.id);

      if (node.isFolder) {
        foldersByDepth.push({file: node, depth});
        for (const child of childrenMap.get(node.id) ?? []) {
          walk(child, depth + 1);
        }
      } else {
        files.push(node);
      }
    };

    for (const target of targets) {
      walk(target, 0);
    }

    // Tiefste Ordner zuerst.
    foldersByDepth.sort((a, b) => b.depth - a.depth);

    return [...files, ...foldersByDepth.map(entry => entry.file)];
  }

  createCoordinationModelFile(): void {
    if (!this.permissionService.hasProjectPrivilege('PROJECT_FILE_EDIT')) {
      this.notificationsService.showErrorMessage('Sie haben nicht die Berechtigung Koordinationsmodelle zu erstellen.');
      return;
    }

    // Nur Dateien MIT Fragments: ein Koordinationsmodell ueberlagert die Fragments seiner IFC-
    // Dateien. Mit einer noch laufenden Analyse liess es sich zwar anlegen, aber der Viewer blieb
    // danach weiss. Die Sperre steht auch am Knopf - hier noch einmal, weil das Menue nur die
    // Ansicht ist.
    const ifcFiles = this.coordinationReadyIfcFiles();
    if (ifcFiles.length < 1) {
      const reason = this.coordinationBlockedReason();
      if (reason) this.notificationsService.showErrorMessage(reason);
      return;
    }

    const dialogRef = this.dialog.open(NewCoordinationModelComponent, {
      hasBackdrop: false,
      data: {
        ifcFiles,
        otherNames: this.currentFolderData.map(item => item.name).filter((n): n is string => !!n),
      },
    });

    dialogRef.afterClosed().subscribe((result: { name: string } | undefined) => {
      if (!result) return;

      const ifcFileIds = ifcFiles.map(f => f.id).filter((id): id is string => !!id);
      const parentId = this.currentParent?.id;
      const projectId = this.stateConfigService.getProjectId();

      this.fileController.createCoordinationModel(
        ifcFileIds,
        result.name.trim(),
        projectId,
        parentId
      ).subscribe({
        next: () => {
          this.loadFiles();
          this.selectedFiles = [];
          this.notificationsService.showSuccessMessage('Koordinationsmodell wurde erfolgreich erstellt.');
        },
        error: (err: HttpErrorResponse) => {
          console.error('[CoordinationModel] Fehler beim Erstellen:', err);
          this.notificationsService.showErrorMessage(coordinationModelErrorMessage(err));
        },
      });
    });
  }

  /**
   * Ob der gesamte Teilbaum eines Ordners im angefragten Status ist.
   *
   * Das Template fragt das pro Ordnerzeile für jeden Status einzeln ab (acht Aufrufe), und jeder
   * Aufruf lief früher über den kompletten Teilbaum. Gezählt wird deshalb einmal pro Ordner und
   * Change-Detection-Zyklus; ngDoCheck verwirft den Cache zu Beginn jedes Zyklus, damit er nicht
   * veralten kann - egal wodurch sich ein Dateistatus ändert (SSE, Upload, Reload).
   */
  folderState(element: ProjectFile, state: ProjectFile.FileStatusEnum): boolean {
    if (!element.isFolder) return false;

    const counts = this.folderStatusCounts(element);
    if (counts.total === 0) return false;

    const {total, checked, ready, processing, uploading, error} = counts;

    if (state === ProjectFile.FileStatusEnum.CHECKED) {
      return checked === total;
    }
    if (state === ProjectFile.FileStatusEnum.READY) {
      return (ready + checked === total) && ready > 0;
    }
    if (state === ProjectFile.FileStatusEnum.PROCESSING) {
      return (processing + ready + checked === total) && processing > 0;
    }
    if (state === ProjectFile.FileStatusEnum.UPLOADING) {
      return (uploading + processing + ready + checked === total) && uploading > 0;
    }
    if (state === ProjectFile.FileStatusEnum.ERROR) {
      return error > 0;
    }

    return false;
  }

  /**
   * True, wenn zu dieser Auswahl noch ein Upload laeuft - entweder ist eine Zeile selbst ein
   * Upload-Dummy, oder ein Ordner der Auswahl enthaelt eine Datei, die gerade hochgeladen wird.
   *
   * Verschieben und Kopieren waren dafuer bisher offen: Zeilen ohne id fielen beim Mappen auf IDs
   * stillschweigend heraus (die Datei blieb liegen, ohne Hinweis), ein Ordner-Dummy ging mit seiner
   * `__folder_dummy_`-ID ans Backend, und die schon laufenden Upload-Jobs trugen weiterhin die alte
   * parentId - die Dateien landeten also im alten Ordner.
   */
  protected hasPendingUploadInSelection(files: ProjectFile[]): boolean {
    // Nur der Aufruf mit der aktuellen Auswahl (bzw. ihrer gecachten sichtbaren Sicht, #2770) wird
    // gecacht - das Kontextmenue fragt pro Zeile.
    if (files === this.selectedFiles || files === this.visibleSelectionCache?.result) {
      if (this.pendingUploadInSelectionCache === undefined) {
        this.pendingUploadInSelectionCache = this.computePendingUploadInSelection(files);
      }
      return this.pendingUploadInSelectionCache;
    }
    return this.computePendingUploadInSelection(files);
  }

  /** True, wenn eine Auswahl noch klassifiziert wird, auch innerhalb eines ausgewählten Ordners. */
  protected hasPendingClassificationInSelection(files: ProjectFile[]): boolean {
    if (files === this.selectedFiles || files === this.visibleSelectionCache?.result) {
      if (this.pendingClassificationInSelectionCache === undefined) {
        this.pendingClassificationInSelectionCache = this.computePendingClassificationInSelection(files);
      }
      return this.pendingClassificationInSelectionCache;
    }
    return this.computePendingClassificationInSelection(files);
  }

  /**
   * True, wenn die Auswahl mindestens eine Zeile mit echtem Dateiinhalt enthaelt.
   *
   * Koordinationsmodelle sind reine Verknuepfungen mehrerer IFC-Dateien; der ZIP-Download scheitert
   * fuer sie. Bei einer Auswahl, die nur daraus besteht, wird der Download-Button gar nicht erst
   * angeboten.
   */
  protected hasDownloadableSelection(): boolean {
    return (this.visibleSelectedFiles() ?? []).some(
      file => file.isFolder || file.fileType !== ProjectFile.FileTypeEnum.COORDINATION,
    );
  }

  /**
   * Der Status, den die Ansicht zeigen soll - nicht unbedingt der, den der Server gespeichert hat.
   *
   * Einziger Unterschied: eine Datei auf UPLOADING, deren Bytes dieser Tab durchgebracht hat, gilt
   * als PROCESSING - denn UPLOADED heisst genau das. Und als Rueckfall gilt dasselbe fuer eine
   * Zeile auf UPLOADING, zu der dieser Tab keinen laufenden Upload kennt: "UPLOADING" bedeutete
   * historisch auch "Bytes durch, Verarbeitung wartet", und das soll niemand als laufende
   * Uebertragung sehen.
   */
  /**
   * Setzt bestaetigte Dateien lokal auf UPLOADED.
   *
   * Nur aus UPLOADING heraus - dieselbe Bedingung wie im Bulk-Update des Servers. Die Bestaetigung
   * kann verspaetet ankommen, wenn die Klassifizierung der Datei schon laeuft oder sie fertig ist;
   * dann darf sie den Status nicht zurueckdrehen.
   */
  private applyUploadedStatus(fileIds: string[]): void {
    for (const fileId of fileIds) {
      if (fileId) this.pendingUploadedFileIds.add(fileId);
    }
    this.flushPendingUploadedStatus();
  }

  /**
   * Traegt bestaetigte UPLOADED-Meldungen in die Liste ein.
   *
   * Die IDs bleiben vorgemerkt, bis ihre Zeile da ist: die Bestaetigung kann eintreffen, bevor
   * {@link applyUploadJobUpdate} die Dummy-Zeile auf die Server-ID umgestellt hat - dieselbe Race
   * Condition, fuer die es {@link pendingClassificationCompletedIds} gibt. Ohne das Vormerken
   * verlor der Ordner genau diese Dateien aus seinem Zaehler.
   *
   * Gesetzt wird nur aus UPLOADING heraus, dieselbe Bedingung wie im Bulk-Update des Servers: die
   * Meldung darf eine schon laufende oder fertige Klassifizierung nicht zurueckdrehen.
   */
  private flushPendingUploadedStatus(): void {
    if (this.pendingUploadedFileIds.size === 0) return;

    let changed = false;
    for (const row of [...(this.originalData ?? []), ...(this.filteredData ?? [])]) {
      if (!row.id || !this.pendingUploadedFileIds.has(row.id)) continue;
      if (row.fileStatus === ProjectFile.FileStatusEnum.UPLOADING) {
        row.fileStatus = ProjectFile.FileStatusEnum.UPLOADED;
        changed = true;
      }
      // Zeile ist da - egal ob gesetzt oder schon weiter, die Vormerkung hat sich erledigt.
      this.pendingUploadedFileIds.delete(row.id);
    }
    if (changed) {
      this.updateCurrentFolderData();
    }
  }

  private effectiveStatus(file: ProjectFile): ProjectFile.FileStatusEnum | undefined {
    // UPLOADED heisst serverseitig "Bytes sind durch, Verarbeitung wartet". Fuer die Anzeige ist das
    // dasselbe wie eine laufende Analyse - der Unterschied ist rein technisch, und die Zeile soll
    // keinen Upload-Ring mehr zeigen.
    if (file.fileStatus === ProjectFile.FileStatusEnum.UPLOADED) {
      return ProjectFile.FileStatusEnum.PROCESSING;
    }

    if (file.fileStatus !== ProjectFile.FileStatusEnum.UPLOADING) {
      return file.fileStatus;
    }

    // Rueckfalltuer fuer Zeilen, die noch aus der Zeit vor UPLOADED stammen (oder deren Meldung
    // verloren ging): laeuft hier kein Upload und ist auch kein Fortschritt zu dieser Datei
    // bekannt, ist UPLOADING kein laufender Transfer. Serverseitig raeumt der UploadTimeoutSweeper
    // solche Zeilen ohnehin auf; bis dahin soll die Anzeige nicht luegen.
    if (!this.uploadRunning && !this.uploadingDocuments.has(this.uploadProgressKey(file))) {
      return ProjectFile.FileStatusEnum.PROCESSING;
    }

    return file.fileStatus;
  }

  private folderStatusCounts(folder: ProjectFile): FolderStatusCounts {
    const key = folder.id ?? '';
    const cached = this.folderStatusCache.get(key);
    if (cached) return cached;

    const counts: FolderStatusCounts = {
      total: 0, checked: 0, ready: 0, processing: 0, uploading: 0, error: 0, retryable: 0,
    };
    for (const file of this.collectAllFileDescendants(folder)) {
      counts.total++;
      if (file.fileStatus === ProjectFile.FileStatusEnum.ERROR && !this.isClassificationExhausted(file)) {
        counts.retryable++;
      }
      switch (this.effectiveStatus(file)) {
        case ProjectFile.FileStatusEnum.CHECKED:
          counts.checked++;
          break;
        case ProjectFile.FileStatusEnum.READY:
          counts.ready++;
          break;
        case ProjectFile.FileStatusEnum.PROCESSING:
          counts.processing++;
          break;
        case ProjectFile.FileStatusEnum.UPLOADING:
          counts.uploading++;
          break;
        case ProjectFile.FileStatusEnum.ERROR:
          counts.error++;
          break;
      }
    }

    this.folderStatusCache.set(key, counts);
    return counts;
  }

  /**
   * Gemeinsamer Wachter fuer Verschieben/Kopieren. Gibt true zurueck, wenn die Aktion NICHT laufen
   * darf, und meldet den Grund.
   */
  /**
   * Blockt eine Aktion, solange zur Auswahl noch ein Upload laeuft.
   *
   * Loeschen und Umbenennen waren dafuer offen, und beim Loeschen war die Folge schlimm: der
   * Oberordner verschwand mitsamt Teilbaum, die noch nicht hochgeladenen Dateien liefen aber
   * weiter - in einen Ordner, den es nicht mehr gab. Sichtbar nur noch als wachsende Zahl oben
   * rechts ohne zugehoerige Zeilen, und eine Restzahl ("12 Dateien"), die sich nicht mehr
   * wegbekommen liess.
   *
   * Das ist die Sperre in der Ansicht. Sie kann nur pruefen, was die Ansicht geladen hat - die
   * verlaessliche Zusage steht serverseitig in {@code FileTrashService.moveToTrash}.
   */
  protected blockedByPendingUpload(
    file: ProjectFile | undefined,
    action: 'verschoben' | 'kopiert' | 'gelöscht' | 'umbenannt',
  ): boolean {
    if (!this.hasPendingUploadInSelection(this.targetsFor(file))) return false;
    this.notificationsService.showErrorMessage(
      `Solange Dateien hochgeladen werden, kann nicht ${action} werden. Bitte warten, bis der Upload abgeschlossen ist.`,
    );
    return true;
  }

  /** Blockt Verschieben und Kopieren, solange die ausgewählten Dateien klassifiziert werden. */
  protected blockedByPendingClassification(
    file: ProjectFile | undefined,
    action: 'verschoben' | 'kopiert',
  ): boolean {
    if (!this.hasPendingClassificationInSelection(this.targetsFor(file))) return false;
    this.notificationsService.showErrorMessage(
      `Solange Dateien klassifiziert werden, kann nicht ${action} werden. Bitte warten, bis die Klassifizierung abgeschlossen ist.`,
    );
    return true;
  }

  /** IDs der Dateien (ohne Ordner), die diese Ansicht kennt - Bezugsmenge fuer classifyingCount. */
  private classifiableFileIds(): Set<string> {
    const ids = new Set<string>();
    for (const pf of this.originalData ?? []) {
      if (!pf.isFolder && pf.id) ids.add(pf.id);
    }
    return ids;
  }

  /** Merkt eine selbst angestossene Klassifizierung vor und hebt einen Haenger-Zustand auf. */
  private markClassificationStarted(fileId: string | undefined): void {
    if (!fileId) return;
    this.locallyStartedClassificationIds.add(fileId);
    this.stalledClassificationIds.delete(fileId);
    this.classificationMissesById.delete(fileId);
  }

  /** Eine Datei hat einen Endzustand erreicht - alle Haenger-Buchhaltung dazu verfaellt. */
  private markClassificationSettled(fileId: string | undefined): void {
    if (!fileId) return;
    this.locallyStartedClassificationIds.delete(fileId);
    this.stalledClassificationIds.delete(fileId);
    this.classificationMissesById.delete(fileId);
  }

  private isAnalysing(element: ProjectFile): boolean {
    const classifyState = this.file_classify_state_map.get(element.id ?? '');
    const ownAnalysis =
      this.effectiveStatus(element) === ProjectFile.FileStatusEnum.PROCESSING
      && (classifyState === undefined
        || classifyState === classifyStateEnum.BEFORE
        || classifyState === classifyStateEnum.IN);

    if (!ownAnalysis && !this.folderState(element, ProjectFile.FileStatusEnum.PROCESSING)) return false;

    // IFC mit Fragments ist fachlich fertig, auch wenn der Status noch PROCESSING sagt.
    return !(element.fileType === ProjectFile.FileTypeEnum.IFC && this.hasFragments(element));
  }

  private isErrored(element: ProjectFile): boolean {
    const hasError =
      element.fileStatus === ProjectFile.FileStatusEnum.ERROR
      || this.folderState(element, ProjectFile.FileStatusEnum.ERROR);
    if (!hasError) return false;

    // Ordner, in denen noch etwas laeuft, zeigen den Fortschritt statt des Fehlers.
    if (element.isFolder
      && (element.fileStatus === ProjectFile.FileStatusEnum.UPLOADING
        || element.fileStatus === ProjectFile.FileStatusEnum.PROCESSING
        || this.folderState(element, ProjectFile.FileStatusEnum.UPLOADING)
        || this.folderState(element, ProjectFile.FileStatusEnum.PROCESSING))) {
      return false;
    }

    return true;
  }

  private isReady(element: ProjectFile): boolean {
    if (element.fileStatus === ProjectFile.FileStatusEnum.ERROR) return false;
    if (this.folderState(element, ProjectFile.FileStatusEnum.ERROR)) return false;

    return element.fileStatus === ProjectFile.FileStatusEnum.READY
      || this.file_classify_state_map.get(element.id ?? '') === classifyStateEnum.AFTER
      || this.folderState(element, ProjectFile.FileStatusEnum.READY)
      || (element.fileType === ProjectFile.FileTypeEnum.IFC
        && element.fileStatus === ProjectFile.FileStatusEnum.PROCESSING);
  }

  /** Beide Schluessel eines Jobs: der des Dummys und - sobald bekannt - der der echten Datei. */
  private uploadJobProgressKeys(job: UploadJob): string[] {
    const keys = [this.getDummyKey(job.file, job.parentId)];
    if (job.fileId) keys.push(job.fileId);
    if (job.projectFile?.id) keys.push(job.projectFile.id);
    return keys;
  }

  // Hilfsfunktion: Suche auf Dataset anwenden (über die sichtbaren Textspalten)
  private applySearchToDataset(dataset: ProjectFile[], term: string): ProjectFile[] {
    const t = (term || '').trim().toLowerCase();
    if (!t) return dataset;

    return dataset.filter((row: any) =>
      this.searchableColumns.some((column: string) => {
        const value = row[column];
        if (value == null) return false;
        return value.toString().toLowerCase().includes(t);
      })
    );
  }

  /**
   * Meldet dem Fortschritts-Dialog, wieviele Jobs tatsaechlich eingereiht wurden, und macht eine
   * Differenz zur erwarteten Anzahl sichtbar, statt sie stumm als "laeuft noch" stehen zu lassen.
   */
  private reportEnqueuedUploadCount(expected: number, enqueued: number): void {
    const instance = this.uploadProgressDialogRef?.componentInstance;
    if (instance) {
      instance.enqueuedFileCount = enqueued;
      instance.notEnqueuedCount = Math.max(0, expected - enqueued);
    }

    const missing = Math.max(0, expected - enqueued);
    if (missing > 0) {
      this.bicToastService.error(
        missing === 1
          ? 'Eine Datei konnte nicht zum Upload eingereiht werden.'
          : `${missing} Dateien konnten nicht zum Upload eingereiht werden.`,
      );
    }
  }

  /**
   * Loescht den Suchbegriff (inkl. Eingabefeld, das per [value]="searchTerm" gebunden ist).
   *
   * Notwendig beim Navigieren: bei aktiver Suche zeigt die Tabelle eine flache, projektweite
   * Trefferliste. Blieb der Begriff beim Betreten eines Ordners stehen, griff weiter der
   * Suchzweig in applyColumnFilters() - ein Ordner, der nur wegen seines eigenen Namens Treffer
   * war, erschien dann leer.
   *
   * Der leere Wert geht zusaetzlich durch searchInput$: dessen distinctUntilChanged merkt sich
   * sonst weiter den alten Begriff, und die naechste manuelle Eingabe desselben Begriffs wuerde
   * verschluckt. searchTerm wird trotzdem direkt gesetzt, damit der unmittelbar folgende
   * applyColumnFilters()-Aufruf nicht 200ms auf die Entprellung warten muss.
   */
  private clearSearchTerm(): void {
    if (!this.searchTerm) return;
    this.searchTerm = '';
    this.searchInput$.next('');
  }

  /**
   * Oeffnet ein gespeichertes Koordinationsmodell in einem eigenen Fenster.
   *
   * Adressiert wird das Koordinationsmodell selbst; der FileViewer loest seine verknuepften Dateien
   * auf. Vorher stand in der URL die erste verknuepfte Datei plus `?coordination=` -- derselbe Link
   * zeigte nach dem Bearbeiten der Verknuepfungen auf einen veralteten Stand, und im Viewer stand
   * der Name des ersten Fachmodells statt der des Modells. Die Aufrufer pruefen vorher, dass
   * ueberhaupt Dateien verknuepft sind, damit es eine Meldung statt eines leeren Viewers gibt.
   */
  private openLinkedCoordinationFiles(file: ProjectFile): void {
    const url = window.location.origin
      + coordinationModelPath(this.stateConfigService.getProjectId(), file.id!);
    this.popoutContentService.open(url, file.name ?? 'Koordinationsmodell');
  }

  /**
   * Startet die Löschung als verfolgbare Jobs und zeigt den Fortschritt.
   *
   * Bewusst NICHT mehr optimistisch: das Löschen dauert serverseitig spürbar lange, und wer die
   * Zeile sofort verschwinden sah und in diesem Fenster neu lud, bekam sie wieder zu sehen. Jetzt
   * verschwindet jede Zeile genau dann, wenn der Server ihre Löschung bestätigt hat.
   */
  private startDelete(targets: ProjectFile[]): void {
    const expanded = this.expandDeleteTargets(targets);
    if (expanded.length === 0) {
      // Kein Kandidat mit id: Zeilen, die es serverseitig (noch) nicht gibt - z.B. ein abgebrochener
      // oder fehlgeschlagener Upload. Vorher endete das Loeschen hier ohne jede Reaktion ("es passiert
      // nichts"). Solche Zeilen gehoeren einfach aus der Tabelle.
      const removable = targets.filter(pf => !pf.id);
      if (removable.length > 0) {
        this.originalData = this.originalData.filter(pf => !removable.includes(pf));
        this.filteredData = this.filteredData.filter(pf => !removable.includes(pf));
        for (const pf of removable) {
          this.uploadingDocuments.delete(this.uploadProgressKey(pf));
        }
        this.updateCurrentFolderData();
        return;
      }

      this.bicToastService.error('Die Auswahl enthält nichts, was gelöscht werden kann.');
      return;
    }

    this.selectedFiles = [];
    this.detailedInfos = false;

    const batchId = this.deleteService.createBatchId();
    const jobs = this.deleteService.enqueueDeletes(this.stateConfigService.getProjectId(), expanded, batchId);

    // Bewusst an den Batch gekoppelt und nicht an den Dialog: der Nutzer darf das Popup schließen und
    // im Hintergrund weiterlöschen lassen. Würde das Tracking mit dem Dialog enden, blieben die danach
    // fertig gelöschten Zeilen bis zum nächsten Reload in der Liste stehen.
    const settled = new Set<string>();
    this.deleteJobsSub?.unsubscribe();
    this.deleteJobsSub = this.deleteService.jobUpdates$.subscribe(job => {
      if (job.batchId !== batchId) return;
      if (job.state !== 'done' && job.state !== 'error') return;

      // Jede bestätigte Datei einzeln entfernen - so wächst der sichtbare Fortschritt mit dem, was der
      // Server tatsächlich schon erledigt hat.
      if (job.state === 'done') {
        this.removeFilesLocally([job.fileId]).forEach(id => this.deletedFileIds.add(id));
        this.filePrefetchService.clear();
      }

      settled.add(job.clientId);
      if (settled.size < jobs.length) return;

      this.deleteJobsSub?.unsubscribe();
      this.deleteJobsSub = undefined;
      this.deleteService.clearFinished();
      // Abschließend den Serverstand holen: fehlgeschlagene Dateien müssen wieder auftauchen, und
      // beim Löschen eines Ordners verschwinden Kinder mit, für die es keinen eigenen Job gab.
      this.loadFiles();
    });

    this.dialog.open(DeleteProgressComponent, {
      hasBackdrop: false,
      data: {batchId},
    });
  }

  /**
   * Die Dateien einer Auswahl, fuer die ein neuer Klassifizierungsversuch sinnvoll ist.
   *
   * Ordner werden aufgeloest: wer einen Ordner mit 40 gescheiterten Dateien anwaehlt, will nicht
   * vierzig Mal klicken - das war der ganze Anlass fuer den Sammel-Neustart. Uebrig bleiben Dateien
   * im Status ERROR, deren Versuche noch nicht erschoepft sind; alles andere waere ein Aufruf, von
   * dem man vorher weiss, dass er nichts bringt.
   */
  protected retryableFilesInSelection(): ProjectFile[] {
    return this.retryableFilesIn(this.visibleSelectedFiles() ?? []);
  }

  /**
   * Wie {@link retryableFilesInSelection}, aber fuer beliebige Ziele - Auswahl in der Kopfleiste
   * ODER ein einzelner Ordner, auf dessen Status-Kreis geklickt wurde.
   */
  private retryableFilesIn(targets: ProjectFile[]): ProjectFile[] {
    const found = new Map<string, ProjectFile>();
    for (const target of targets) {
      const candidates = target.isFolder ? this.collectAllFileDescendants(target) : [target];
      for (const file of candidates) {
        if (!file?.id || file.isFolder) continue;
        if (file.fileStatus !== ProjectFile.FileStatusEnum.ERROR) continue;
        if (this.isClassificationExhausted(file)) continue;
        found.set(file.id, file);
      }
    }
    return [...found.values()];
  }

  /** Fuer den Knopf in der Kopfleiste - er zeigt die Zahl mit an. */
  protected retryableCountInSelection(): number {
    return this.retryableFilesInSelection().length;
  }

  /**
   * Startet die Klassifizierung fuer alle gescheiterten Dateien der Auswahl - in EINEM Stapel.
   *
   * Bewusst ueber den Stapel-Endpunkt und nicht als Schleife ueber den Einzelaufruf, und das ist
   * nicht nur eine Frage der Anzahl der Requests: {@code ClassificationTimeoutSweeper.timeoutFor()}
   * gibt einer Anfrage OHNE Stapel die interaktive Frist von zwei Minuten statt der Stapel-Frist von
   * 30. Vierzig einzeln eingereichte Dateien stellen sich beim Modul an, laufen der Reihe nach ab
   * und werden neu versendet - genau die Lastverdopplung, gegen die die Stapel-Frist eingefuehrt
   * wurde. Als Stapel ist auch die Fortschrittsanzeige in der Seitenleiste dabei.
   */
  protected retrySelectedClassifications(): void {
    const targets = this.retryableFilesInSelection();
    if (targets.length === 0) {
      this.bicToastService.error('In der Auswahl ist keine Datei, die erneut klassifiziert werden kann.');
      return;
    }
    this.startClassificationRetry(targets);
  }

  /**
   * Schickt die uebergebenen Dateien als EINEN Stapel in die Klassifizierung.
   *
   * Gemeinsame Basis des Knopfes in der Kopfleiste und des Klicks auf den Status-Kreis eines
   * Ordners - beide meinen dasselbe, nur die Herkunft der Liste unterscheidet sich.
   */
  private startClassificationRetry(targets: ProjectFile[]): void {
    if (!this.permissionService.hasProjectPrivilege('PROJECT_FILE_EDIT')) {
      this.bicToastService.error('Sie haben nicht die Berechtigung Dateien zu klassifizieren.');
      return;
    }
    if (targets.length === 0) return;

    const projectId = this.stateConfigService.getProjectId();
    if (!projectId) return;

    for (const file of targets) {
      this.markClassificationStarted(file.id);
    }

    this.classificationService
      .classifyFiles(projectId, targets.map(file => ({fileId: file.id!, name: file.name ?? ''})))
      .subscribe({
        next: queuedIds => {
          // Nur die, die der Server wirklich eingereiht hat - er ueberspringt, was schon laeuft.
          for (const fileId of queuedIds) {
            this.markFileProcessingLocally(fileId);
          }
          if (queuedIds.length === 0) {
            this.bicToastService.error('Keine der gewählten Dateien konnte eingereiht werden.');
            return;
          }
          this.bicToastService.success(queuedIds.length === 1
            ? 'Klassifizierung wird erneut versucht.'
            : `${queuedIds.length} Dateien werden erneut klassifiziert.`);
        },
        error: (error: unknown) => {
          console.error('Fehler beim Sammel-Neustart der Klassifizierung:', error);
          for (const file of targets) {
            this.markClassificationSettled(file.id);
          }
          this.bicToastService.error('Klassifizierung konnte nicht gestartet werden. Bitte erneut versuchen.');
        },
      });
  }

  /** Stößt die Klassifizierung für eine fehlgeschlagene Datei (Status ERROR) erneut an. */
  /**
   * Markiert eine Datei lokal als "wird gerade klassifiziert", damit der Nutzer sofort Feedback hat.
   *
   * Gemeinsame Basis von retryClassification(), reclassifyFile() und reclassifyDocumentClass(), die
   * das vorher jede fuer sich taten - jeweils indem sie die Zeile durch ein NEUES Objekt ersetzten.
   * Die Ordner halten dieselbe Zeile ueber `children`, dort blieb also der alte Stand stehen.
   * Zusaetzlich wird die ID als "selbst angestossen" vermerkt: der Banner-Zaehler speist sich aus der
   * Backend-Liste, die diese Klassifizierung erst beim naechsten State-Abruf kennt.
   */
  private markFileProcessingLocally(fileId: string, serverFile?: ProjectFile): void {
    const fields: Partial<ProjectFile> = {
      ...(serverFile ? this.serverFieldsWithoutChildren(serverFile) : {}),
      fileStatus: ProjectFile.FileStatusEnum.PROCESSING,
      errorReason: undefined,
    };

    const existing = this.originalData.find(f => f.id === fileId);
    if (existing) Object.assign(existing, fields);

    const filteredIndex = this.filteredData.findIndex(f => f.id === fileId);
    if (filteredIndex !== -1) {
      Object.assign(this.filteredData[filteredIndex], fields);
      this.filteredData = [...this.filteredData];
    }

    // Bestehender Tracking-Code (BEFORE -> IN -> AFTER) greift ab hier automatisch.
    this.file_classify_state_map.set(fileId, classifyStateEnum.BEFORE);
    this.markClassificationStarted(fileId);
    this.updateParentFolderStatuses(fileId);

    // Ohne den Poll haengt die Zeile in PROCESSING, falls das CLASSIFICATION_COMPLETED-SSE
    // ausbleibt (Multi-Replica) - genau der Fall, in dem der Nutzer manuell neu laden musste.
    this.ensureClassificationFallbackPoll();
  }

  /** Serverfelder ohne `children` - der Baum wird lokal aus parentId aufgebaut. */
  private serverFieldsWithoutChildren(file: ProjectFile): Partial<ProjectFile> {
    const {children: _children, ...rest} = file as ProjectFile & { children?: ProjectFile[] };
    return rest;
  }

  private computePendingUploadInSelection(files: ProjectFile[]): boolean {
    return (files ?? []).some(file => {
      if (this.isPendingUploadRow(file)) return true;
      if (!file.isFolder) return false;
      return this.collectAllFileDescendants(file).some(child => this.isPendingUploadRow(child));
    });
  }

  private computePendingClassificationInSelection(files: ProjectFile[]): boolean {
    return (files ?? []).some(file => {
      if (this.isPendingClassificationRow(file)) return true;
      return file.isFolder
        && this.collectAllFileDescendants(file).some(child => this.isPendingClassificationRow(child));
    });
  }

  private isPendingClassificationRow(file: ProjectFile): boolean {
    return this.effectiveStatus(file) === ProjectFile.FileStatusEnum.PROCESSING
      && !(file.fileType === ProjectFile.FileTypeEnum.IFC && this.hasFragments(file));
  }


  get isElementInSelectedFiles(): (element: ProjectFile) => boolean {
    return (element: ProjectFile): boolean => {
      if (element.isFolder && Array.isArray(element.children) && element.children.length > 0) {
        return element.children.every((child: ProjectFile) =>
          this.selectedFiles.some(file => file.id === child.id)
        );
      }
      return this.selectedFiles.some(file => file.id === element.id);
    };
  }

  get hasElementSelectedChildren(): (element: ProjectFile) => boolean {
    return (element: ProjectFile): boolean => {
      if (!(element.isFolder && Array.isArray(element.children) && element.children.length > 0)) {
        return false;
      }

      const selectedCount = element.children.filter((child: ProjectFile) =>
        this.selectedFiles.some(file => file.id === child.id)
      ).length;

      return selectedCount > 0 && selectedCount < element.children.length;
    };
  }

  private collectAllFileDescendants(folder: ProjectFile): ProjectFile[] {
    if (!folder.isFolder || !Array.isArray(folder.children) || folder.children.length === 0) {
      return [];
    }

    const result: ProjectFile[] = [];
    const stack: ProjectFile[] = [...folder.children];

    while (stack.length > 0) {
      const node = stack.pop() as ProjectFile;

      if (node.isFolder) {
        if (Array.isArray(node.children) && node.children.length > 0) {
          stack.push(...node.children);
        }
      } else {
        result.push(node);
      }
    }

    return result;
  }

  /**
   * Auswahl der Toolbar bzw. eine einzelne Zeile aus dem Kontextmenue. Die Toolbar-Auswahl ist nur der
   * sichtbare Teil ({@link visibleSelectedFiles}, #2770) - Aktionen immer ueber diese Methode.
   */
  private targetsFor(file?: ProjectFile): ProjectFile[] {
    if (file != undefined) return [file];
    return this.visibleSelectedFiles() ?? [];
  }

  /**
   * Stoppt die Uploads, die in die zu loeschende Auswahl hineinlaufen.
   *
   * Bezugsmenge sind die ORDNER der Auswahl samt Teilbaum: ein Upload-Job traegt in
   * {@code parentId} den Zielordner seiner Datei, und genau darueber findet er sich wieder. Zeilen,
   * die selbst gerade hochgeladen werden, sind ueber ihren Elternordner mit abgedeckt.
   */
  private cancelUploadsForDeletion(file?: ProjectFile): void {
    const projectId = this.stateConfigService.getProjectId();
    if (!projectId) return;

    const folderIds = new Set<string>();
    for (const target of this.targetsFor(file)) {
      if (target.isFolder && target.id) folderIds.add(target.id);
      // Eine einzelne Datei: ihr Elternordner ist das, was die Jobs tragen.
      if (!target.isFolder && target.parentId) folderIds.add(target.parentId);
      for (const descendant of this.collectAllFileDescendants(target)) {
        if (descendant.isFolder && descendant.id) folderIds.add(descendant.id);
      }
    }
    if (folderIds.size === 0) return;

    const cancelled = this.uploadService.cancelUploadsInto(projectId, folderIds);
    if (cancelled > 0) {
      this.notificationsService.showSuccessMessage(
        `${cancelled} laufende${cancelled !== 1 ? ' Uploads wurden' : 'r Upload wurde'} abgebrochen.`,
      );
    }
  }

  /** Namen der echten Geschwister einer Zeile (gleicher Ordner), ohne die Zeile selbst. */
  private siblingNamesOf(file: ProjectFile): (string | undefined)[] {
    const parentId = file.parentId ?? null;
    return (this.originalData ?? [])
      .filter(pf => (pf.parentId ?? null) === parentId && pf.id !== file.id)
      .map(pf => pf.name);
  }

  /**
   * Setzt einen Namen in beiden Datensaetzen - bewusst in place, weil die Ordner dieselbe Zeile
   * ueber `children` als Referenz halten.
   */
  private applyRenameLocally(fileId: string | undefined, name: string): void {
    if (!fileId) return;

    for (const item of this.originalData) {
      if (item.id === fileId) item.name = name;
    }
    for (const item of this.filteredData) {
      if (item.id === fileId) item.name = name;
    }
    this.filteredData = [...this.filteredData];

    this.rebuildChildrenRelations();
    this.updateCurrentFolderData();
  }

  /**
   * Uebernimmt einen frisch vom Server geholten Stand einer Datei in die Tabelle.
   *
   * Bewusst per Object.assign auf das vorhandene Objekt und nicht als Ersatz: die Ordner halten
   * dieselbe Zeile ueber `children` als Referenz, wovon folderState() und
   * collectAllFileDescendants() leben. Ein neues Objekt liess dort den alten Stand stehen - der
   * Ordner-Ring drehte weiter, obwohl alle Kinder fertig waren.
   *
   * `children` des Servers wird nicht uebernommen: die Tabellen-Projektion liefert keine, den Baum
   * baut rebuildChildrenRelations() aus parentId auf.
   */
  private patchFileInDataSets(updatedFile: ProjectFile): void {
    if (!updatedFile.id) return;

    const {children: _serverChildren, ...serverFields} = updatedFile as ProjectFile & { children?: ProjectFile[] };

    const normalizedFields =
      updatedFile.fileType === ProjectFile.FileTypeEnum.IFC &&
      this.hasFragments(updatedFile) &&
      updatedFile.fileStatus === ProjectFile.FileStatusEnum.PROCESSING
        ? {...serverFields, fileStatus: ProjectFile.FileStatusEnum.READY}
        : serverFields;

    const existing = this.originalData.find(file => file.id === updatedFile.id);
    if (existing) {
      Object.assign(existing, normalizedFields);
    }

    const fdIdx = this.filteredData.findIndex(file => file.id === updatedFile.id);
    if (fdIdx !== -1) {
      // Identitaet erhalten, aber Array-Referenz erneuern, damit die cdk-table neu rendert.
      Object.assign(this.filteredData[fdIdx], normalizedFields);
      this.filteredData = [...this.filteredData];
    }
  }

  private scheduleIfcReadyPoll(fileId: string, attempt: number = 0): void {
    const maxAttempts = 20;
    if (this.ifcReadyPollTimeoutByFileId.has(fileId)) return;

    if (attempt >= maxAttempts) {
      // Nach ~40s endgueltig aufgegeben. Vorher endete das stumm und die Zeile blieb dauerhaft im
      // Zustand "IFC-Datei erfordert Klassifizierung" - ohne Hinweis, dass hier nichts mehr laeuft.
      // Jetzt geht sie in denselben Haenger-Zustand wie eine verlorene Klassifizierung: roter
      // Kreis, Klick startet die Klassifizierung neu.
      this.stalledClassificationIds.add(fileId);
      this.classificationMissesById.delete(fileId);
      this.updateParentFolderStatuses(fileId);
      return;
    }

    const timeoutId = setTimeout(() => {
      this.ifcReadyPollTimeoutByFileId.delete(fileId);
      this.fileController.getFile(this.stateConfigService.getProjectId(), fileId).subscribe({
        next: (updatedFile: ProjectFile) => {
          this.patchFileInDataSets(updatedFile);
          this.updateParentFolderStatuses(fileId);

          const isIfcWithoutFragments =
            updatedFile.fileType === ProjectFile.FileTypeEnum.IFC &&
            !this.hasFragments(updatedFile);
          if (isIfcWithoutFragments) {
            this.scheduleIfcReadyPoll(fileId, attempt + 1);
          }
        },
        error: () => {
          this.scheduleIfcReadyPoll(fileId, attempt + 1);
        }
      });
    }, 2000);

    this.ifcReadyPollTimeoutByFileId.set(fileId, timeoutId);
  }

  private applyUploadJobUpdate(job: UploadJob): void {
    // Progress Map aktualisieren. Unter beiden Schluesseln, weil die Zeile im Laufe des Uploads vom
    // Dummy (name+size+parentId) auf die Server-id wechselt.
    // Vorgemerkte UPLOADED-Meldungen nachziehen: hier bekommt die Zeile ihre Server-ID.
    this.flushPendingUploadedStatus();

    const progressKeys = this.uploadJobProgressKeys(job);
    const finishedJob = job.state === 'done' || job.state === 'error';
    for (const key of progressKeys) {
      if (finishedJob) {
        this.uploadingDocuments.delete(key);
      } else {
        this.uploadingDocuments.set(key, job.progress ?? 0);
      }
    }

    // Vom Nutzer abgebrochen: die Datei existiert nicht (mehr) \- Zeile komplett aus der Liste nehmen.
    if (job.state === 'error' && job.error === 'cancelled') {
      this.removeCancelledUpload(job);
      return;
    }

    // Fehlgeschlagen (nicht abgebrochen): die Zeile blieb bisher stumm in UPLOADING stehen.
    // Ohne id (der Upload-Call selbst schlug fehl, es gibt serverseitig nichts) ist sie eine
    // Phantomzeile: nicht loeschbar (startDelete braucht eine id) und erst nach einem Reload weg.
    // Mit id existiert die Datei, hat aber keine Bytes \- als ERROR markieren, damit sie sichtbar
    // fehlerhaft ist und ueber das Kontextmenue behandelt werden kann.
    if (job.state === 'error') {
      this.applyFailedUpload(job);
      return;
    }

    // Ob sich die Zeilenmenge oder ein filterrelevantes Feld geaendert hat. Nur dann muessen
    // currentFolderData und die Filter neu berechnet werden. Ein reiner Fortschritts-Tick aendert
    // nur uploadingDocuments (das die Zeile direkt liest) \- vorher lief hier trotzdem pro Tick ein
    // voller Scan ueber originalData (das GANZE Projekt) plus applyColumnFilters().
    let dataSetChanged = false;

    // Sobald Backend uns ein echtes ProjectFile liefert: Dummy ersetzen
    if (job.projectFile?.id) {
      const real = job.projectFile;

      if (!real.id) {
        // sollte durch if-Bedingung eigentlich nie passieren, aber TS braucht den Guard
        this.updateCurrentFolderData();
        return;
      }

      if (!this.file_classify_state_map.has(real.id)) {
        this.file_classify_state_map.set(real.id, classifyStateEnum.BEFORE);
      }

      const sameDummy = (pf: ProjectFile) =>
        !pf.id && pf.name === job.file.name && pf.size === job.file.size && (pf.parentId ?? '') === (job.parentId ?? '');

      // originalData: Dummy raus, real rein (inkl. Parent/Size Update)
      const dummyInOriginal = this.originalData.find(sameDummy);
      if (dummyInOriginal) {
        dataSetChanged = true;
        this.addOrRemoveFileToOrFromOriginalData(dummyInOriginal, true);
        this.setOrRemoveParentInOriginalData(dummyInOriginal, dummyInOriginal.parentId ?? null, true);

        this.addOrRemoveFileToOrFromOriginalData(real);
        this.setOrRemoveParentInOriginalData(real, real.parentId ?? null);
      } else if (!this.originalData.some(pf => pf.id === real.id) && !this.deletedFileIds.has(real.id)) {
        // deletedFileIds-Guard: ohne ihn holt ein Job-Update, das nach dem Löschen eintrifft, die
        // Datei wieder in die Tabelle.
        dataSetChanged = true;
        this.addOrRemoveFileToOrFromOriginalData(real);
        if (real.parentId) this.setOrRemoveParentInOriginalData(real, real.parentId);
      }

      // filteredData Dummy ersetzen (nur UI)
      const fdIdx = this.filteredData.findIndex(sameDummy);
      if (fdIdx !== -1) {
        dataSetChanged = true;
        this.filteredData.splice(fdIdx, 1, real);
        this.filteredData = [...this.filteredData];
      }

      // Auswahl: ein angehakter Platzhalter wird zur echten Datei (wie beim Bearbeiten). Sonst bleibt
      // er nach dem Upload als "ausgeblendet" in der Auswahl haengen, weil er in filteredData fehlt (#2770).
      // Ist die echte Datei schon ausgewaehlt, faellt nur der Platzhalter weg. Neues Array, damit der
      // Cache von visibleSelectedFiles sicher neu rechnet.
      const sfIdx = this.selectedFiles.findIndex(sameDummy);
      if (sfIdx !== -1) {
        this.selectedFiles = this.selectedFiles.some(pf => pf.id === real.id)
          ? this.selectedFiles.filter((_, i) => i !== sfIdx)
          : this.selectedFiles.map((pf, i) => (i === sfIdx ? real : pf));
        this.selectedFilesChange.emit(this.selectedFiles);
      }

      // Race Condition auflösen: CLASSIFICATION_COMPLETED kam bevor die Datei in originalData war
      if (this.pendingClassificationCompletedIds.has(real.id)) {
        this.pendingClassificationCompletedIds.delete(real.id);
        const projectId = this.stateConfigService.getProjectId();
        if (projectId) {
          this.reloadClassifiedFile(real.id, projectId);
        }
      }
    }

    // Status-Updates, die evtl. NACH dem ersten projectFile kommen (z.B. READY/CHECKED)
    // -> in originalData + filteredData patchen, damit Filter sofort greift.
    if (job.fileId && job.state) {
      const odIdx = this.originalData.findIndex(pf => pf.id === job.fileId);
      const currentFile = odIdx !== -1 ? this.originalData[odIdx] : undefined;
      const keepIfcInProcessing =
        job.state === 'done' &&
        currentFile?.fileType === ProjectFile.FileTypeEnum.IFC &&
        !this.hasFragments(currentFile);

      if (keepIfcInProcessing) {
        this.scheduleIfcReadyPoll(job.fileId);
      }

      // 'done' bedeutet nur, dass der classify-HTTP-Call angenommen wurde (der Endpoint
      // antwortet sofort mit 200, die @Async-Klassifizierung im Backend laeuft erst danach).
      // Deshalb NICHT lokal auf READY patchen: eine frisch hochgeladene IFC hat noch keine
      // Fragments und wuerde sonst kurz faelschlich den roten Error-Circle zeigen.
      // Der echte Endstatus (READY/ERROR) + fragmentsCreated kommt via SSE-Reload bzw. loadFiles.
      const newStatus =
        job.state === 'processing' || keepIfcInProcessing
          ? ProjectFile.FileStatusEnum.PROCESSING
          : undefined;

      if (newStatus) {
        // fileStatus ist filterrelevant (Status-Spaltenfilter), also neu rechnen.
        dataSetChanged = true;

        if (odIdx !== -1) {
          this.originalData[odIdx].fileStatus = newStatus;
        }

        const fdIdx = this.filteredData.findIndex(pf => pf.id === job.fileId);
        if (fdIdx !== -1) {
          this.filteredData[fdIdx].fileStatus = newStatus;
          this.filteredData = [...this.filteredData];
        }

        // Update Parent-Ordner-Status
        this.updateParentFolderStatuses(job.fileId);
        this.markClassificationStarted(job.fileId);

        // Frisch hochgeladene Dateien haengen sonst allein am SSE: kommt CLASSIFICATION_COMPLETED
        // nicht an (Zustellung ueber mehrere Replikas), bleibt die Zeile bis zum manuellen Reload
        // in PROCESSING. Der Poll ist genau dafuer das Sicherheitsnetz.
        this.ensureClassificationFallbackPoll();
      }
    }

    // currentFolderData + Filter neu berechnen (ohne Full-Reload) \- aber nur, wenn oben wirklich
    // etwas an der Zeilenmenge oder am Status haengt.
    if (dataSetChanged) {
      this.updateCurrentFolderData();
    }
  }

  private stopClassificationFallbackPoll(): void {
    if (this.classificationPollInterval) {
      clearInterval(this.classificationPollInterval);
      this.classificationPollInterval = null;
    }
  }

  /**
   * Verarbeitet einen fehlgeschlagenen Upload (kein Abbruch durch den Nutzer).
   *
   * Hat der Job schon eine serverseitige id, bleibt die Zeile stehen und wird als ERROR markiert -
   * sie existiert im Backend und laesst sich normal loeschen oder neu klassifizieren. Ohne id gibt es
   * serverseitig nichts: die Zeile wird entfernt, sonst haengt eine Zeile ohne id in der Tabelle, die
   * sich nicht loeschen laesst (`expandDeleteTargets` ueberspringt Zeilen ohne id).
   */
  private applyFailedUpload(job: UploadJob): void {
    const name = job.file.name;

    if (!job.fileId) {
      this.removeCancelledUpload(job);
      this.bicToastService.error(`Upload von "${name}" fehlgeschlagen.`);
      return;
    }

    // Auch den Dummy treffen: schlägt der Upload NACH dem `upload()`-Call fehl (S3-PUT), steht in der
    // Tabelle noch die Zeile ohne id. Sie bekommt hier die serverseitige id - sonst bliebe sie
    // unloeschbar.
    const matches = (pf: ProjectFile): boolean =>
      pf.id === job.fileId
      || (!pf.id && pf.name === job.file.name && pf.size === job.file.size && (pf.parentId ?? '') === (job.parentId ?? ''));

    // Bewusst in place: der Ordner haelt dieselbe Zeile ueber `children` als Referenz (Ordnerstatus,
    // Upload-Fortschritt). Ein neues Objekt liesse dort die alte UPLOADING-Zeile stehen.
    for (const pf of this.originalData) {
      if (!matches(pf)) continue;
      pf.id = pf.id ?? job.fileId;
      pf.fileStatus = ProjectFile.FileStatusEnum.ERROR;
      pf.errorReason = pf.errorReason ?? 'Upload fehlgeschlagen.';
    }
    this.filteredData = [...this.filteredData];

    for (const key of this.uploadJobProgressKeys(job)) {
      this.uploadingDocuments.delete(key);
    }
    this.uploadClientIdByDummyKey.delete(this.getDummyKey(job.file, job.parentId));

    this.updateParentFolderStatuses(job.fileId);
    this.updateCurrentFolderData();
    this.bicToastService.error(`Upload von "${name}" fehlgeschlagen.`);
  }

  /**
   * Entfernt die Zeile eines abgebrochenen Uploads \- je nachdem, wie weit der Job war, ist das noch
   * der Dummy oder schon das vom Backend gelieferte ProjectFile (das der UploadService gelöscht hat).
   */
  private removeCancelledUpload(job: UploadJob): void {
    const matches = (pf: ProjectFile) =>
      (!!job.fileId && pf.id === job.fileId)
      || (!pf.id && pf.name === job.file.name && pf.size === job.file.size && (pf.parentId ?? '') === (job.parentId ?? ''));

    for (const pf of this.originalData.filter(matches)) {
      this.setOrRemoveParentInOriginalData(pf, pf.parentId ?? null, true);
    }
    this.originalData = this.originalData.filter(pf => !matches(pf));
    this.filteredData = this.filteredData.filter(pf => !matches(pf));

    for (const key of this.uploadJobProgressKeys(job)) {
      this.uploadingDocuments.delete(key);
    }
    this.uploadClientIdByDummyKey.delete(this.getDummyKey(job.file, job.parentId));

    this.updateCurrentFolderData();
    this.applyColumnFilters();
  }

  /**
   * Lädt eine einzelne Datei nach abgeschlossener Klassifizierung neu und aktualisiert
   * damit documentClassTitles, fileStatus und fragmentsCreated live in der Tabelle.
   */
  /** Startet den Fallback-Poll, sofern noch Dateien klassifiziert werden und er nicht schon läuft. */
  private ensureClassificationFallbackPoll(): void {
    if (this.classificationPollInterval) return;
    // Bewusst locallyPendingFileCount und nicht classifyingCount: letzterer zaehlt die
    // Backend-Liste, in der eine frisch hochgeladene Datei nie steht - der Poll waere fuer genau
    // diesen Fall nie angelaufen und die Zeile haette bis zum manuellen Reload weitergedreht.
    if (this.locallyPendingFileCount === 0) return;
    this.classificationPollInterval = setInterval(
      () => this.pollProcessingFilesFallback(),
      this.CLASSIFICATION_POLL_INTERVAL_MS,
    );
  }

  /**
   * Lädt alle noch als PROCESSING markierten Dateien neu und übernimmt fertige Ergebnisse
   * (READY/ERROR) genauso wie der SSE-Pfad. Stoppt sich selbst, sobald nichts mehr läuft.
   */
  private pollProcessingFilesFallback(): void {
    const projectId = this.stateConfigService.getProjectId();
    if (!projectId) {
      this.stopClassificationFallbackPoll();
      return;
    }

    const processing = (this.originalData ?? []).filter(
      pf => !pf.isFolder
        && pf.id
        && pf.fileStatus === ProjectFile.FileStatusEnum.PROCESSING
        && !this.stalledClassificationIds.has(pf.id),
    );
    if (processing.length === 0) {
      this.stopClassificationFallbackPoll();
      return;
    }

    // Bewusst begrenzt parallel: direkt nach einem Batch-Upload stehen hier schnell dreistellig
    // viele Dateien in PROCESSING. Vorher feuerte diese Schleife fuer JEDE davon gleichzeitig ein
    // getFile() (Voll-Entity inkl. Presign, und jede Antwort loest updateCurrentFolderData() +
    // updateParentFolderStatuses() aus) \- und das alle 15 Sekunden erneut.
    from(processing).pipe(
      mergeMap(pf => {
        const id = pf.id!;
        return this.fileController.getFile(projectId, id).pipe(
          tap((updated: ProjectFile) => {
            this.patchFileInDataSets(updated);

            if (updated.fileStatus !== ProjectFile.FileStatusEnum.PROCESSING) {
              this.file_classify_state_map.set(id, classifyStateEnum.AFTER);
              this.markClassificationSettled(id);
              this.updateCurrentFolderData();
              this.updateParentFolderStatuses(id);
              if (this.locallyPendingFileCount === 0) this.stopClassificationFallbackPoll();
              return;
            }

            this.trackClassificationProgress(id);
          }),
          // Ignorieren – der nächste Poll-Tick versucht es erneut. Bewusst pro Datei, damit ein
          // Fehler nicht die restlichen Abfragen des Ticks abbricht.
          catchError(() => EMPTY),
        );
      }, this.CLASSIFICATION_POLL_CONCURRENCY),
    ).subscribe();
  }

  /** Klick auf das rote Fehler-Icon einer Datei stößt die erneute Klassifizierung an. */
  onErrorCircleClick(event: Event, file: ProjectFile): void {
    if (file.isFolder) {
      const targets = this.retryableFilesIn([file]);
      // Nichts zu wiederholen: den Klick der Zeile lassen, also in den Ordner hinein. Der Kreis
      // zeigt dann auch keinen Zeiger an - wir nehmen dem Nutzer hier nichts weg.
      if (targets.length === 0) return;
      // Ohne das kam bisher NUR der Zeilen-Klick an: man landete im Ordner, statt den Neustart
      // auszuloesen. Das frueher hier stehende `return` lag vor dem stopPropagation().
      event.stopPropagation();
      this.startClassificationRetry(targets);
      return;
    }
    event.stopPropagation();
    this.retryClassification(file);
  }

  /**
   * Haenger-Erkennung: der Server fuehrt die Datei weiter als PROCESSING, meldet sie aber nicht
   * (mehr) in `runningClassifications`. Passiert das STALLED_CLASSIFICATION_MISSES Poll-Runden
   * hintereinander, ist die Klassifizierung verloren gegangen (verlorenes SSE, abgebrochener
   * Backend-Lauf) - die Zeile bekommt den roten Retry-Kreis statt eines endlos drehenden Rings.
   */
  private trackClassificationProgress(fileId: string): void {
    if (this.stateConfigService.isRunningClassification(fileId)
      || this.locallyStartedClassificationIds.has(fileId)) {
      this.classificationMissesById.set(fileId, 0);
      return;
    }

    const misses = (this.classificationMissesById.get(fileId) ?? 0) + 1;
    this.classificationMissesById.set(fileId, misses);

    if (misses >= this.STALLED_CLASSIFICATION_MISSES) {
      this.stalledClassificationIds.add(fileId);
      this.classificationMissesById.delete(fileId);
      this.updateParentFolderStatuses(fileId);
      if (this.locallyPendingFileCount === 0) this.stopClassificationFallbackPoll();
    }
  }

  private getEntriesInTargetFolder(parentId: string | undefined): ProjectFile[] {
    // Quelle der Wahrheit: originalData (filteredData kann Suche/Filter enthalten)
    return (this.originalData ?? []).filter((pf) => {
      const pfParentId = pf.parentId ?? undefined;
      return pfParentId === (parentId ?? undefined);
    });
  }

  private buildExistingNameSet(entries: ProjectFile[], isFolder: boolean): Set<string> {
    // Windows-Style: case-insensitive
    const set = new Set<string>();
    for (const e of entries) {
      if (!!e.isFolder !== !!isFolder) continue;
      if (!e.name) continue;
      set.add(e.name.toLocaleLowerCase());
    }
    return set;
  }

  private splitFilename(name: string): { stem: string; ext: string } {
    // Behält Sonderfälle wie ".env" oder "file" (ohne Extension) sauber bei.
    const lastDot = name.lastIndexOf('.');
    if (lastDot <= 0) {
      return {stem: name, ext: ''};
    }
    return {stem: name.substring(0, lastDot), ext: name.substring(lastDot)};
  }

  private makeNameUniqueWindowsStyle(originalName: string, existingNamesLower: Set<string>, isFolder: boolean): string {
    const trimmed = (originalName ?? '').trim();
    if (!trimmed) return trimmed;

    if (!existingNamesLower.has(trimmed.toLocaleLowerCase())) {
      return trimmed;
    }

    if (isFolder) {
      // Folder: Suffix an ganzen Namen
      const base = trimmed;
      for (let i = 1; i < 10_000; i++) {
        const candidate = `${base} (${i})`;
        if (!existingNamesLower.has(candidate.toLocaleLowerCase())) {
          return candidate;
        }
      }
      return `${base} (${Date.now()})`;
    }

    // File: Suffix vor Extension
    const {stem, ext} = this.splitFilename(trimmed);
    const baseStem = stem;

    for (let i = 1; i < 10_000; i++) {
      const candidate = `${baseStem} (${i})${ext}`;
      if (!existingNamesLower.has(candidate.toLocaleLowerCase())) {
        return candidate;
      }
    }

    return `${baseStem} (${Date.now()})${ext}`;
  }

  private renameFileKeepingContent(file: File, newName: string): File {
    if (!newName || file.name === newName) return file;
    return new File([file], newName, {type: file.type, lastModified: file.lastModified});
  }

  protected readonly faChevronRight = faChevronRight;
  protected readonly faCircleQuestion = faCircleQuestion;
  protected readonly faBars = faBars;
  protected readonly faFolderOpen = faFolderOpen;
  protected readonly ProjectFile = ProjectFile;
  protected readonly undefined = undefined;
  protected readonly faHouse = faHouse;
  protected readonly faArrowUpFromBracket = faArrowUpFromBracket;
  protected readonly faFolderPlus = faFolderPlus;

  private toSyntheticFileInputEvent(files: File[]): Event {
    const fileListLike: Record<number | 'length', unknown> = {length: files.length};
    files.forEach((file, index) => (fileListLike[index] = file));
    return {target: {files: fileListLike, value: ''}} as unknown as Event;
  }
  protected readonly faCircleCheck = faCircleCheck;
  protected readonly faCircleXmark = faCircleXmark;
  protected readonly faCircleExclamation = faCircleExclamation;
  protected readonly faSort = faSort;
  protected readonly faSortUp = faSortUp;
  protected readonly faSortDown = faSortDown;
  protected readonly faArrowDownToBracket = faArrowDownToBracket;
  protected readonly faEllipsisVertical = faEllipsisVertical;
  protected readonly faArrowUpRightFromSquare = faArrowUpRightFromSquare;
  protected readonly faCopy = faCopy;
  protected readonly faPenToSquare = faPenToSquare;
  protected readonly faTrash = faTrash;
  protected readonly faCircleInfo = faCircleInfo;
  protected readonly faArrowsUpDownLeftRight = faArrowsUpDownLeftRight;

  /**
   * Aktualisiert Ordner-Status basierend auf ihren Kindern.
   * Wenn alle Datei-Nachkommen READY/CHECKED sind, wird der Ordner auf READY gesetzt.
   */
  private updateParentFolderStatuses(fileId: string | null | undefined): void {
    if (!fileId) return;

    // Finde den Parent des geänderten Elements
    const changedFile = this.originalData.find(f => f.id === fileId);
    if (!changedFile) return;

    let currentParentId = changedFile.parentId;

    // Gehe alle Parent-Ordner hoch und aktualisiere ihre Status
    while (currentParentId) {
      const parentFolder = this.originalData.find(f => f.id === currentParentId);
      if (!parentFolder || !parentFolder.isFolder) break;

      // Sammle Statuses aller Datei-Nachkommen
      const descendantFiles = this.collectAllFileDescendants(parentFolder);
      if (descendantFiles.length === 0) {
        currentParentId = parentFolder.parentId;
        continue;
      }

      const statuses = descendantFiles.map(f => f.fileStatus);
      const checked = statuses.filter(s => s === ProjectFile.FileStatusEnum.CHECKED).length;
      const ready = statuses.filter(s => s === ProjectFile.FileStatusEnum.READY).length;
      const processing = statuses.filter(s => s === ProjectFile.FileStatusEnum.PROCESSING).length;
      const uploading = statuses.filter(s => s === ProjectFile.FileStatusEnum.UPLOADING).length;
      const total = statuses.length;

      // Bestimme neuen Status für Ordner
      let newStatus: ProjectFile.FileStatusEnum;
      if (uploading > 0) {
        newStatus = ProjectFile.FileStatusEnum.UPLOADING;
      } else if (processing > 0) {
        newStatus = ProjectFile.FileStatusEnum.PROCESSING;
      } else if (ready + checked === total) {
        newStatus = ProjectFile.FileStatusEnum.READY;
      } else {
        newStatus = ProjectFile.FileStatusEnum.READY;
      }

      // Update Ordner-Status
      const odIdx = this.originalData.findIndex(f => f.id === currentParentId);
      if (odIdx !== -1) {
        this.originalData[odIdx].fileStatus = newStatus;
      }

      // Update filteredData auch
      const fdIdx = this.filteredData.findIndex(f => f.id === currentParentId);
      if (fdIdx !== -1) {
        this.filteredData[fdIdx].fileStatus = newStatus;
      }

      currentParentId = parentFolder.parentId;
    }

    this.filteredData = [...this.filteredData];
  }
  protected readonly faFilePdf = faFilePdf;
  protected readonly faFile = faFile;
  protected readonly range = range;
  protected readonly Math = Math;
  protected readonly faFileDoc = faFileDoc;
  protected readonly faFileXls = faFileXls;
  protected readonly faFilePpt = faFilePpt;
  protected readonly faIfc = faIfc;
  protected readonly faLayerGroup = faLayerGroup;
  protected readonly faXmark = faXmark;
  protected readonly faChevronDown = faChevronDown;
  protected readonly FileCircleState = FileCircleState;
  protected readonly classifyStateEnum = classifyStateEnum;
  protected readonly faChevronLeft = faChevronLeft;
  protected readonly length = length;
  protected readonly faMagnifyingGlass = faMagnifyingGlass;
  protected readonly faInfoCircle = faInfoCircle;

  /** Gibt an, ob eine Datei keine Dokumentenklasse hat (nur für Nicht-Ordner relevant). */
  fileHasNoDocumentClass(file: ProjectFile): boolean {
    if (file.isFolder) return false;
    return !file.documentClassTitles || file.documentClassTitles.length === 0;
  }

  /**
   * Merkt eine fertig klassifizierte Datei zum Nachladen vor.
   *
   * Frueher holte das je Datei ein eigenes {@code getFile}. Bei einem Ordner-Upload mit ~380 Dateien
   * waren das ~380 GETs plus ~380 CORS-Preflights - der Netzwerk-Tab lief voll - und zu JEDER
   * Antwort ein {@code updateCurrentFolderData()}, also ~380 Neuaufbauten der Tabelle. Daher kam das
   * Ruckeln. Jetzt sammeln sich die IDs kurz und gehen als EIN Request raus; die Tabelle wird
   * einmal je Block neu aufgebaut statt einmal je Datei.
   */
  private reloadClassifiedFile(fileId: string, projectId: number): void {
    if (this.reloadingClassifiedFileIds.has(fileId)) return;
    this.reloadingClassifiedFileIds.add(fileId);
    this.classifiedReloadQueue.add(fileId);

    // Der Timer wird von spaeter eintreffenden IDs NICHT verlaengert: bei 380 Dateien, die ueber
    // Minuten fertig werden, wuerde ein Debounce den ersten Block endlos hinausschieben.
    if (this.classifiedReloadTimer !== null) return;
    this.classifiedReloadTimer = setTimeout(() => {
      this.classifiedReloadTimer = null;
      this.flushClassifiedReloadQueue(projectId);
    }, FilesComponent.CLASSIFIED_RELOAD_DELAY_MS);
  }

  /** Holt einen Block vorgemerkter Dateien und aktualisiert die Zeilen in einem Durchgang. */
  private flushClassifiedReloadQueue(projectId: number): void {
    const ids = Array.from(this.classifiedReloadQueue).slice(0, FilesComponent.CLASSIFIED_RELOAD_CHUNK);
    if (ids.length === 0) return;
    ids.forEach(id => this.classifiedReloadQueue.delete(id));

    const scheduleRest = () => {
      if (this.classifiedReloadQueue.size === 0 || this.classifiedReloadTimer !== null) return;
      this.classifiedReloadTimer = setTimeout(() => {
        this.classifiedReloadTimer = null;
        this.flushClassifiedReloadQueue(projectId);
      }, FilesComponent.CLASSIFIED_RELOAD_DELAY_MS);
    };

    this.fileController.getFilesByIds(ids, projectId).subscribe({
      next: (updatedFiles: ProjectFile[]) => {
        for (const updatedFile of updatedFiles) {
          if (!updatedFile.id) continue;
          this.patchFileInDataSets(updatedFile);
          // classify-state auf AFTER setzen, damit UI den fertigen Status zeigt.
          this.file_classify_state_map.set(updatedFile.id, classifyStateEnum.AFTER);
          this.markClassificationSettled(updatedFile.id);
        }

        // Einmal je Block, nicht je Datei: currentFolderData + Filter aktualisieren, damit Tooltip
        // und Filter den neuen documentClassTitles-Wert beruecksichtigen.
        this.updateCurrentFolderData();
        for (const updatedFile of updatedFiles) {
          if (updatedFile.id) this.updateParentFolderStatuses(updatedFile.id);
        }

        // Dateien, die der Server nicht mehr kennt (inzwischen geloescht), tauchen in der Antwort
        // nicht auf. Ihre Sperre muss trotzdem fallen, sonst blieben sie fuer immer vorgemerkt.
        ids.forEach(id => this.reloadingClassifiedFileIds.delete(id));
        scheduleRest();
      },
      error: () => {
        // Kein lokaler Abschluss: die Zeilen bleiben in PROCESSING und damit im Zugriff des
        // Fallback-Polls, der sie entweder fertigmeldet oder als haengengeblieben markiert.
        ids.forEach(id => this.reloadingClassifiedFileIds.delete(id));
        scheduleRest();
      }
    });
  }
}
